import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useLocation } from "react-router-dom";
import './InventoryManagement.css'
import './InventoryManagementAddOn.css'
import FilterDropdown from '../team-portal/TeamPortalUtil/FilterDropdown';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { useAuth } from '../Auth';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Tooltip } from '../team-portal/TOATab/ActivityAndPerformance';
import { createPortal } from 'react-dom';

import TeamHeader from '../team-portal/TeamHeader/TeamHeader';

// Flowchart imports
import { ReactFlow, Controls, Background, Node as FlowNode, Edge } from '@xyflow/react';

import '@xyflow/react/dist/style.css';

// Toast import 
import { toast, ToastContainer } from 'react-toastify';

// Icons
import { IoAddCircleOutline, IoBarChart, IoBag, IoBagCheck, IoBagAdd, IoHammerSharp } from "react-icons/io5";
import { MdShoppingCart, MdOutlineReplay, MdPlaylistAddCheckCircle } from "react-icons/md";
import { FaTrashAlt, FaLayerGroup, FaCircle, FaClipboardList, FaFileCsv } from "react-icons/fa";
import { FaBoltLightning, FaCircleInfo, FaWarehouse, FaTruckFast, FaBoxesStacked } from "react-icons/fa6";
import { BsBoxSeamFill, BsBoxFill } from "react-icons/bs";
import { GiTwoCoins } from "react-icons/gi";
import { TbReportMoney, TbPackages } from "react-icons/tb";
import { PiWarningDuotone, PiPuzzlePieceFill, PiClockCountdownFill } from "react-icons/pi";
import { HiBellAlert, HiArchiveBoxXMark, HiArchiveBox } from "react-icons/hi2";
import { AiFillPicture } from "react-icons/ai";
import { TfiDropboxAlt } from "react-icons/tfi";
import { GoNumber } from "react-icons/go";
import { LuPencil, LuArchiveRestore } from "react-icons/lu";

// Assets
import fnnBlueLogo from '../img/inventory/FNNIconBlue-07.png'

// Interface for getting the available org content
interface InventoryItems {
    partID: number;
    partName: string;
    partPicture?: any;
    currentAmount: number;
    unitCost: number;
    category: string;
    partDocument?: any;
    partLink?: string;
    lastCounted?: string | null; // ISO date string
    location?: string | null;
    description?: string | null;
    mpn?: string | null;
    manufacturer?: string | null;
    location_picture?: any;
    stock_target?: number | null;
    sold_in_packets?: boolean;
    amount_per_packet?: number | null;
    bundle_subassembly?: number | null;
}

// Forecast
interface InventoryForecast {
    id: number;
    part: number;
    expectedAmount: number;
    cost: number;
    orderedDate: Date | null;
    arrivalDate: Date | null;
    status: string;
    sourceLinks: string;
    notes: string;
    requestor: string;
    requestor_email?: string;
    priority?: string;
    lead_time?: string | null;
    is_packet?: boolean;
    number_of_packets?: number | null;
    amount_per_packet?: number | null;
    pack_cost?: number;
    expectedArrivalDate?: Date | null;
    alert_generated?: boolean;
    belongs_to_batch: number | null;
    belongs_to_batch_name?: string | null;
}

// Part requirement for SubAssembly
interface SubAssemblyPart {
    part: number; // not InventoryItems
    quantity_required: number;
}

// SubAssembly
interface SubAssembly {
    id: number;
    name: string;
    parts: SubAssemblyPart[];
    subPicture?: any;
    subSOP?: any;
}

// DetectorSubAssembly includes full SubAssembly object
interface DetectorSubAssembly {
    subassembly: number | SubAssembly; // Accepts either id or object
    quantity_required: number;
}

// Detector version
interface Detector {
    id: number;
    name: string;
    version: string;
    subassemblies: DetectorSubAssembly[];
}

type SubAssemblyOperation =
    | "BUILD_NEW"
    | "REGISTER_EXISTING"
    | "ADD_COMPONENTS";


type SubAssemblyRequest = {
    id: number;
    selectedSubAssemblyId: string;
    operation: SubAssemblyOperation;
    operationCount: number;
};

interface MonthlyProduction {
    content_type: string;
    object_id: number;
    object_name: string;
    object_version: string | null;
    month: Date | null;
    amount_produced: number;
}

interface BuiltSubAssemblies {
    id: string;
    built_at: string;
    built_by: string;
    status: string;
    subassembly: number;
    used_by: string;
    belongs_to_batch: number | null;
    batch_name: string | null;
}

interface BuiltDetectors {
    mac_address: string;
    code?: string;
    built_at: string;
    assembled_by: string | number; // can be PK
    status: string;
    detector: number | null;
    belongs_to_batch: number | null;
    batch_name: string | null;
    subassemblies: string[]; // IDs only
}

interface FNNUsers {
    id: number;
    user_id: number;
    username: string;
    email: string;
    first_name: string;
    last_name: string;
}

interface Batch {
    batchID: number;
    batch_name: string;
    region: string;
    detector_version: number;
    target_quantity: number;
    status: string;
    detectors_built: number;
    subassemblies_built: number;
}

interface BatchProgress {
    batchID: number;
    batch_name: string;
    status: string;
    detectors: { target: number; built: number; remaining: number };
    subassemblies: {
        total_needed: number;
        built: number;
        bom_per_detector: Record<string, number>;
        built_breakdown: Record<string, number>;
    };
}

const LOCAL_MODE = false;

function apiUrl(path: string): string {
    return LOCAL_MODE ? `http://localhost:8000${path}` : path;
}

function credentials(): RequestCredentials {
    return LOCAL_MODE ? "include" : "same-origin";
}

// Function to ensure the time displayed in the order table matches the correct from backend (Avoid showing 1 day earlier)
function formatDateForDisplay(date: Date | string | null | undefined) {
    if (!date) return 'Unknown';

    // Convert string to Date object
    const d = typeof date === 'string' ? new Date(date + 'T00:00:00') : date;
    // Use UTC methods to avoid timezone shift
    const year = d.getUTCFullYear();
    const month = d.getUTCMonth() + 1;
    const day = d.getUTCDate();

    return `${month.toString().padStart(2, '0')}/${day.toString().padStart(2, '0')}/${year}`;
}




const InventoryManagement = () => {

    const { first_name, last_name, email, username } = useAuth();
    const slipRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const [invInfo, setInvInfo] = useState<InventoryItems[]>([]);
    const [subAssemblies, setSubAssemblies] = useState<SubAssembly[]>([]);
    const [detectors, setDetectors] = useState<Detector[]>([]);
    const [builtSubAssemblies, setBuiltSubAssemblies] = useState<BuiltSubAssemblies[]>([]);
    const [builtDetectors, setBuiltDetectors] = useState<BuiltDetectors[]>([]);
    const [fnnUsers, setFnnUsers] = useState<FNNUsers[]>([]);
    const [production, setProduction] = useState<MonthlyProduction[]>([]);
    const [showSectionPopup, setShowSectionPopup] = useState(false);
    const [showBOMPopup, setShowBOMPopup] = useState(false);
    const [showDetBOMPopup, setShowDetBOMPopup] = useState(false);
    const [activeSection, setActiveSection] = useState("");
    const [invalidFields, setInvalidFields] = useState<string[]>([]);
    const [barTooltip, setBarTooltip] = useState<{ text: string; x: number; y: number } | null>(null);

    // --- State ---
    const [sortMode, setSortMode] = useState<'alphabetical' | 'amount' | 'lastCounted' | 'mpn'>('lastCounted');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    // Category filter state
    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
    // Sub-assembly filter state
    const [filterSelectedSubAssemblies, setFilterSelectedSubAssemblies] = useState<number[]>([]);


    // Sorting options
    const sortOptions = [
        { value: 'lastCounted', label: 'Last Counted' },
        { value: 'alphabetical', label: 'Alphabetical' },
        { value: 'amount', label: 'By Amount' },
        { value: 'mpn', label: 'By MPN (FNN #)' },
    ];

    const location = useLocation();

    useEffect(() => {
        if (location.hash === "#openOrders") {
            setActiveSection("OrderParts");
            setShowSectionPopup(true);
            // Remove the hash from the URL so it looks clean
            window.history.replaceState(null, "", window.location.pathname);
        }
    }, [location.hash]);

    const queryParams = new URLSearchParams(location.search);

    const initialTab = queryParams.get("tab");
    const initialBatch = queryParams.get("batch");

    useEffect(() => {
        if (initialTab === "orders") {
            setActiveInvTab("orders");
        }
    }, []);

    useEffect(() => {
        if (initialBatch) {
            const batchId = Number(initialBatch);
            setSelectedBatchFilter(batchId);

            // Remove the hash from the URL so it looks clean
            window.history.replaceState(null, "", window.location.pathname);
        }
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(location.search);

        const tab = params.get("tab");
        const mac = params.get("mac");
        const status = params.get("status");

        const openBOM = params.get("openBOM");
        const subName = params.get("sub");
        const detId = params.get("det");

        // -----------------------------
        // 1. TAB HANDLING (NEW)
        // -----------------------------
        if (tab) {
            setActiveInvTab(tab as any);
        }

        // -----------------------------
        // 2. UNITS FILTER (REPLACES openAssembly)
        // -----------------------------
        if (tab === "units" && mac) {
            setSelectedUnits("Detectors");
            setSelectedUnitStatus(status || "installed");
            setOpenSlips({ [mac]: true });

            setTimeout(() => {
                const targetSlip = slipRefs.current[mac];
                if (targetSlip) {
                    targetSlip.scrollIntoView({
                        behavior: "smooth",
                        block: "center"
                    });
                }
            }, 400);
        }

        // -----------------------------
        // 3. BOM LOGIC (UNCHANGED)
        // -----------------------------
        if (openBOM === "true") {
            if (subName) {
                const foundSub = subAssemblies.find(sa => sa.name === subName);
                if (foundSub) {
                    setSelectedSubAssembly(foundSub);
                    setShowBOMPopup(true);
                }
            } else if (detId) {
                const foundDet = detectors.find(d => d.id === Number(detId));
                if (foundDet) {
                    setSelectedDetector2(foundDet);
                    setShowDetBOMPopup(true);
                }
            }

            window.history.replaceState(null, "", window.location.pathname);
            return;
        }

    }, [location.search, subAssemblies, detectors]);




    // Function to create toast
    const warnUser = (message: string, fieldName?: string) => {
        toast.warn(message, {
            autoClose: 4000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            style: {
                backgroundColor: 'white',
                color: 'black',
                fontSize: '16px',
            },
        });

        if (fieldName) {
            setInvalidFields((prev) => {
                const updated = new Set(prev);
                updated.add(fieldName);
                return Array.from(updated);
            });
        }
    };

    const fetchCsrfToken = async () => {
        const response = await fetch(apiUrl('/api/csrf-token/'), {
            method: 'GET',
            credentials: credentials(),
        });

        if (response.ok) {
            const data = await response.json();
            return data.csrfToken; // Return the CSRF token
        } else {
            console.error('Failed to fetch CSRF token');
            return null;
        }
    };

    const fetchApiInfo = async () => {
        try {
            const response = await fetch(apiUrl('/api/inventory_view/'), {
                credentials: credentials() // ensures cookies/session are sent for auth
            });

            const data = await response.json();
            // console.log(data)

            if (response.ok) {
                setInvInfo(data); // Assign the fetched gallery data to state
            } else {
                console.error("Failed to load api information:", data.error || response.statusText);
            }
        } catch (error) {
            console.error("❌ Error fetching inventory info:", error);
        }
    };

    useEffect(() => {
        fetchApiInfo();
    }, []);

    useEffect(() => {
        const fetchSubAssemblies = async () => {
            try {
                const response = await fetch(apiUrl('/api/inventory_subassemblies/'), {
                    credentials: credentials()
                });
                const data = await response.json();
                if (response.ok) {
                    setSubAssemblies(data);
                } else {
                    console.error("Failed to load subassemblies:", data.error || response.statusText);
                }
            } catch (error) {
                console.error("❌ Error fetching subassemblies:", error);
            }
        };

        const fetchDetectors = async () => {
            try {
                const response = await fetch(apiUrl('/api/inventory_detectors/'), {
                    credentials: credentials()
                });
                const data = await response.json();
                if (response.ok) {
                    setDetectors(data);
                } else {
                    console.error("Failed to load detectors:", data.error || response.statusText);
                }
            } catch (error) {
                console.error("❌ Error fetching detectors:", error);
            }
        };

        const fetchFNNUsers = async () => {
            try {
                const response = await fetch(apiUrl('/api/inventory/fnn-users/'), {
                    credentials: credentials()
                });
                const data = await response.json();
                if (response.ok) {
                    setFnnUsers(data);
                } else {
                    console.error("Failed to load fnn users:", data.error || response.statusText);
                }
            } catch (error) {
                console.error("❌ Error fetching fnn users:", error);
            }
        };

        fetchSubAssemblies();
        fetchDetectors();
        fetchFNNUsers();
    }, []);

    const fetchBuiltSubAssemblies = async () => {
        try {
            const response = await fetch(apiUrl('/api/inventory_built_subs/'), {
                credentials: credentials()
            });
            const data = await response.json();
            if (response.ok) {
                setBuiltSubAssemblies(data);
            } else {
                console.error("Failed to load subassemblies:", data.error || response.statusText);
            }
        } catch (error) {
            console.error("❌ Error fetching subassemblies:", error);
        }
    };

    useEffect(() => {
        fetchBuiltSubAssemblies();
    }, []);

    const fetchbuiltDetectors = async () => {
        try {
            const response = await fetch(apiUrl('/api/inventory_built_dets/'), {
                credentials: credentials()
            });
            const data = await response.json();
            if (response.ok) {
                setBuiltDetectors(data);
            } else {
                console.error("Failed to load subassemblies:", data.error || response.statusText);
            }
        } catch (error) {
            console.error("❌ Error fetching subassemblies:", error);
        }
    };

    useEffect(() => {
        fetchbuiltDetectors();
    }, []);

    const fetchMonthlyProdution = async () => {
        try {
            const response = await fetch(apiUrl('/api/inventory_montly_production/'), {
                credentials: credentials()
            });

            const data = await response.json();

            if (response.ok) {
                setProduction(data); // Assign the fetched gallery data to state
            } else {
                console.error("Failed to load monthly production info", data.error || response.statusText);
            }
        } catch (error) {
            console.error("❌ Error fetching monthly production info:", error);
        }
    };

    useEffect(() => {
        fetchMonthlyProdution();
    }, []);

    const detectorProduction = production.filter(item => item.content_type === 'detector');
    const subAssemblyProduction = production.filter(item => item.content_type === 'subassembly');


    // useEffect(() => {
    //     console.log("🔧 SubAssemblies:", subAssemblies);
    //     console.log("📦 Detectors:", detectors);
    //     console.log("📦 Production:", production);
    // }, [subAssemblies, detectors, production]);

    // Sorting inventory
    // To handle searching parts
    const [searchTerm, setSearchTerm] = useState("");
    const [orderSearchTerm, setOrderSearchTerm] = useState("");

    const filteredInventory = invInfo
        // Category filter
        .filter(item =>
            selectedCategories.length ? selectedCategories.includes(item.category) : true
        )
        // Sub-assembly filter
        .filter(item => {
            if (filterSelectedSubAssemblies.length === 0) return true;
            return subAssemblies.some(sa =>
                filterSelectedSubAssemblies.includes(sa.id) &&
                sa.parts.some(p => p.part === item.partID)
            );
        })
        // Search filter
        .filter(item => {
            if (!searchTerm.trim()) return true;
            const lower = searchTerm.toLowerCase();
            return (
                item.partName.toLowerCase().includes(lower) ||
                item.mpn?.toLowerCase().includes(lower) ||
                item.description?.toLowerCase().includes(lower)
            );
        });



    const sortedInventory = [...filteredInventory].sort((a, b) => {
        if (sortMode === 'alphabetical') {
            return a.partName.localeCompare(b.partName);
        } else if (sortMode === 'amount') {
            return b.currentAmount - a.currentAmount;
        } else if (sortMode === 'lastCounted') {
            const aDate = new Date(a.lastCounted || '1970-01-01');
            const bDate = new Date(b.lastCounted || '1970-01-01');
            return aDate.getTime() - bDate.getTime();
        } else if (sortMode === 'mpn') {
            if (!a.mpn) return 1;
            if (!b.mpn) return -1;
            return a.mpn.localeCompare(b.mpn);
        }

        return 0;
    });



    function getColorTooltip(lastCounted: string | null | undefined): string {
        if (!lastCounted) return "Gray - Item has never been counted.";

        const now = new Date();
        const countedDate = new Date(lastCounted);
        const diffInDays = Math.floor((now.getTime() - countedDate.getTime()) / (1000 * 60 * 60 * 24));

        if (diffInDays > 60) return `Red - Item has not been counted in over ${diffInDays} days.`;
        if (diffInDays > 30) return `Orange - Item has not been counted in over ${diffInDays} days.`;
        return "Normal - Item has been counted recently.";
    }

    function showInfoWithColor(lastCounted: string | null | undefined, extraClassName?: string) {
        const tooltipText = getColorTooltip(lastCounted);

        // Don't show icon if counted recently
        if (tooltipText.startsWith("Normal")) return null;

        // Decide icon color based on the tooltip prefix
        let iconColor = "black"; // default
        if (tooltipText.startsWith("Red")) iconColor = "red";
        else if (tooltipText.startsWith("Orange")) iconColor = "orange";
        else if (tooltipText.startsWith("Gray")) iconColor = "gray";

        return (
            <div className="tooltip-wrapper">
                <PiWarningDuotone style={{ color: iconColor }} />
                <div className={`custom-tooltip ${extraClassName ?? ""}`}>
                    {tooltipText}
                </div>
            </div>
        );
    }

    function showInfoTooltip(message: string, extraClassName?: string) {
        return (
            <div className="tooltip-wrapper">
                <FaCircleInfo />
                <div className={`custom-tooltip ${extraClassName ?? ""}`}>
                    {message}
                </div>
            </div>
        );
    }

    // Util function to find the id of the logged in user
    const loggedFnnUserId = React.useMemo(() => {
        if (!fnnUsers?.length) return null;

        const matchedUser = fnnUsers.find(
            (user) =>
                user.username === username &&
                user.first_name === first_name &&
                user.last_name === last_name
        );

        return matchedUser ? matchedUser.id : null;
    }, [fnnUsers, username, first_name, last_name]);

    const getFnnUserName = (userId: string | number | null | undefined) => {
        if (!userId || !fnnUsers?.length) return "—";
        const user = fnnUsers.find((u) => String(u.id) === String(userId));
        if (!user) return "—";
        return `${user.first_name} ${user.last_name}`.trim() || user.username || "—";
    };



    // PUT, POST, and DELETE request components
    const [editingPartId, setEditingPartId] = useState<number | null>(null);
    const [editedValues, setEditedValues] = useState<Partial<InventoryItems>>({});
    const [showAddItemPopup, setShowAddItemPopup] = useState(false);
    const [showDeleteItemPopup, setShowDeleteItemPopup] = useState(false);
    const [showCountedPopup, setShowCountedPopup] = useState(false);
    const [itemToCount, setItemToCount] = useState<InventoryItems | null>(null);
    const [itemToDeleteId, setItemToDeleteId] = useState<number | null>(null);
    const [showArrivalItemPopup, setShowArrivalItemPopup] = useState(false);
    const [showConfirmProceedPopup, setShowConfirmProceedPopup] = useState(false);
    const [showLocationPopup, setShowLocationPopup] = useState(false);
    const [activeLocation, setActiveLocation] = useState<string | null>(null);
    const [activeLocationPicture, setActiveLocationPicture] = useState<string | undefined>(undefined);


    const CATEGORY_OPTIONS = [
        { value: 'misc', label: 'Accessories & Miscellaneous' },
        { value: 'cable', label: 'Wiring & Connectors' },
        { value: 'eletronics', label: 'Electronics' },
        { value: 'mounting', label: 'Fasteners & Mounting' },
        { value: 'branding', label: 'Branding & Labeling' },
        { value: 'power', label: 'Power Components' },
        { value: 'structural', label: 'Structural' },
        { value: 'protection', label: 'Protection' },
        { value: 'marketing', label: 'Marketing' },
        { value: 'tools', label: 'Tools' },
        { value: 'outsourced', label: 'Outsourced Component' },
    ];

    const [newPartData, setNewPartData] = useState<{
        partName: string;
        currentAmount: number;
        unitCost: number;
        category: string;
        partPictureFile?: File | null;
        partDocumentFile?: File | null;
        partLink?: string;
        location?: string;
        description?: string;
        mpn?: string;
        manufacturer?: string;
        partLocationFile?: File | null;
        stock_target?: number | null;
        sold_in_packets?: boolean;
        amount_per_packet?: number | null;
        bundle_subassembly?: number | null;
    }>({
        partName: '',
        currentAmount: 0,
        unitCost: 0,
        category: CATEGORY_OPTIONS[0].value,
        partPictureFile: null,
        partDocumentFile: null,
        partLink: '',
        location: '',
        description: '',
        mpn: '',
        manufacturer: '',
        partLocationFile: null,
        stock_target: null,
        sold_in_packets: false,
        amount_per_packet: null,
        bundle_subassembly: null
    });


    const resetNewPartData = () => {
        setNewPartData({
            partName: '',
            currentAmount: 0,
            unitCost: 0,
            category: CATEGORY_OPTIONS[0].value,
            partPictureFile: null,
            partDocumentFile: null,
            partLink: '',
            location: '',
            description: '',
            mpn: '',
            manufacturer: '',
            partLocationFile: null,
            stock_target: null,
            sold_in_packets: false,
            amount_per_packet: null,
            bundle_subassembly: null
        });
    };



    const updateInventoryItem = async (id: number, updatedFields: Partial<InventoryItems>) => {
        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        const original = invInfo.find(item => item.partID === id);
        if (!original) return;

        const { partPicture, partDocument, location_picture, ...restOriginal } = original;
        const payload = { ...restOriginal, ...updatedFields };

        try {
            const response = await fetch(apiUrl(`/api/inventory_view/${id}/`), {
                method: 'PUT',
                credentials: credentials(),
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const updatedItem = await response.json();

                // Check if currentAmount changed AND item has a bundle subassembly
                const amountChanged = 'currentAmount' in updatedFields;
                const linkedSubId = updatedFields.bundle_subassembly ?? original.bundle_subassembly;
                const linkedSub = linkedSubId ? subAssemblies.find(sa => sa.id === linkedSubId) : null;

                if (amountChanged && linkedSub) {
                    const oldAmount = original.currentAmount;
                    const newAmount = updatedFields.currentAmount!;
                    const delta = newAmount - oldAmount;

                    // Propagate delta to all sub-assembly parts via individual PUTs
                    const bundleUpdatePromises = linkedSub.parts.map(async (subPart) => {
                        const partItem = invInfo.find(i => i.partID === subPart.part);
                        if (!partItem) return null;

                        const adjustment = subPart.quantity_required * delta;
                        const newPartAmount = Math.max(0, partItem.currentAmount + adjustment);

                        const { partPicture: pp, partDocument: pd, location_picture: lp, ...restPart } = partItem;
                        const partPayload = { ...restPart, currentAmount: newPartAmount };

                        const partResponse = await fetch(apiUrl(`/api/inventory_view/${subPart.part}/`), {
                            method: 'PUT',
                            credentials: credentials(),
                            headers: {
                                'Content-Type': 'application/json',
                                'X-CSRFToken': csrfToken
                            },
                            body: JSON.stringify(partPayload)
                        });

                        if (partResponse.ok) {
                            return await partResponse.json() as InventoryItems;
                        }
                        return null;
                    });

                    const updatedParts = await Promise.all(bundleUpdatePromises);

                    // Update state: the saved item + all bundle parts
                    setInvInfo(prev => prev.map(item => {
                        if (item.partID === id) return updatedItem;
                        const bundleUpdate = updatedParts.find(u => u?.partID === item.partID);
                        if (bundleUpdate) return bundleUpdate;
                        return item;
                    }));

                    toast.success(
                        `Bundle item updated. Also updated count of all items in "${linkedSub.name}".`,
                        {
                            autoClose: 6000,
                            hideProgressBar: false,
                            closeOnClick: true,
                            pauseOnHover: true,
                            draggable: true,
                            style: { backgroundColor: "white", color: "black", fontSize: "16px" },
                        }
                    );

                } else {
                    // Normal save, no bundle propagation
                    setInvInfo(prev => prev.map(item => item.partID === id ? updatedItem : item));

                    toast.success("Item updated successfully!", {
                        autoClose: 4000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        style: { backgroundColor: "white", color: "black", fontSize: "16px" },
                    });
                }

            } else {
                const errorText = await response.text();
                toast.error(`Failed to update item: ${errorText}`, {
                    autoClose: 4000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    style: { backgroundColor: "white", color: "black", fontSize: "16px" },
                });
                console.error("Error details:", errorText);
            }
        } catch (err) {
            console.error("❌ PUT request failed:", err);
        }
    };

    // New dedicated function alongside updateInventoryItem
    const markItemCounted = async (id: number, currentAmount: number) => {
        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        try {
            const response = await fetch(apiUrl(`/api/inventory_view/${id}/mark_counted/`), {
                method: 'POST',
                credentials: credentials(),
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken,
                },
                body: JSON.stringify({ currentAmount }),
            });

            if (response.ok) {
                const updatedItems: InventoryItems[] = await response.json();
                setInvInfo(prev =>
                    prev.map(item => {
                        const updated = updatedItems.find(u => u.partID === item.partID);
                        if (!updated) return item;

                        return {
                            ...item,
                            currentAmount: updated.currentAmount,
                            lastCounted: updated.lastCounted,
                            // ONLY fields that actually change in this endpoint
                        };
                    })
                );
                toast.success("Item counted successfully!", { autoClose: 4000 });
            } else {
                // Get the raw text to see the actual Django error
                const errorText = await response.text();
                console.error("mark_counted error response:", errorText);
                toast.error(`Failed: ${response.status} ${response.statusText}`, { autoClose: 6000 });
            }
        } catch (err) {
            console.error("mark_counted request failed:", err);
        }
    };

    const handleFileUpload = async (
        partID: number,
        file: File,
        fileType: 'image' | 'document' | 'location'
    ) => {

        // --- File size validation ---
        if (file.size > 1024 * 1024) {
            toast.warn("Files must be at most 1MB", {
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                style: {
                    backgroundColor: 'white',
                    color: 'black',
                    fontSize: '16px',
                },
            });
            return; // Stop upload
        }

        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        const original = invInfo.find((item) => item.partID === partID);
        if (!original) return;

        const formData = new FormData();

        formData.append('partName', editedValues.partName ?? original.partName);
        formData.append('currentAmount', String(editedValues.currentAmount ?? original.currentAmount));
        formData.append('unitCost', String(editedValues.unitCost ?? original.unitCost));
        formData.append('category', editedValues.category ?? original.category);

        if (fileType === 'image') formData.append('partPicture', file);
        else if (fileType === 'document') formData.append('partDocument', file);
        else if (fileType === 'location') formData.append('location_picture', file);

        try {
            const response = await fetch(apiUrl(`/api/inventory_view/${partID}/`), {
                method: 'PUT',
                credentials: credentials(),
                headers: { 'X-CSRFToken': csrfToken },
                body: formData,
            });

            if (response.ok) {
                const updatedItem = await response.json();
                setInvInfo((prev) =>
                    prev.map((item) => (item.partID === partID ? updatedItem : item))
                );
                setEditingPartId(null);
                setEditedValues({});
            } else {
                console.error('Failed to upload file:', response.statusText);
                console.error(await response.text());
            }
        } catch (error) {
            console.error('Error uploading file:', error);
        }
    };


    const addNewPart = async () => {
        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        const formData = new FormData();

        formData.append('partName', newPartData.partName);
        formData.append('currentAmount', String(newPartData.currentAmount));
        formData.append('unitCost', String(newPartData.unitCost));
        formData.append('category', newPartData.category);

        if (newPartData.partPictureFile) formData.append('partPicture', newPartData.partPictureFile);
        if (newPartData.partDocumentFile) formData.append('partDocument', newPartData.partDocumentFile);
        if (newPartData.partLink) formData.append('partLink', newPartData.partLink);
        if (newPartData.location) formData.append('location', newPartData.location);
        if (newPartData.description) formData.append('description', newPartData.description);
        if (newPartData.mpn) formData.append('mpn', newPartData.mpn);
        if (newPartData.manufacturer) formData.append('manufacturer', newPartData.manufacturer);
        if (newPartData.partLocationFile) formData.append('location_picture', newPartData.partLocationFile);
        if (newPartData.stock_target != null)
            formData.append('stock_target', String(newPartData.stock_target));

        if (newPartData.sold_in_packets !== undefined) {
            formData.append(
                'sold_in_packets',
                String(newPartData.sold_in_packets) // ✅ boolean → string
            );
        }

        if (newPartData.amount_per_packet !== null && newPartData.amount_per_packet !== undefined) {
            formData.append(
                'amount_per_packet',
                String(newPartData.amount_per_packet) // ✅ number → string
            );
        }


        try {
            const response = await fetch(apiUrl('/api/inventory_view/'), {
                method: 'POST',
                credentials: credentials(),
                headers: {
                    'X-CSRFToken': csrfToken,
                },
                body: formData,
            });

            if (response.ok) {
                const addedItem = await response.json();
                setInvInfo(prev => [...prev, addedItem]);
                // Reset form
                resetNewPartData();
                toast.success("Item created successfully.", toastStyle);
            } else {
                const errorText = await response.text();
                console.error('Failed to add new part:', response.statusText, errorText);
                toast.error(errorText, toastStyle);
            }
        } catch (error) {
            console.error('Error adding new part:', error);
        }
    };

    const deleteInventoryItem = async (id: number) => {
        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        try {
            const response = await fetch(apiUrl(`/api/inventory_view/${id}/`), {
                method: 'DELETE',
                credentials: credentials(),
                headers: {
                    'X-CSRFToken': csrfToken,
                },
            });

            if (response.ok || response.status === 204) {
                setInvInfo(prev => prev.filter(item => item.partID !== id));
                setShowDeleteItemPopup(false);
                toast.success("Item was deleted successfully.", toastStyle);
                setItemToDeleteId(null);
            } else {
                const errorText = await response.text();
                console.error('Failed to delete item:', response.statusText, errorText);
            }
        } catch (error) {
            console.error('Error deleting item:', error);
        }
    };

    // Function to submit request on ENTER press

    const handleSubmitEdit = () => {
        if (editingPartId === null) return;

        const original = invInfo.find(item => item.partID === editingPartId);
        const hasChanges = original &&
            Object.keys(editedValues).some(key => (original as any)[key] !== (editedValues as any)[key]);

        if (hasChanges) {
            updateInventoryItem(editingPartId, editedValues);
        }

        setEditingPartId(null);
        setEditedValues({});
    };


    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Enter' && editingPartId !== null) {
                handleSubmitEdit();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [editingPartId, editedValues, invInfo]);

    // ------------------------------------------- Dealing with Sub-Assembly Logic -------------------------------------------
    const [subAssemblyRequests, setSubAssemblyRequests] = useState<SubAssemblyRequest[]>([]);
    const [subAssemblyToDeleteId, setSubAssemblyToDeleteId] = useState<number | null>(null);
    const [showDeleteSubAssemblyPopup, setShowDeleteSubAssemblyPopup] = useState(false);
    const [showAddSubAssemblyPopup, setShowAddSubAssemblyPopup] = useState(false);

    // Adding/Editing new Sub-Assembly
    const [newSubAssemblyName, setNewSubAssemblyName] = useState('');
    const [newSubAssemblyPic, setNewSubAssemblyPic] = useState<File | null>(null);
    const [newSubAssemblySop, setNewSubAssemblySop] = useState<File | null>(null);
    const [newSubAssemblyParts, setNewSubAssemblyParts] = useState<
        { part: number; quantity_required: number }[]
    >([]);
    const [selectedPartId, setSelectedPartId] = useState<number | null>(null);
    const [selectedPartAmount, setSelectedPartAmount] = useState<number>(1);
    const [editingSubAssemblyId, setEditingSubAssemblyId] = useState<number | null>(null);

    // Additonal popup handling to build a sub-assembly
    const [showBuildConfirmPopup, setShowBuildConfirmPopup] = useState(false);
    const [showBuildResultPopup, setShowBuildResultPopup] = useState(false);
    // Add this with your other useState declarations near the top
    const [buildSummary, setBuildSummary] = useState<{
        buildNew: { sub: string; qty: number }[];
        register: { sub: string; qty: number }[];
        addComponents: { sub: string; qty: number }[];
    }>({
        buildNew: [],
        register: [],
        addComponents: []
    });



    const [newlyBuiltSubs, setNewlyBuiltSubs] = useState<{ name: string; qty: number; ids: string[] }[]>([]);
    const [requestSearchTerms, setRequestSearchTerms] = useState<Record<number, string>>({});
    const [requestDropdownOpen, setRequestDropdownOpen] = useState<Record<number, boolean>>({});
    const [showDiscardPopup, setShowDiscardPopup] = useState(false);
    const [discardTargetId, setDiscardTargetId] = useState<string | null>(null);
    const [discardSubAssemblyTypeId, setDiscardSubAssemblyTypeId] = useState<string | null>(null);

    // For Build Sub-Assembly popup
    const [selectedBatchForBuild, setSelectedBatchForBuild] = useState<number | null>(null);


    const handleRequestChange = <K extends keyof SubAssemblyRequest>(
        index: number,
        field: K,
        value: SubAssemblyRequest[K]
    ) => {
        const updatedRequests = [...subAssemblyRequests];
        updatedRequests[index][field] = value;
        setSubAssemblyRequests(updatedRequests);
    };

    const addNewRequest = () => {
        setSubAssemblyRequests(prev => [
            ...prev,
            {
                id: Date.now(),
                selectedSubAssemblyId: "",
                operation: "BUILD_NEW" as SubAssemblyOperation,
                operationCount: 1,
            },
        ]);
    };

    // One Request is always open by default
    useEffect(() => {
        if (!showSectionPopup) return;

        // If batch is selected → start EMPTY
        if (selectedBatchForBuild) {
            setSubAssemblyRequests([]);
            setRequestSearchTerms({});
            setRequestDropdownOpen({});
            return;
        }

        // If NO batch → ensure at least one request exists
        setSubAssemblyRequests(prev => {
            if (prev.length > 0) return prev;

            return [
                {
                    id: Date.now(),
                    selectedSubAssemblyId: "",
                    operation: "BUILD_NEW" as SubAssemblyOperation,
                    operationCount: 1,
                },
            ];
        });
    }, [showSectionPopup, selectedBatchForBuild]);


    const removeRequest = (index: number) => {
        setSubAssemblyRequests(prev => prev.filter((_, i) => i !== index));
        setRequestSearchTerms(prev => {
            const updated = { ...prev };
            delete updated[index];
            return updated;
        });
        setRequestDropdownOpen(prev => {
            const updated = { ...prev };
            delete updated[index];
            return updated;
        });
    };


    // Helper for clean grammar
    const formatList = (items: string[]) => {
        if (items.length === 1) return items[0];
        if (items.length === 2) return `${items[0]} and ${items[1]}`;

        return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
    };

    // Checking if it is possible to perform build operations
    const validateBuildRequests = () => {

        const invalidSubs: string[] = [];
        const invalidParts: string[] = [];

        /* =============================
           1. BATCH VALIDATION (ONLY IF BATCH EXISTS)
        ============================== */

        if (selectedBatchForBuild && selectedBatchProgress) {
            const builtBreakdown = selectedBatchProgress.subassemblies.built_breakdown;
            const requiredPerSub = selectedBatchProgress.subassemblies.bom_per_detector;
            const target = selectedBatchProgress.detectors.target;

            const requestedMap: Record<string, number> = {};

            subAssemblyRequests.forEach(req => {
                if (req.operation !== "BUILD_NEW") return;

                const sub = subAssemblies.find(
                    s => s.id.toString() === req.selectedSubAssemblyId
                );
                if (!sub) return;

                requestedMap[sub.name] = (requestedMap[sub.name] || 0) + req.operationCount;
            });

            for (const [subName, requestedQty] of Object.entries(requestedMap)) {
                const alreadyBuilt = builtBreakdown[subName] || 0;
                const requiredTotal = (requiredPerSub[subName] || 0) * target;

                if (alreadyBuilt + requestedQty > requiredTotal) {
                    invalidSubs.push(subName);
                }
            }
        }

        /* =============================
           2. INVENTORY VALIDATION (ALWAYS RUN)
        ============================== */

        const partUsage: Record<number, number> = {};

        for (const req of subAssemblyRequests) {
            if (req.operation !== "BUILD_NEW") continue;

            const sub = subAssemblies.find(
                s => s.id.toString() === req.selectedSubAssemblyId
            );
            if (!sub) continue;

            for (const partReq of sub.parts) {
                const partId = partReq.part;
                const totalNeeded = partReq.quantity_required * req.operationCount;

                partUsage[partId] = (partUsage[partId] || 0) + totalNeeded;
            }
        }

        for (const [partIdStr, totalNeeded] of Object.entries(partUsage)) {
            const partId = Number(partIdStr);
            const part = invInfo.find(p => p.partID === partId);

            const available = part?.currentAmount || 0;
            const partName = part?.partName || `Part ${partId}`;

            if (available < totalNeeded) {
                invalidParts.push(partName);
            }
        }

        /* =============================
           TOASTS
        ============================== */

        if (invalidSubs.length > 0) {
            toast.error(
                `You are trying to build more ${formatList(invalidSubs)} than this batch needs.`,
                { ...toastStyle, toastId: "batch-limit-error" }
            );
        }

        if (invalidParts.length > 0) {
            toast.error(
                `You don't have enough ${formatList(invalidParts)}.`,
                { ...toastStyle, toastId: "inventory-error" }
            );
        }

        /* =============================
           RESULT
        ============================== */

        return invalidSubs.length === 0 && invalidParts.length === 0;
    };

    const handleDiscardConfirm = async (addBackToInventory: boolean) => {
        if (!discardTargetId || !discardSubAssemblyTypeId) return;

        // Mark the built sub-assembly as discarded
        await handleUnitStatusChange(discardTargetId, "discarded");

        if (addBackToInventory) {
            const csrfToken = await fetchCsrfToken();
            if (!csrfToken) return;

            await fetch(apiUrl(`/api/inventory_subassemblies/${discardSubAssemblyTypeId}/add/`), {
                method: "POST",
                credentials: credentials(),
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": csrfToken,
                },
                body: JSON.stringify({ times: 1 }),
            });

            await fetchApiInfo();
        }

        setShowDiscardPopup(false);
        setDiscardTargetId(null);
        setDiscardSubAssemblyTypeId(null);
    };

    const handleSubAssemblyBuildAction = async () => {
        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        const newBuiltSubs: { name: string; qty: number; ids: string[] }[] = [];

        for (const req of subAssemblyRequests) {
            const { selectedSubAssemblyId, operation, operationCount } = req;
            const sub = subAssemblies.find(s => s.id.toString() === selectedSubAssemblyId);
            if (!sub) continue;

            const builtIds: string[] = [];

            /* =============================
               CREATE BUILT SUB-ASSEMBLIES
            ============================== */
            if (operation === "BUILD_NEW" || operation === "REGISTER_EXISTING") {
                for (let i = 0; i < operationCount; i++) {
                    const payload = {
                        subassembly: selectedSubAssemblyId,
                        status: "available",
                        built_by: loggedFnnUserId,
                        belongs_to_batch: selectedBatchForBuild ?? null,
                    };

                    const response = await fetch(apiUrl(
                        "/api/inventory_built_subs/"),
                        {
                            method: "POST",
                            credentials: credentials(),
                            headers: {
                                "Content-Type": "application/json",
                                "X-CSRFToken": csrfToken,
                            },
                            body: JSON.stringify(payload),
                        }
                    );

                    if (response.ok) {
                        const data = await response.json();
                        builtIds.push(data.id);
                    }
                }

                newBuiltSubs.push({
                    name: sub.name,
                    qty: operationCount,
                    ids: builtIds,
                });
            }

            /* =============================
               INVENTORY SIDE EFFECTS
            ============================== */
            if (operation === "BUILD_NEW") {
                // consume inventory
                await fetch(apiUrl(
                    `/api/inventory_subassemblies/${selectedSubAssemblyId}/consume/`),
                    {
                        method: "POST",
                        credentials: credentials(),
                        headers: {
                            "Content-Type": "application/json",
                            "X-CSRFToken": csrfToken,
                        },
                        body: JSON.stringify({ times: operationCount }),
                    }
                );
            }

            if (operation === "ADD_COMPONENTS") {
                // add inventory
                await fetch(apiUrl(
                    `/api/inventory_subassemblies/${selectedSubAssemblyId}/add/`),
                    {
                        method: "POST",
                        credentials: credentials(),
                        headers: {
                            "Content-Type": "application/json",
                            "X-CSRFToken": csrfToken,
                        },
                        body: JSON.stringify({ times: operationCount }),
                    }
                );
            }
        }

        setNewlyBuiltSubs(newBuiltSubs);

        await fetchApiInfo();
        await fetchMonthlyProdution();
        await fetchBuiltSubAssemblies();
        await fetchbuiltDetectors();
    };



    function closeConsumeAddPopup(subOrDet: string) {
        if (subOrDet === 'sub') {
            setSubAssemblyRequests([]);
            setRequestSearchTerms({});
            setRequestDropdownOpen({});
            setSelectedBatchProgress(null);
            setSelectedBatchForBuild(null);
        }

        setShowSectionPopup(false);
    }

    const handleDeleteSubAssembly = async (id: number) => {
        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        try {
            const response = await fetch(apiUrl(`/api/inventory_subassemblies/${id}/`), {
                method: 'DELETE',
                credentials: credentials(),
                headers: {
                    'X-CSRFToken': csrfToken,
                },
            });

            if (response.ok || response.status === 204) {
                setSubAssemblies(prev => prev.filter(sa => sa.id !== id));
                setShowDeleteSubAssemblyPopup(false);
                setSubAssemblyToDeleteId(null);
                closeAddSubPopup();
                toast.success("Sub-Assembly deleted successfully.", toastStyle);
            } else {
                const err = await response.text();
                console.error('Failed to delete sub-assembly:', err);
                toast.error("Failed to delete sub-assembly.", toastStyle);
            }
        } catch (error) {
            console.error('❌ Error deleting sub-assembly:', error);
            toast.error("Error deleting sub-assembly.", toastStyle);
        }
    };

    const editOrAddSubAssembly = async () => {
        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        const method = editingSubAssemblyId ? 'PUT' : 'POST';
        const url = editingSubAssemblyId
            ? `/api/inventory_subassemblies/${editingSubAssemblyId}/`
            : '/api/inventory_subassemblies/';

        // Create FormData object
        const formData = new FormData();
        formData.append('name', newSubAssemblyName);
        formData.append('parts', JSON.stringify(newSubAssemblyParts)); // stringify parts

        if (newSubAssemblyPic) {
            formData.append('subPicture', newSubAssemblyPic);
        }
        if (newSubAssemblySop) {
            formData.append('subSOP', newSubAssemblySop);
        }

        try {
            const response = await fetch(apiUrl(url), {
                method,
                credentials: credentials(),
                headers: {
                    'X-CSRFToken': csrfToken,
                    // IMPORTANT: Do NOT set 'Content-Type' header here for FormData
                },
                body: formData,
            });

            if (response.ok) {
                const updatedSub = await response.json();

                setSubAssemblies(prev =>
                    editingSubAssemblyId
                        ? prev.map(s => (s.id === editingSubAssemblyId ? updatedSub : s))
                        : [...prev, updatedSub]
                );

                closeAddSubPopup(); // Reset state and close
                toast.success("Sub-Assembly updated successfully.", toastStyle);
            } else {
                const errorText = await response.text();
                console.error('Failed to save subassembly:', errorText);
                toast.error("Failed to save subassembly.", toastStyle);
            }
        } catch (err) {
            console.error('❌ Error saving subassembly:', err);
            toast.error("Error saving subassembly.", toastStyle);
        }
    };

    const [existingPicName, setExistingPicName] = useState('');
    const [existingSopName, setExistingSopName] = useState('');

    useEffect(() => {
        if (editingSubAssemblyId) {
            const editingSub = subAssemblies.find(s => s.id === editingSubAssemblyId);
            if (editingSub) {
                setNewSubAssemblyName(editingSub.name);
                setNewSubAssemblyParts(editingSub.parts);

                setNewSubAssemblyPic(null);
                setNewSubAssemblySop(null);

                // extract filename from URL
                setExistingPicName(editingSub.subPicture?.split('/').pop() ?? '');
                setExistingSopName(editingSub.subSOP?.split('/').pop() ?? '');
            }
        }
    }, [editingSubAssemblyId]);



    const closeAddSubPopup = () => {
        setShowAddSubAssemblyPopup(false);
        setNewSubAssemblyName('');
        setNewSubAssemblyPic(null);
        setNewSubAssemblySop(null);
        setExistingSopName('');
        setExistingPicName('');
        setSelectedPartId(null);
        setSelectedPartAmount(1);
        setNewSubAssemblyParts([]);
        setEditingSubAssemblyId(null);
    };


    // ------------------------------------------- Dealing with Sub-Assembly Logic -------------------------------------------

    // Optional: Central toast style object
    const toastStyle = {
        autoClose: 6000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: {
            backgroundColor: 'white',
            color: 'black',
            fontSize: '16px',
        },
    };



    // Adding and Deleting Detectors
    const [newDetectorName, setNewDetectorName] = useState('');
    const [newDetectorVersion, setNewDetectorVersion] = useState('');
    const [newDetectorSubAssemblies, setNewDetectorSubAssemblies] = useState<
        { subassembly: number; quantity_required: number }[]
    >([]);
    const [selectedSubAssemblyId, setSelectedSubAssemblyId] = useState<number | null>(null);
    const [selectedSubAssemblyAmount, setSelectedSubAssemblyAmount] = useState<number>(1);
    const [editingDetectorId, setEditingDetectorId] = useState<number | null>(null);
    const [detectorToDeleteId, setDetectorToDeleteId] = useState<number | null>(null);
    const [showAddDetectorPopup, setShowAddDetectorPopup] = useState(false);
    const [showDeleteDetectorPopup, setShowDeleteDetectorPopup] = useState(false);

    const editOrAddDetector = async () => {
        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        const method = editingDetectorId ? 'PUT' : 'POST';
        const url = editingDetectorId
            ? `/api/inventory_detectors/${editingDetectorId}/`
            : '/api/inventory_detectors/';

        // Prepare the payload
        // const payload = {
        //     name: newDetectorName,
        //     version: newDetectorVersion,
        //     subassemblies: newDetectorSubAssemblies,
        // };

        // console.log('Sending payload:', JSON.stringify(payload, null, 2));

        try {
            const response = await fetch(apiUrl(url), {
                method,
                credentials: credentials(),
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken,
                },
                body: JSON.stringify({
                    name: newDetectorName,
                    version: newDetectorVersion,
                    subassemblies: newDetectorSubAssemblies,
                }),
            });

            if (response.ok) {
                const updated = await response.json();
                setDetectors(prev =>
                    editingDetectorId
                        ? prev.map(d => (d.id === editingDetectorId ? updated : d))
                        : [...prev, updated]
                );
                closeAddDetectorPopup();
                toast.success("Detector updated successfully.", toastStyle);
            } else {
                console.error(await response.text());
                toast.error("Failed to update detector.", toastStyle);
            }
        } catch (err) {
            console.error('❌ Error saving detector:', err);
            toast.error("Error saving detector.", toastStyle);
        }
    };

    const handleDeleteDetector = async (id: number) => {
        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        try {
            const response = await fetch(apiUrl(`/api/inventory_detectors/${id}/`), {
                method: 'DELETE',
                credentials: credentials(),
                headers: {
                    'X-CSRFToken': csrfToken,
                },
            });

            if (response.ok || response.status === 204) {
                setDetectors(prev => prev.filter(det => det.id !== id));
                setShowDeleteDetectorPopup(false);
                setDetectorToDeleteId(null);
                closeAddDetectorPopup();
                toast.success("Detector deleted successfully.", toastStyle);
            } else {
                console.error(await response.text());
                toast.error("Failed to delete detector.", toastStyle);
            }
        } catch (error) {
            console.error('❌ Error deleting detector:', error);
            toast.error("Error deleting detector.", toastStyle);
        }
    };

    const closeAddDetectorPopup = () => {
        setShowAddDetectorPopup(false);
        setNewDetectorName('');
        setNewDetectorVersion('');
        setSelectedSubAssemblyId(null);
        setSelectedSubAssemblyAmount(1);
        setNewDetectorSubAssemblies([]);
        setEditingDetectorId(null);
    };

    // ------------------------------------------- Dealing with Detector Logic -------------------------------------------

    // ------------------------------------------- Placing and Managing Orders Logic -------------------------------------------

    const [showAddOrderPopup, setShowAddOrderPopup] = useState(false);
    const [forecastInfo, setForecastInfo] = useState<InventoryForecast[]>([]);
    const [forecastToDeleteId, setForecastToDeleteId] = useState<number | null>(null);
    const [showDeleteForecastPopup, setShowDeleteForecastPopup] = useState(false);
    const [showTypeOrderPopup, setShowTypeOrderPopup] = useState(false);
    const [showBatchRequestTypePopup, setShowBatchRequestTypePopup] = useState(false);
    const [showBatchOrderPopup, setShowBatchOrderPopup] = useState(false);
    const [considerStock, setConsiderStock] = useState<boolean>(true);
    const [showSubAssemblyOrderPopup, setShowSubAssemblyOrderPopup] = useState(false);


    // ------------------------------------------- Placing and Managing Orders Logic -------------------------------------------

    const fetchForecastInfo = async () => {
        try {
            const response = await fetch(apiUrl('/api/inventory_forecast/'), {
                credentials: credentials() // ensures cookies/session are sent for auth
            });

            const data = await response.json();
            // console.log(data)

            if (response.ok) {
                setForecastInfo(data); // Assign the fetched gallery data to state
            } else {
                console.error("Failed to load api information:", data.error || response.statusText);
            }
        } catch (error) {
            console.error("❌ Error fetching forecast info:", error);
        }
    };

    useEffect(() => {
        fetchForecastInfo();
    }, []);

    const handleDeleteForecast = async (id: number) => {
        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        try {
            const response = await fetch(apiUrl(`/api/inventory_forecast/${id}/`), {
                method: 'DELETE',
                credentials: credentials(),
                headers: {
                    'X-CSRFToken': csrfToken,
                },
            });

            if (response.ok) {
                setForecastInfo(prev => prev.filter(item => item.id !== id));
                setShowDeleteForecastPopup(false);
                setForecastToDeleteId(null);
            } else {
                console.error("Failed to delete forecast:", response.statusText);
            }
        } catch (error) {
            console.error("Error deleting forecast:", error);
        }
    };


    // Adding or Editing Orders
    const [forecastPartID, setForecastPartID] = useState<number | null>(null);
    const [forecastPartName, setForecastPartName] = useState('');
    const [forecastPartImage, setForecastPartImage] = useState<string | null>(null);
    const [forecastAmount, setForecastAmount] = useState<number>(0);
    const [forecastCost, setForecastCost] = useState<number>(0);
    const [forecastOrderedDate, setForecastOrderedDate] = useState('');
    const [forecastArrivalDate, setForecastArrivalDate] = useState('');
    const [forecastStatus, setForecastStatus] = useState<string>("Requested");
    const [forecastStoreLinks, setForecastStoreLinks] = useState('');
    const [forecastNotes, setForecastNotes] = useState('');
    const [forecastRequestor, setForecastRequestor] = useState('');
    const [forecastIsPacket, setForecastIsPacket] = useState(false);
    const [forecastNumberOfPackets, setForecastNumberOfPackets] = useState<number>(0);
    const [forecastAmountPerPacket, setForecastAmountPerPacket] = useState<number>(0);
    const [forecastPriority, setForecastPriority] = useState('Low');
    const [forecastLeadTime, setForecastLeadTime] = useState('');
    const [forecastPackCost, setForecastPackCost] = useState(0);
    const [forecastExpectedArrivalDate, setForecastExpectedArrivalDate] = useState('');
    const [forecastBatchId, setForecastBatchId] = useState<number | null>(null);
    const [selectedBatchFilter, setSelectedBatchFilter] = useState<number | "all" | "none">("all");

    const [editingForecastId, setEditingForecastId] = useState<number | null>(null);
    const [partSearchTerm, setPartSearchTerm] = useState('');
    const filteredPartOptions = invInfo.filter((part) => {
        const term = partSearchTerm.toLowerCase();
        return (
            part.partName?.toLowerCase().includes(term) ||
            part.mpn?.toLowerCase().includes(term)
        );
    });
    const [showDropdown, setShowDropdown] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState<keyof typeof statusPriority>("Requested");
    const [selectedManage, setSelectedManage] = useState("Sub-Assemblies");
    const [selectedUnits, setSelectedUnits] = useState("Sub-Assemblies");

    const [arrivalAmount, setArrivalAmount] = useState<number>(0);
    const [activeForecast, setActiveForecast] = useState<InventoryForecast | null>(null);

    const [subAssemblySearchTerm, setSubAssemblySearchTerm] = useState('');
    const [showSubAssemblyDropdown, setShowSubAssemblyDropdown] = useState(false);

    const isPacketOrder = activeForecast?.is_packet && activeForecast.amount_per_packet;
    const [arrivedPackets, setArrivedPackets] = useState<number>(0);

    // For handling the partial ordering
    const [showOrderItemPopup, setShowOrderItemPopup] = useState(false);
    const [orderedAmount, setOrderedAmount] = useState(0);
    const [orderedPackets, setOrderedPackets] = useState(0);

    // =========================
    // Sub-Assembly Order States
    // =========================

    // Search input
    const [subAssemblyOrderSearch, setSubAssemblyOrderSearch] = useState<string>("");

    // Dropdown visibility
    const [showSubAssemblyOrderDropdown, setShowSubAssemblyOrderDropdown] = useState<boolean>(false);

    // Selected sub-assembly (from dropdown)
    const [selectedSubAssemblyOrderId, setSelectedSubAssemblyOrderId] = useState<number | null>(null);

    // Quantity input
    const [selectedSubAssemblyOrderQty, setSelectedSubAssemblyOrderQty] = useState<number>(1);

    // Final list of requests
    const [subAssemblyOrderRequests, setSubAssemblyOrderRequests] = useState<
        { subassemblyId: number; quantity: number }[]
    >([]);

    const resetSubAssemblyOrderState = () => {
        setSubAssemblyOrderRequests([]);
        setOpenBatchSlips({});
        setSubAssemblyOrderSearch('');
        setSelectedSubAssemblyOrderId(null);
        setSelectedSubAssemblyOrderQty(1);
        setShowSubAssemblyOrderDropdown(false);
    };


    // Setting the number of packets to the max possible when confirming arrival
    useEffect(() => {
        if (!showArrivalItemPopup || !activeForecast) return;

        if (isPacketOrder) {
            const maxPackets = activeForecast.number_of_packets ?? 0;

            setArrivedPackets(maxPackets);
            setArrivalAmount(
                maxPackets * (activeForecast.amount_per_packet ?? 1)
            );
        } else {
            setArrivalAmount(activeForecast.expectedAmount);
        }
    }, [showArrivalItemPopup, activeForecast, isPacketOrder]);


    // Filter sub-assemblies dynamically based on search term
    const filteredSubAssemblies = subAssemblies.filter(sa =>
        sa.name.toLowerCase().includes(subAssemblySearchTerm.toLowerCase())
    );

    const isArrivalAmountValid = () => {
        if (!activeForecast) return false;

        if (arrivalAmount <= 0) return false;
        if (arrivalAmount > activeForecast.expectedAmount) return false;

        return true;
    };


    const handleMarkArrived = async (): Promise<boolean> => {
        if (!activeForecast) return false;

        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return false;

        try {
            const response = await fetch(apiUrl(
                `/api/inventory_forecast/${activeForecast.id}/mark-arrived/`),
                {
                    method: "POST",
                    credentials: credentials(),
                    headers: {
                        "Content-Type": "application/json",
                        "X-CSRFToken": csrfToken,
                    },
                    body: JSON.stringify({ arrived_amount: arrivalAmount }),
                }
            );

            if (!response.ok) {
                const errorText = await response.text();
                let message = errorText;

                try {
                    const json = JSON.parse(errorText);
                    message = json.error ?? errorText;
                } catch {  /*  */ }

                toast.warn(message, { autoClose: 5000 });
                return false; // ⛔ STOP FLOW
            }

            await fetchApiInfo();
            await fetchForecastInfo();

            toast.success(`Set ${arrivalAmount} items as Arrived`);
            return true; // ✅ SUCCESS

        } catch (err) {
            toast.error("Unexpected error marking arrival.");
            return false;
        }
    };

    const isOrderAmountValid = () => {
        if (!activeForecast) return false;
        if (orderedAmount <= 0) return false;
        if (orderedAmount > activeForecast.expectedAmount) return false;
        return true;
    };

    // Handler for marking items as ordered
    const handleMarkOrdered = async (): Promise<boolean> => {
        if (!activeForecast) return false;

        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return false;

        try {
            const response = await fetch(apiUrl(
                `/api/inventory_forecast/${activeForecast.id}/mark-ordered/`),
                {
                    method: "POST",
                    credentials: credentials(),
                    headers: {
                        "Content-Type": "application/json",
                        "X-CSRFToken": csrfToken,
                    },
                    body: JSON.stringify({
                        ordered_amount: orderedAmount,
                        expected_arrival_date: forecastExpectedArrivalDate,
                    }),
                }
            );

            if (!response.ok) {
                const errorText = await response.text();
                let message = errorText;

                try {
                    const json = JSON.parse(errorText);
                    message = json.error ?? errorText;
                } catch { /* ignore */ }

                toast.warn(message, { autoClose: 5000 });
                return false; // ⛔ STOP FLOW
            }

            await fetchApiInfo();
            await fetchForecastInfo();

            toast.success(`${orderedAmount} items marked as Ordered`);
            return true; // ✅ SUCCESS

        } catch (err) {
            toast.error("Unexpected error marking order.");
            return false;
        }
    };


    const editOrAddForecast = async () => {
        // Validation checks
        const isValidPart = filteredPartOptions.some(
            (item) => item.partName.toLowerCase() === partSearchTerm.trim().toLowerCase()
        );

        setInvalidFields([]); // clear previous errors

        if (!isValidPart) {
            warnUser("Please select a valid part from the suggestion list.", "part");
            return;
        }

        if (forecastAmount <= 0) {
            warnUser("Amount must be greater than 0.", "amount");
            return;
        }

        if (forecastCost <= 0) {
            warnUser("Cost must be greater than 0.", "cost");
            return;
        }

        if (!forecastStatus) {
            warnUser("Please select a status.", "status");
            return;
        }

        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        const method = editingForecastId ? 'PUT' : 'POST';
        const url = editingForecastId
            ? `/api/inventory_forecast/${editingForecastId}/`
            : '/api/inventory_forecast/';

        const selectedPart = invInfo.find(item => item.partID === forecastPartID);
        const fallbackLink = selectedPart?.partLink || "";

        // Construct the payload
        const payload: any = {
            part: forecastPartID,
            expectedAmount: forecastAmount,
            cost: forecastCost,
            orderedDate: forecastOrderedDate || null,
            arrivalDate: forecastArrivalDate || null,
            status: forecastStatus,
            sourceLinks: forecastStoreLinks.trim() || fallbackLink,
            notes: forecastNotes,
            priority: forecastPriority,
            lead_time: forecastLeadTime,
            is_packet: forecastIsPacket,
            number_of_packets: forecastNumberOfPackets,
            amount_per_packet: forecastAmountPerPacket,
            pack_cost: forecastPackCost,
            expectedArrivalDate: forecastExpectedArrivalDate || null,
            belongs_to_batch: forecastBatchId,
        };

        // Only add `requestor` if it's a POST request
        if (method === 'POST') {
            payload.requestor = `${first_name} ${last_name}`;
            payload.requestor_email = email;
        }

        try {
            const response = await fetch(apiUrl(url), {
                method,
                credentials: credentials(),
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken,
                },
                body: JSON.stringify(payload),
            });

            if (response.ok) {
                const updated = await response.json();
                setForecastInfo((prev: InventoryForecast[]) =>
                    editingForecastId
                        ? prev.map((f: InventoryForecast) =>
                            f.id === editingForecastId ? updated : f
                        )
                        : [...prev, updated]
                );

                if (forecastStatus === 'Arrived') {
                    fetchApiInfo(); // 👈 Refresh when arrived
                }

                setShowAddOrderPopup(false);

                // ✅ Show appropriate toast based on method
                if (method === 'POST') {
                    toast.success(`Order successfully ${forecastStatus.toLowerCase()}`, {
                        autoClose: 4000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        style: {
                            backgroundColor: 'white',
                            color: 'black',
                            fontSize: '16px',
                        },
                    });
                } else {
                    toast.success("Order successfully updated", {
                        autoClose: 4000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        style: {
                            backgroundColor: 'white',
                            color: 'black',
                            fontSize: '16px',
                        },
                    });
                }

                clearForecastForm(); // Reset the input fields
            } else {
                console.error(await response.text());
            }
        } catch (err) {
            console.error('❌ Error saving forecast:', err);
        }
    };

    const clearForecastForm = () => {
        setForecastPartID(null);
        setForecastPartName('');
        setPartSearchTerm('');
        setForecastAmount(0);
        setForecastCost(0);
        setForecastOrderedDate('');
        setForecastArrivalDate('');
        setForecastStatus("Requested");
        setForecastStoreLinks('');
        setForecastNotes('');
        setForecastRequestor('');
        setEditingForecastId(null);
        setForecastIsPacket(false);
        setForecastNumberOfPackets(0);
        setForecastAmountPerPacket(0);
        setForecastPriority("Low");
        setForecastLeadTime('')
        setForecastPartImage(null);
        setForecastPackCost(0)
        setForecastExpectedArrivalDate('');
        setForecastBatchId(null);
    };

    const forecastAmountsByPart: Record<number, number> = {};

    forecastInfo.forEach(forecast => {
        if (!["Returned", "Cancelled", "Arrived"].includes(forecast.status)) {
            forecastAmountsByPart[forecast.part] = (forecastAmountsByPart[forecast.part] || 0) + forecast.expectedAmount;
        }
    });

    const statusPriority = {
        Requested: 1,
        Ordered: 2,
        Arrived: 3,
        Cancelled: 4,
        Returned: 5,
    };

    const today = new Date();

    const sortedForecasts = [...forecastInfo].sort((a, b) => {
        const priorityA = statusPriority[a.status as keyof typeof statusPriority] ?? 999;
        const priorityB = statusPriority[b.status as keyof typeof statusPriority] ?? 999;

        // 1️⃣ Sort by status priority
        if (priorityA !== priorityB) {
            return priorityA - priorityB;
        }

        // 2️⃣ SPECIAL CASE: Requested → sort by requestor (A–Z)
        if (a.status === "Requested" && b.status === "Requested") {
            return a.requestor.localeCompare(b.requestor, undefined, {
                sensitivity: "base",
            });
        }

        // 3️⃣ Default behavior for other statuses

        const arrivalA = a.arrivalDate ? new Date(a.arrivalDate).getTime() : Infinity;
        const arrivalB = b.arrivalDate ? new Date(b.arrivalDate).getTime() : Infinity;

        const diffA = Math.abs(arrivalA - today.getTime());
        const diffB = Math.abs(arrivalB - today.getTime());

        if (diffA !== diffB) {
            return diffA - diffB;
        }

        // 4️⃣ Tie-breaker: most recent order date first
        const orderA = a.orderedDate ? new Date(a.orderedDate).getTime() : 0;
        const orderB = b.orderedDate ? new Date(b.orderedDate).getTime() : 0;

        return orderB - orderA;
    });


    const filteredForecasts = sortedForecasts.filter((forecast) => {
        // status filter
        if (forecast.status !== selectedStatus) return false;

        // batch filter
        if (selectedBatchFilter === "none") {
            // only items with NO batch
            if (forecast.belongs_to_batch !== null) return false;

        } else if (selectedBatchFilter !== "all") {
            // specific batch selected
            if (forecast.belongs_to_batch !== selectedBatchFilter) return false;
        }

        // search filter
        if (!orderSearchTerm.trim()) return true;

        const matchingPart = invInfo.find(p => p.partID === forecast.part);
        const search = orderSearchTerm.toLowerCase();

        return (
            matchingPart?.partName?.toLowerCase().includes(search) ||
            matchingPart?.mpn?.toLowerCase().includes(search) ||
            forecast.requestor?.toLowerCase().includes(search)
        );
    });

    // Automatically resets search upon status change

    useEffect(() => {
        setOrderSearchTerm("");
    }, [selectedStatus]);




    // ------------------------------------------- Placing and Managing Orders Logic -------------------------------------------

    // ------------------------------------------- Functions for Analytics and Cards -------------------------------------------

    // Functions for Analytics and Cards
    const [maxBuildableDetectors, setMaxBuildableDetectors] = useState<number>(0);
    const [nextXDetectors, setNextXDetectors] = useState(1);
    const [partsNeededForNext, setPartsNeededForNext] = useState<Record<number, number>>({});
    const [showRequestItemPopup, setShowRequestItemPopup] = useState(false);
    const [activeRequestPartID, setActiveRequestPartID] = useState<number | null>(null);
    const [requestQuantity, setRequestQuantity] = useState<number>(0);


    // 1. Get latest version detector
    function getLatestDetectorVersion(detectors: Detector[]): Detector | null {
        if (!detectors || detectors.length === 0) return null;

        // Helper to parse version string into number array
        const parseVersion = (version: string) =>
            version.split('.').map(part => Number(part));

        return detectors.reduce((latest, current) => {
            const latestParts = parseVersion(latest.version);
            const currentParts = parseVersion(current.version);

            for (let i = 0; i < Math.max(latestParts.length, currentParts.length); i++) {
                const latestNum = latestParts[i] || 0;
                const currentNum = currentParts[i] || 0;

                if (currentNum > latestNum) return current;
                if (currentNum < latestNum) return latest;
                // if equal, continue to next part
            }

            return latest; // versions are equal
        });
    }


    // 2. Get parts required to build one detector
    function getRequiredPartsForDetector(detector: Detector, subAssemblies: SubAssembly[]) {
        const partRequirements: Record<number, number> = {};

        detector.subassemblies.forEach(({ subassembly, quantity_required }) => {
            const sub = subAssemblies.find(sa => sa.id === subassembly);
            if (!sub) return;

            sub.parts.forEach(({ part, quantity_required: partQty }) => {
                if (!partRequirements[part]) partRequirements[part] = 0;
                partRequirements[part] += partQty * quantity_required;
            });
        });

        return partRequirements;
    }

    // 3. Determine how many detectors can be built
    function getMaxBuildableDetectors(
        detector: Detector,
        subAssemblies: SubAssembly[],
        invInfo: InventoryItems[]
    ): number {
        const partRequirements = getRequiredPartsForDetector(detector, subAssemblies);

        let minBuildable = Infinity;

        for (const partID in partRequirements) {
            const requiredQty = partRequirements[Number(partID)];
            const partInInventory = invInfo.find(item => item.partID === Number(partID));
            const availableQty = partInInventory?.currentAmount ?? 0;

            const buildable = Math.floor(availableQty / requiredQty);
            minBuildable = Math.min(minBuildable, buildable);
        }

        return isFinite(minBuildable) ? minBuildable : 0;
    }

    function getPartsNeededForXDetectors(
        detector: Detector,
        subAssemblies: SubAssembly[],
        invInfo: InventoryItems[],
        targetCount: number
    ): Record<number, number> {
        const requiredParts = getRequiredPartsForDetector(detector, subAssemblies);
        const partsNeeded: Record<number, number> = {};

        // Step 1: Determine how many full detectors we can currently build
        let maxBuildable = Infinity;

        for (const partID in requiredParts) {
            const requiredQty = requiredParts[Number(partID)];
            const partInInventory = invInfo.find(item => item.partID === Number(partID));
            const availableQty = partInInventory?.currentAmount ?? 0;

            const buildableWithThisPart = Math.floor(availableQty / requiredQty);
            maxBuildable = Math.min(maxBuildable, buildableWithThisPart);
        }

        // Step 2: Calculate what’s missing to build `targetCount` more detectors beyond current
        const totalTarget = maxBuildable + targetCount;

        for (const partID in requiredParts) {
            const requiredQtyPerDetector = requiredParts[Number(partID)];
            const totalRequired = totalTarget * requiredQtyPerDetector;

            const partInInventory = invInfo.find(item => item.partID === Number(partID));
            const availableQty = partInInventory?.currentAmount ?? 0;

            if (availableQty < totalRequired) {
                partsNeeded[Number(partID)] = totalRequired - availableQty;
            }
        }

        return partsNeeded;
    }

    const handlePartForecastRequest = async (
        partID: number,
        quantity: number,
        silent = false,
        batchId: number | null = null
    ) => {
        const part = invInfo.find(item => item.partID === partID);
        if (!part) return;

        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        // Determine if part comes in packs
        let finalQuantity = quantity;
        const isPacket = part.sold_in_packets ?? false;
        const amountPerPacket = part.amount_per_packet ?? undefined;
        let numberOfPackets: number | undefined = undefined;
        let packCost: number | undefined = undefined;

        if (isPacket && amountPerPacket && amountPerPacket > 0) {
            numberOfPackets = Math.ceil(quantity / amountPerPacket);
            finalQuantity = numberOfPackets * amountPerPacket;
            packCost = part.unitCost * amountPerPacket;
        }

        // Build payload conditionally
        const payload: Partial<InventoryForecast> = {
            part: part.partID,
            expectedAmount: finalQuantity,
            cost: part.unitCost,
            orderedDate: null,
            arrivalDate: null,
            status: "Requested",
            sourceLinks: part.partLink || "",
            notes: "",
            requestor: `${first_name} ${last_name}`,
            requestor_email: email,
            is_packet: isPacket,
            belongs_to_batch: batchId,
        };

        if (isPacket) {
            payload.number_of_packets = numberOfPackets;
            payload.amount_per_packet = amountPerPacket;
            payload.pack_cost = packCost;
        }

        try {
            const response = await fetch(apiUrl("/api/inventory_forecast/"), {
                method: "POST",
                credentials: credentials(),
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": csrfToken,
                },
                body: JSON.stringify(payload),
            });

            if (response.ok) {
                const newForecast = await response.json();
                setForecastInfo(prev => [...prev, newForecast]);
                setShowSectionPopup(false);
                setNextXDetectors(1);
                setShowTypeOrderPopup(false);

                if (!silent) {
                    toast.success("Item successfully requested!", {
                        autoClose: 4000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        style: {
                            backgroundColor: 'white',
                            color: 'black',
                            fontSize: '16px',
                            zIndex: 9999,
                        },
                    });
                }

            } else {
                console.error("❌ Request failed:", await response.text());
            }
        } catch (err) {
            console.error("❌ Error requesting forecast:", err);
        }
    };

    // ------------------------------------------- Functions for Analytics and Cards -------------------------------------------

    // Define state with correct types
    const [categorySeries, setCategorySeries] = useState<number[]>([]);
    const [categoryLabels, setCategoryLabels] = useState<string[]>([]);
    const [subAssemblyLabels, setSubAssemblyLabels] = useState<string[]>([]);
    const [subAssemblySeries, setSubAssemblySeries] = useState<number[]>([]);
    const [selectedDetectorId, setSelectedDetectorId] = useState<number | null>(null);

    const [subAssemblyChartDetectorId, setSubAssemblyChartDetectorId] = useState<number | null>(null);
    const [buildableDetectorId, setBuildableDetectorId] = useState<number | null>(null);
    const [buildableDetectorCount, setBuildableDetectorCount] = useState<number>(0);

    const [showVersionTooltip, setShowVersionTooltip] = useState(false);

    // Assigning the latest version to the correct detector ID for the donut graph
    useEffect(() => {
        if (detectors.length === 0) return;
        const latestDetector = getLatestDetectorVersion(detectors);
        if (latestDetector) {
            setSelectedDetectorId(latestDetector.id);
            setSubAssemblyChartDetectorId(latestDetector.id);
            setBuildableDetectorId(latestDetector.id);
        }
    }, [detectors]);

    useEffect(() => {
        if (!buildableDetectorId || !detectors.length || !subAssemblies.length || !invInfo.length) return;
        const det = detectors.find(d => d.id === buildableDetectorId);
        if (!det) return;
        setBuildableDetectorCount(getMaxBuildableDetectors(det, subAssemblies, invInfo));
    }, [buildableDetectorId, detectors, subAssemblies, invInfo]);


    useEffect(() => {
        if (!selectedDetectorId || detectors.length === 0 || subAssemblies.length === 0 || invInfo.length === 0) return;

        const selectedDetector = detectors.find(d => d.id === selectedDetectorId);
        if (!selectedDetector) return;

        const requiredParts = getRequiredPartsForDetector(selectedDetector, subAssemblies);
        const maxBuildable = getMaxBuildableDetectors(selectedDetector, subAssemblies, invInfo);
        setMaxBuildableDetectors(maxBuildable);

        const neededParts = getPartsNeededForXDetectors(selectedDetector, subAssemblies, invInfo, nextXDetectors);
        setPartsNeededForNext(neededParts);
    }, [selectedDetectorId, detectors, subAssemblies, invInfo, nextXDetectors]);

    // Assigning the lastest version to the correct detector ID for the donut graph
    useEffect(() => {
        if (detectors.length === 0) return;

        const latestDetector = getLatestDetectorVersion(detectors);
        if (latestDetector) {
            setSelectedDetectorId(latestDetector.id);
        }
    }, [detectors]);

    // Parts per category
    useEffect(() => {
        const categoryCountMap: { [key: string]: number } = {};

        invInfo.forEach(item => {
            if (categoryCountMap[item.category]) {
                categoryCountMap[item.category] += 1;
            } else {
                categoryCountMap[item.category] = 1;
            }
        });

        const labels = Object.keys(categoryCountMap).map(key => {
            const option = CATEGORY_OPTIONS.find(opt => opt.value === key);
            return option ? option.label : key; // fallback to raw key if not found
        });

        setCategorySeries(Object.values(categoryCountMap));
        setCategoryLabels(labels);
    }, [invInfo]);

    // Parts per sub-assembly
    useEffect(() => {
        if (!subAssemblyChartDetectorId) return;

        const detector = detectors.find(d => d.id === subAssemblyChartDetectorId);
        if (!detector) return;

        const subAssemblyMap = new Map(subAssemblies.map(sa => [sa.id, sa]));
        const subAssemblyPartCount: { [key: string]: number } = {};

        detector.subassemblies.forEach(({ subassembly, quantity_required }) => {
            const subAssemblyObj: SubAssembly | undefined =
                typeof subassembly === 'number' ? subAssemblyMap.get(subassembly) : subassembly;
            if (!subAssemblyObj) return;
            const totalParts = subAssemblyObj.parts.reduce((acc, part) => acc + part.quantity_required, 0) * quantity_required;
            subAssemblyPartCount[subAssemblyObj.name] = totalParts;
        });

        setSubAssemblyLabels(Object.keys(subAssemblyPartCount));
        setSubAssemblySeries(Object.values(subAssemblyPartCount));

    }, [subAssemblyChartDetectorId, detectors, subAssemblies]);

    // Parts per detector version
    const [flowOrTable, setFlowOrTable] = useState("flow");
    const [flowOrTableSub, setFlowOrTableSub] = useState<'flow' | 'table'>('flow');

    // Flowchart
    const [selectedDetector2, setSelectedDetector2] = useState<Detector | null>(null);
    const [selectedSubAssembly, setSelectedSubAssembly] = useState<SubAssembly | null>(null);

    // Util functions to display total costs
    function getSubAssemblyCost(sa: SubAssembly, invInfo: InventoryItems[]): number {
        return sa.parts.reduce((total, part) => {
            const inv = invInfo.find(i => i.partID === part.part);
            return inv ? total + inv.unitCost * part.quantity_required : total;
        }, 0);
    }

    function getDetectorCost(detector: Detector, subAssemblies: SubAssembly[], invInfo: InventoryItems[]): number {
        return detector.subassemblies.reduce((total, ds) => {
            const sa =
                typeof ds.subassembly === 'object'
                    ? ds.subassembly
                    : subAssemblies.find(s => s.id === ds.subassembly);

            return sa ? total + getSubAssemblyCost(sa, invInfo) : total;
        }, 0);
    }

    const totalSubAssemblyProductionCost = subAssemblyProduction.reduce((sum, item) => {
        const subassembly = subAssemblies.find(sa => sa.id === item.object_id);
        return subassembly ? sum + getSubAssemblyCost(subassembly, invInfo) * item.amount_produced : sum;
    }, 0);

    // ------ Detector Flowchart ------
    // Set the latest detector version as the default
    useEffect(() => {
        if (detectors.length > 0) {
            const latest = getLatestDetectorVersion(detectors);
            setSelectedDetector2(latest);
        }
    }, [detectors]);

    // Generate flowchart nodes
    function generateFlowNodes(
        detector: Detector,
        subAssemblies: SubAssembly[],
        invInfo: InventoryItems[]
    ): FlowNode[] {
        const nodes: FlowNode[] = [];

        const totalDetectorCost = getDetectorCost(detector, subAssemblies, invInfo).toFixed(2);

        // Detector head node
        nodes.push({
            id: `detector-${detector.id}`,
            type: 'default',
            data: {
                label: (
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontWeight: 'bold' }} >{detector.name} (v{detector.version})</div>
                        <div>Total: ${totalDetectorCost}</div>
                    </div>
                )
            },
            position: { x: 300, y: 50 },
            style: {
                background: '#e6f7ff',
                border: '1px solid #91d5ff',
                borderRadius: 6,
                padding: 10,
            },
        });

        // Sub-assembly nodes
        detector.subassemblies.forEach((ds, index) => {
            const sa =
                typeof ds.subassembly === 'object'
                    ? ds.subassembly
                    : subAssemblies.find((s) => s.id === ds.subassembly);

            if (!sa) return;

            // Build label JSX
            const saCost = getSubAssemblyCost(sa, invInfo).toFixed(2);
            const label = (
                <div style={{ fontSize: 12 }}>

                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: "1rem", justifyContent: 'center' }}>


                        <img
                            src={sa.subPicture ? sa.subPicture : fnnBlueLogo}
                            alt={sa.name}
                            className="part-image"
                            style={{
                                width: 35,
                                height: 35,
                                objectFit: 'cover',
                                marginRight: 8,
                                borderRadius: 4,
                            }}
                        // style={{ cursor: isEditing ? 'pointer' : 'default' }}
                        />

                        <strong>{sa.name} - ${saCost}</strong>

                    </div>


                    <ul style={{ listStyleType: 'none', padding: 0, marginTop: 8 }}>
                        {sa.parts.map((p) => {
                            const invPart = invInfo.find((i) => i.partID === p.part);
                            if (!invPart) return null;

                            const totalCost = (invPart.unitCost * p.quantity_required).toFixed(2);

                            return (
                                <li key={invPart.partID} style={{ display: 'flex', alignItems: 'center', marginBottom: 4, textAlign: 'left' }}>
                                    <img
                                        src={invPart.partPicture ? invPart.partPicture : fnnBlueLogo}
                                        alt={invPart.partName}
                                        className="part-image"
                                        style={{
                                            width: 24,
                                            height: 24,
                                            objectFit: 'cover',
                                            marginRight: 8,
                                            borderRadius: 4,
                                        }}
                                    />
                                    <span>{invPart.mpn ? `${invPart.mpn} - ` : ""} {invPart.partName} x{p.quantity_required} - ${totalCost}</span>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            );

            // Push the node
            nodes.push({
                id: `sub-${sa.id}`,
                type: 'default',
                data: { label },
                position: { x: 100 + index * 250, y: 250 },
                style: {
                    width: 220,
                    padding: 10,
                    backgroundColor: '#fff',
                    border: '1px solid #ccc',
                    borderRadius: 6,
                },
            });
        });

        return nodes;
    }

    // Generate flowchart edges
    function generateFlowEdges(detector: Detector): Edge[] {
        return detector.subassemblies.map((ds) => {
            const saID = typeof ds.subassembly === 'object' ? ds.subassembly.id : ds.subassembly;
            return {
                id: `edge-detector-sub-${saID}`,
                source: `detector-${detector.id}`,
                target: `sub-${saID}`,
                type: 'smoothstep',
            };
        });
    }

    // ------ Sub-Assembly Flowchart ------
    function generateSubAssemblyNodes(
        subAssembly: SubAssembly,
        invInfo: InventoryItems[]
    ): FlowNode[] {
        const nodes: FlowNode[] = [];

        const totalCost = getSubAssemblyCost(subAssembly, invInfo).toFixed(2);

        // Head node for sub-assembly
        nodes.push({
            id: `sub-${subAssembly.id}`,
            type: 'default',
            data: {
                label: (
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: "1rem", justifyContent: 'center' }}>

                        <img
                            src={subAssembly.subPicture ? subAssembly.subPicture : fnnBlueLogo}
                            alt={subAssembly.name}
                            className="part-image"
                            style={{
                                width: 35,
                                height: 35,
                                objectFit: 'cover',
                                marginRight: 8,
                                borderRadius: 4,
                            }}
                        // style={{ cursor: isEditing ? 'pointer' : 'default' }}
                        />

                        <div>
                            <div style={{ fontWeight: 'bold' }} >{subAssembly.name}</div>
                            <div>Total: ${totalCost}</div>
                        </div>
                    </div>
                )
            },
            position: { x: 300, y: 50 },
            style: {
                background: '#e6f7ff',
                border: '1px solid #91d5ff',
                borderRadius: 6,
                padding: 10,
            },
        });


        // Part nodes
        subAssembly.parts.forEach((p, index) => {
            const invPart = invInfo.find(i => i.partID === p.part);
            if (!invPart) return;

            const label = (
                <div style={{ display: 'flex', alignItems: 'center', fontSize: 12 }}>
                    <img
                        src={invPart.partPicture ? invPart.partPicture : fnnBlueLogo}
                        alt={invPart.partName}
                        className='part-image'
                        style={{
                            width: 24,
                            height: 24,
                            objectFit: 'cover',
                            marginRight: 8,
                            borderRadius: 4,
                        }}
                    />
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                        <div style={{ textAlign: 'left' }}>{invPart.mpn ? `${invPart.mpn} - ` : ""} {invPart.partName} x{p.quantity_required}</div>
                        <div> - ${(invPart.unitCost * p.quantity_required).toFixed(2)}</div>
                    </div>
                </div>
            );

            nodes.push({
                id: `part-${invPart.partID}`,
                type: 'default',
                data: { label },
                position: { x: 100 + index * 200, y: 200 },
                style: {
                    width: 180,
                    background: '#fff',
                    border: '1px solid #ccc',
                    borderRadius: 6,
                    padding: 8,
                },
            });
        });

        return nodes;
    }

    function generateSubAssemblyEdges(subAssembly: SubAssembly): Edge[] {
        return subAssembly.parts.map(p => {
            return {
                id: `edge-sub-part-${p.part}`,
                source: `sub-${subAssembly.id}`,
                target: `part-${p.part}`,
                type: 'smoothstep',
            };
        });
    }

    // Analytics Functions
    function getFullInventoryCost(invInfo: InventoryItems[]): number {
        return invInfo.reduce((total, item) => total + item.unitCost * item.currentAmount, 0);
    }

    function getFulfilledOrdersCostThisMonth(forecastInfo: InventoryForecast[]): number {
        const now = new Date();
        return forecastInfo.reduce((sum, order) => {
            if (
                order.status === "Arrived" &&
                order.arrivalDate &&
                new Date(order.arrivalDate).getMonth() === now.getMonth() &&
                new Date(order.arrivalDate).getFullYear() === now.getFullYear()
            ) {
                return sum + (order.cost * order.expectedAmount);
            }
            return sum;
        }, 0);
    }

    function getMostExpensiveItem(invInfo: InventoryItems[]): string {
        const max = invInfo.reduce((prev, curr) => (curr.unitCost > prev.unitCost ? curr : prev), invInfo[0]);
        return max?.partName || '';
    }

    function isSameMonthAndYear(dateString: string | Date, now: Date = new Date()): boolean {
        const date = new Date(dateString);
        return (
            date.getUTCFullYear() === now.getUTCFullYear() &&
            date.getUTCMonth() === now.getUTCMonth()
        );
    }

    function getItemsOrderedThisMonth(forecastInfo: InventoryForecast[]): number {
        const now = new Date();
        return forecastInfo.reduce((sum, order) => {
            if (
                order.orderedDate &&
                isSameMonthAndYear(order.orderedDate, now)
            ) {
                return sum + order.expectedAmount;
            }
            return sum;
        }, 0);
    }

    function getOrdersPlacedThisMonth(forecastInfo: InventoryForecast[]): number {
        const now = new Date();
        return forecastInfo.filter(order =>
            (order.status === "Ordered" || order.status === "Arrived") &&
            order.orderedDate &&
            isSameMonthAndYear(order.orderedDate, now)
        ).length;
    }

    function getFulfilledOrdersCountThisMonth(forecastInfo: InventoryForecast[]): number {
        const now = new Date();
        return forecastInfo.filter(order =>
            order.status === "Arrived" &&
            order.arrivalDate &&
            isSameMonthAndYear(order.arrivalDate, now)
        ).length;
    }

    function getMostOrderedItemByAmount(forecastInfo: InventoryForecast[], invInfo: InventoryItems[]): string {
        const now = new Date();
        const partTotals: Record<number, number> = {};

        forecastInfo.forEach(order => {
            if (
                order.orderedDate &&
                isSameMonthAndYear(order.orderedDate, now)
            ) {
                partTotals[order.part] = (partTotals[order.part] || 0) + order.expectedAmount;
            }
        });

        const mostOrderedID = Object.entries(partTotals).sort((a, b) => b[1] - a[1])[0]?.[0];
        return invInfo.find(item => item.partID === Number(mostOrderedID))?.partName || '';
    }

    function getMostOrderedItemByOrders(forecastInfo: InventoryForecast[], invInfo: InventoryItems[]): string {
        const now = new Date();
        const orderCounts: Record<number, number> = {};

        forecastInfo.forEach(order => {
            if (
                order.orderedDate &&
                isSameMonthAndYear(order.orderedDate, now)
            ) {
                orderCounts[order.part] = (orderCounts[order.part] || 0) + 1;
            }
        });

        const mostOrderedID = Object.entries(orderCounts).sort((a, b) => b[1] - a[1])[0]?.[0];
        return invInfo.find(item => item.partID === Number(mostOrderedID))?.partName || '';
    }

    // Getting the current dates for Analytics
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    const formatDate = (date: Date) =>
        `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;


    // Variable for generating pdf:
    const reportRef = useRef<HTMLDivElement>(null);
    const planningRef = useRef<HTMLDivElement>(null);

    // Reusable function
    const [isPrinting, setIsPrinting] = useState(false);

    const handlePrint = async (elementRef: React.RefObject<HTMLDivElement>, fileName: string) => {
        if (!elementRef.current) return;
        setIsPrinting(true);  // force desktop layout

        const element = elementRef.current;

        // Add print-mode class to force desktop layout
        element.classList.add("print-mode");

        // Save original styles
        const originalWidth = element.style.width;
        const originalHeight = element.style.height;
        const originalOverflow = element.style.overflow;

        try {
            element.style.width = "1700px";   // ensure desktop width
            element.style.height = "auto";
            element.style.overflow = "visible";

            await new Promise(resolve => setTimeout(resolve, 100));

            const canvas = await html2canvas(element, {
                scale: 2,
                scrollY: -window.scrollY,
                useCORS: true,
                windowWidth: 1024,   // important for consistent rendering
            });

            const imgData = canvas.toDataURL("image/png");

            const pdf = new jsPDF({
                orientation: "portrait",
                unit: "pt",
                format: "a4",
            });

            const pageWidth = pdf.internal.pageSize.getWidth();
            const imgWidth = pageWidth - 40; // margins
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            pdf.addImage(imgData, "PNG", 20, 20, imgWidth, imgHeight);
            pdf.save(`${fileName}.pdf`);
        } catch (error) {
            console.error("❌ Failed to generate PDF:", error);
        } finally {
            // restore original styles
            element.style.width = originalWidth;
            element.style.height = originalHeight;
            element.style.overflow = originalOverflow;

            element.classList.remove("print-mode");
            setIsPrinting(false); // restore mobile layout
        }
    };

    // Carousel logic for inv-card-stack

    const widgets = [
        {
            key: "arriving",
            component: (
                <div className='inventory-summary-section-info-widget'>
                    <div className='inventory-inner-widget-text'>
                        <p>Items Arriving Soon</p>
                        <p>
                            <strong>
                                {(() => {
                                    const now = new Date();
                                    const oneMonthFromNow = new Date();
                                    oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1);

                                    return forecastInfo.filter(f => {
                                        if (!f.expectedArrivalDate) return false;
                                        if (
                                            f.status === 'Arrived' ||
                                            f.status === 'Cancelled' ||
                                            f.status === 'Returned'
                                        ) return false;

                                        const arrival = new Date(f.expectedArrivalDate);
                                        return arrival >= now && arrival <= oneMonthFromNow;
                                    }).length;
                                })()}
                            </strong>
                            <FaTruckFast className='inv-inner-widget' />
                        </p>
                    </div>
                    <FaTruckFast className='inv-outter-widget' />
                </div>
            )
        },

        {
            key: "pending",
            component: (
                <div className='inventory-summary-section-info-widget'>
                    <div className='inventory-inner-widget-text'>
                        <p>Items Pending Count</p>
                        <p>
                            <strong>
                                {invInfo.filter(item =>
                                    !getColorTooltip(item.lastCounted).startsWith('Normal')
                                ).length}
                            </strong>
                            <PiClockCountdownFill className='inv-inner-widget' />
                        </p>
                    </div>
                    <PiClockCountdownFill className='inv-outter-widget' />
                </div>
            )
        },

        {
            key: "subassemblies",
            component: (
                <div className='inventory-summary-section-info-widget'>
                    <div className='inventory-inner-widget-text'>
                        <p>Ready Sub-Assemblies</p>
                        <p>
                            <strong>
                                {builtSubAssemblies.filter(bsa => bsa.status === 'available').length}
                            </strong>
                            <PiPuzzlePieceFill className='inv-inner-widget' />
                        </p>
                    </div>
                    <PiPuzzlePieceFill className='inv-outter-widget' />
                </div>
            )
        },

        {
            key: "detectors",
            component: (
                <div className='inventory-summary-section-info-widget'>
                    <div className='inventory-inner-widget-text'>
                        <p>Detectors in Stock</p>
                        <p>
                            <strong>
                                {builtDetectors.filter(det => det.status === 'in-stock').length}
                            </strong>
                            <FaWarehouse className='inv-inner-widget' />
                        </p>
                    </div>
                    <FaWarehouse className='inv-outter-widget' />
                </div>
            )
        },

        {
            key: "buildable",
            component: (
                <div className='inventory-summary-section-info-widget'>
                    <div className='inventory-inner-widget-text'>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', flexWrap: 'wrap' }}>
                            <p>Buildable Detectors</p>

                            <Tooltip
                                showTooltip={showVersionTooltip}
                                onClose={() => setShowVersionTooltip(false)}
                                extraClassName="inv-version-bottom"
                                message={
                                    <div className="version-selector-tooltip">
                                        {detectors.map(det => (
                                            <div
                                                key={det.id}
                                                className={`version-option ${buildableDetectorId === det.id ? "active" : ""}`}
                                                onClick={() => {
                                                    setBuildableDetectorId(det.id);
                                                    setShowVersionTooltip(false);
                                                }}
                                            >
                                                v{det.version}
                                            </div>
                                        ))}
                                    </div>
                                }
                            >
                                <button
                                    className='det-version-button'
                                    onClick={() => setShowVersionTooltip(prev => !prev)}
                                >
                                    <strong>v</strong>
                                </button>
                            </Tooltip>
                        </div>

                        <p>
                            <strong>{buildableDetectorCount}</strong>
                            <IoHammerSharp className='inv-inner-widget' />
                        </p>
                    </div>
                    <IoHammerSharp className='inv-outter-widget' />
                </div>
            )
        }
    ];

    const [widgetIndex, setWidgetIndex] = useState(0);
    const [isWidgetCarousel, setIsWidgetCarousel] = useState(window.innerWidth <= 650);
    const [visibleWidgets, setVisibleWidgets] = useState(1);

    useEffect(() => {
        const handleResize = () => {
            const width = window.innerWidth;

            setIsWidgetCarousel(width <= 650);

            if (width <= 550) setVisibleWidgets(1);
            else if (width <= 650) setVisibleWidgets(2);
            else setVisibleWidgets(5);
        };

        window.addEventListener("resize", handleResize);
        handleResize();

        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const handleWidgetNext = () => {
        if (isWidgetCarousel) {
            setWidgetIndex((prev) => (prev + 1) % widgets.length);
        }
    };

    const handleWidgetPrev = () => {
        if (isWidgetCarousel) {
            setWidgetIndex((prev) => (prev - 1 + widgets.length) % widgets.length);
        }
    };

    const getVisibleWidgets = () => {
        if (!isWidgetCarousel) return widgets;

        const visible = [];
        for (let i = 0; i < visibleWidgets; i++) {
            visible.push(widgets[(widgetIndex + i) % widgets.length]);
        }
        return visible;
    };


    const tabs = [
        { key: "overview", label: "Overview" },
        { key: "items", label: "Item List" },
        { key: "orders", label: "Order Management" },
        { key: "structure", label: "Version Management" },
        { key: "build", label: "Build & Assemble" },
        { key: "units", label: "Built Units" },
    ];

    const [isMobileSmall, setIsMobileSmall] = useState(window.innerWidth < 800);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 840);
    const [isMobile2, setIsMobile2] = useState(window.innerWidth < 1060);
    const [isMobile4, setIsMobile4] = useState(window.innerWidth < 1050)
    const [expandedForecastId, setExpandedForecastId] = useState<number | null>(null);
    const [expandedStockId, setExpandedStockId] = useState<number | null>(null);
    const [expandedPendingId, setExpandedPendingId] = useState<number | null>(null);
    const [expandedPartsId, setExpandedPartsId] = useState<number | null>(null);

    useEffect(() => {
        const handleResize = () => {
            setIsMobileSmall(window.innerWidth < 800);
            setIsMobile(window.innerWidth < 840);
            setIsMobile2(window.innerWidth < 1060);
            setIsMobile4(window.innerWidth < 1050);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);


    const truncateText = (text: string, maxLength: number) =>
        text.length > maxLength ? text.slice(0, maxLength) + "..." : text;

    // Renders part name, with hover tooltip when text is truncated
    function renderPartName(item: any, showInfo = true) {
        const tooltipText = `
        ${item.partName}
        ${item.description ? "\n\n" + item.description : ""}
    `.trim();

        const nameContent = (
            <span className="tooltip-wrapper">
                {item.partLink ? (
                    <a
                        href={item.partLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="clickable-inv-option"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {truncateText(((item.mpn ? (item.mpn + " - ") : "") + item.partName), 40)}
                    </a>
                ) : (
                    <span className="unclickable-inv-option">
                        {truncateText(((item.mpn ? (item.mpn + " - ") : "") + item.partName), 40)}
                    </span>
                )}
                <div className="custom-tooltip part-name-tooltip">
                    <strong>{item.partName}</strong>
                    {item.description && (
                        <>
                            <br />
                            <span>{item.description}</span>
                        </>
                    )}
                </div>
            </span>
        );

        return (
            <div className="text-with-icon">
                {nameContent}
                {showInfo && showInfoWithColor(item.lastCounted)}
            </div>
        );
    }

    function renderUnitAmountWithPackInfo(forecast: InventoryForecast) {
        const numberOfPackets = forecast.number_of_packets ?? 0;
        const amountPerPacket = forecast.amount_per_packet ?? 0;
        const hasPack = !!forecast.is_packet && numberOfPackets > 0 && amountPerPacket > 0;

        return (
            <div>
                <span className="tooltip-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                    {forecast.expectedAmount}
                    {hasPack && (
                        <>
                            <TbPackages className="pack-icon" />
                            <div className="custom-tooltip pack-tooltip">
                                <strong>Item comes in a pack:</strong>
                                <br />
                                - Number of packs: {numberOfPackets}
                                <br />
                                - Items per pack: {amountPerPacket}
                                <br />
                                - Total amount: {forecast.expectedAmount}
                            </div>
                        </>
                    )}
                </span>
            </div>
        );
    }

    function renderPartNameWithPriority(forecast: InventoryForecast) {
        const matchingPart = invInfo.find(p => p.partID === forecast.part);

        if (!matchingPart) return <span>Unknown Part</span>;

        // Use first link from comma-separated sourceLinks
        const firstLink = forecast.sourceLinks?.split(",")[0]?.trim() || matchingPart.partLink || "";

        // Determine priority color
        const priority = forecast.priority || "Low";
        let priorityColor = "";
        if (priority === "Medium") priorityColor = "orange";
        else if (priority === "High") priorityColor = "red";

        // Part name with tooltip
        const nameContent = (
            <span className="tooltip-wrapper">
                {firstLink ? (
                    <a
                        href={firstLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="clickable-inv-option"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {truncateText(((matchingPart.mpn ? (matchingPart.mpn + " - ") : "") + matchingPart.partName), 40)}
                    </a>
                ) : (
                    <span className="unclickable-inv-option">
                        {truncateText(((matchingPart.mpn ? (matchingPart.mpn + " - ") : "") + matchingPart.partName), 40)}
                    </span>
                )}
                <div className="custom-tooltip part-name-tooltip">
                    <strong>{matchingPart.partName}</strong>
                    {matchingPart.description && (
                        <>
                            <br />
                            <span>{matchingPart.description}</span>
                        </>
                    )}
                </div>
            </span>
        );

        // Priority icon with tooltip
        const priorityIcon = priority !== "Low" && (
            <span className="tooltip-wrapper" style={{ marginLeft: "0.3rem" }}>
                <HiBellAlert color={priorityColor} />
                <div className="custom-tooltip pack-tooltip2">
                    <strong>Priority:</strong> {priority}
                    {forecast.lead_time && (
                        <>
                            <br />
                            <strong>Lead time:</strong> {forecast.lead_time}
                        </>
                    )}
                </div>
            </span>
        );

        return (
            <div className="text-with-icon">
                {nameContent}
                {priorityIcon}
            </div>
        );
    }

    // ------------------------------------ New functions to determine color and tooltip of the amount based on the set detector number ------------------------------------

    const [desiredPartsNeeded, setDesiredPartsNeeded] = useState<Record<number, number>>({});
    const [desiredDetectors, setDesiredDetectors] = useState<number | null>(null);
    const [stockAmountId, setStockAmountId] = useState<number | null>(null);

    useEffect(() => {
        const fetchStockNumber = async () => {
            try {
                const response = await fetch(apiUrl("/api/inventory_stock_amount/"), {
                    credentials: credentials()
                });
                const data = await response.json();
                if (response.ok && data.length > 0) {
                    setDesiredDetectors(data[0].stockAmount ?? null);
                    setStockAmountId(data[0].id); // store the id for later PUT
                } else {
                    console.error("Failed to load stock number:", data.error || response.statusText);
                }
            } catch (error) {
                console.error("❌ Error fetching stock number:", error);
            }
        };

        fetchStockNumber();
    }, []);

    useEffect(() => {
        if (!detectors.length || !subAssemblies.length) return;

        const latestDetector = getLatestDetectorVersion(detectors);
        if (!latestDetector) return;

        const partsMap: Record<number, number> = {};

        latestDetector.subassemblies.forEach(ds => {
            const subassembly = typeof ds.subassembly === "number"
                ? subAssemblies.find(s => s.id === ds.subassembly)
                : ds.subassembly;

            if (!subassembly) return;

            subassembly.parts.forEach(p => {
                const total = (p.quantity_required || 0) * ds.quantity_required * (desiredDetectors ?? 0);
                partsMap[p.part] = (partsMap[p.part] || 0) + total;
            });
        });

        setDesiredPartsNeeded(partsMap);
    }, [detectors, subAssemblies, desiredDetectors]);

    // New function for stock status
    function getStockStatus(
        item: InventoryItems,
        desiredPartsNeeded: Record<number, number>
    ): { color: string; status: string; tooltip: string } {
        const requiredFromDetector = desiredPartsNeeded[item.partID];
        const stockTarget = item.stock_target ?? 0; // ✅ New variable
        const current = item.currentAmount ?? 0;
        const unitCost = item.unitCost ?? 0;

        let required: number | null = null;
        let source: "stock" | "detector" | "none" = "none";

        // 1️⃣ Priority: use stock_target if available and > 0
        if (stockTarget > 0) {
            required = stockTarget;
            source = "stock";
        }
        // 2️⃣ Otherwise, use detector requirement if exists
        else if (requiredFromDetector) {
            required = requiredFromDetector;
            source = "detector";
        }
        // 3️⃣ Otherwise, no known requirement
        else {
            return {
                color: "black",
                status: "N/A",
                tooltip: "Item is neither from a detector build nor has its stock target determined."
            };
        }

        // ✅ Now calculate ratios
        const difference = current - required;
        const ratio = current / required;
        const percent = Math.round(ratio * 100);

        // 🔴 Critical (≤50%)
        if (ratio < 0.5) {
            return {
                color: "red",
                status: `Critical - ${percent}%`,
                tooltip:
                    `Critical (${percent}% of Stock) — Needs ${Math.abs(difference)} more to meet the ${required} required by the set ${source === "stock" ? "stock target" : "detector stock"}.`
            };
        }

        // 🟠 Low (>50% but <100%)
        if (ratio < 1) {
            return {
                color: "orange",
                status: `Low - ${percent}%`,
                tooltip:
                    `Low (${percent}% of Stock) — Needs ${Math.abs(difference)} more to meet the ${required} required by the set ${source === "stock" ? "stock target" : "detector stock"}.`
            };
        }

        // 🟢 Adequate (≤120%)
        if (ratio <= 1.2) {
            return {
                color: "green",
                status: `Adequate - ${percent}%`,
                tooltip:
                    `Adequate (${percent}% of Stock) — ${current} available out of ${required} required by the set ${source === "stock" ? "stock target" : "detector stock"}.`
            };
        }

        // 🟣 Excess (>120%)
        const extraCost = difference * unitCost;
        const formattedCost = extraCost.toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 2,
        });

        return {
            color: "purple",
            status: `Excess - ${percent}%`,
            tooltip:
                `Excess (${percent}% of Stock) — ${difference} more than the ${required} required by the set ${source === "stock" ? "stock target" : "detector stock"}. Estimated ${formattedCost} in idle inventory.`
        };
    }

    // Modifying the stock number
    const [newStockAmount, setNewStockAmount] = useState(desiredDetectors ?? 0);
    const [showSetStockPopup, setShowSetStockPopup] = useState(false);

    const handleStockNumber = async () => {
        if (!stockAmountId) return; // no record to update

        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        try {
            const response = await fetch(apiUrl(`/api/inventory_stock_amount/${stockAmountId}/`), {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": csrfToken,
                },
                credentials: credentials(),
                body: JSON.stringify({ stockAmount: newStockAmount }),
            });

            if (response.ok) {
                setDesiredDetectors(newStockAmount);
            } else {
                console.error("❌ Failed to update stock amount:", await response.text());
            }
        } catch (error) {
            console.error("❌ Error updating stock amount:", error);
        }
    };

    // ------------------------------------ New functions to determine color and tooltip of the amount based on the set detector number ------------------------------------

    // Functions for assemble popup
    const [assembleDetectorId, setAssembleDetectorId] = useState<number | null>(null);
    const [assembleSelectedSubAssemblies, setAssembleSelectedSubAssemblies] = useState<Record<number, string>>({});
    const [assembleMacAddress, setAssembleMacAddress] = useState<string>("");
    const [assembleDetCode, setAssembleDetCode] = useState<string>("");
    const [editCodes, setEditCodes] = useState<Record<string, string>>({});
    const [editMacs, setEditMacs] = useState<Record<string, string>>({});
    const [batches, setBatches] = useState<Batch[]>([]);
    const [selectedBatchProgress, setSelectedBatchProgress] = useState<BatchProgress | null>(null);
    const [batchOrderProgress, setBatchOrderProgress] = useState<BatchProgress | null>(null);
    const [batchOrderSelectedId, setBatchOrderSelectedId] = useState<number | null>(null);
    const [openBatchSlips, setOpenBatchSlips] = useState<Record<string, boolean>>({});

    const [showVersionChangePopup, setShowVersionChangePopup] = useState(false);
    const [pendingVersionChange, setPendingVersionChange] = useState<any>(null);
    const [replacementSelections, setReplacementSelections] = useState<Record<number, string>>({});

    // Replace assembleDetectorId, assembleMacAddress, assembleDetCode, assembleSelectedSubAssemblies with:
    const [assembleSharedDetectorId, setAssembleSharedDetectorId] = useState<number | null>(null);
    const [assembleDetectorRequests, setAssembleDetectorRequests] = useState<AssembleDetectorRequest[]>([{
        id: Date.now(),
        macAddress: "",
        detCode: "",
        selectedSubAssemblies: {}
    }]);

    // New interface
    interface AssembleDetectorRequest {
        id: number;
        macAddress: string;
        detCode: string;
        selectedSubAssemblies: Record<number, string>;
    }


    // For Assemble Detector popup
    const [assembleSelectedBatch, setAssembleSelectedBatch] = useState<number | null>(null);

    // Add to your existing fetch calls
    const fetchBatches = async () => {
        try {
            const response = await fetch(apiUrl('/api/inventory_batches/'), {
                credentials: credentials()
            });

            const data = await response.json();

            if (response.ok) {
                setBatches(data);
            } else {
                console.error("Failed to load batch information:", data.error || response.statusText);
            }
        } catch (error) {
            console.error("❌ Error fetching batch info:", error);
        }
    };

    const fetchBatchProgress = async (batchId: number) => {
        const response = await fetch(apiUrl(
            `/api/inventory_batches/${batchId}/progress/`),
            { credentials: credentials() }
        );
        if (response.ok) {
            const data = await response.json();
            setSelectedBatchProgress(data);
            setBatchOrderProgress(data);
        } else {
            setSelectedBatchProgress(null);
            setBatchOrderProgress(null);
        }
    };

    useEffect(() => {
        fetchBatches();
    }, []);

    // Check if there are any available batches
    const hasAvailableBatches = batches.some(
        b => b.status !== 'completed' && b.status !== 'cancelled'
    );

    // Function for handling the placement of order requests per batch
    const handleBatchForecastRequest = async (batchProgress: BatchProgress) => {
        if (!batchProgress) return;

        const target = batchProgress.detectors.target;
        const bomPerDetector = batchProgress.subassemblies.bom_per_detector;

        const partTotals: Record<number, number> = {};

        for (const [subName] of Object.entries(bomPerDetector)) {
            const subAssemblyObj = subAssemblies.find(sa => sa.name === subName);
            if (!subAssemblyObj) continue;

            for (const partReq of subAssemblyObj.parts) {
                const part = invInfo.find(p => p.partID === partReq.part);

                const rawNeeded = partReq.quantity_required * target;
                const available = part?.currentAmount || 0;

                const totalNeeded = considerStock
                    ? Math.max(rawNeeded - available, 0)
                    : rawNeeded;

                partTotals[partReq.part] =
                    (partTotals[partReq.part] || 0) + totalNeeded;
            }
        }

        // Send requests once per part
        for (const [partID, qty] of Object.entries(partTotals)) {
            if (qty > 0) {
                await handlePartForecastRequest(Number(partID), qty, true, batchProgress.batchID); // silent
            }
        }

        toast.success("Batch order requests placed successfully!", toastStyle);
        setShowBatchOrderPopup(false);
    };

    const handleSubAssemblyForecastRequest = async () => {
        const partTotals: Record<number, number> = {};

        for (const req of subAssemblyOrderRequests) {
            const sub = subAssemblies.find(sa => sa.id === req.subassemblyId);
            if (!sub) continue;

            for (const partReq of sub.parts) {
                const totalNeeded = partReq.quantity_required * req.quantity;

                partTotals[partReq.part] =
                    (partTotals[partReq.part] || 0) + totalNeeded;
            }
        }

        // Send requests once per part
        for (const [partID, qty] of Object.entries(partTotals)) {
            if (qty > 0) {
                await handlePartForecastRequest(Number(partID), qty, true);
            }
        }

        toast.success("Sub-assembly order requests placed successfully!", toastStyle);
        setShowSubAssemblyOrderPopup(false);
        resetSubAssemblyOrderState();
    };


    const assembleDetector = async (
        detectorId: number | null,
        selectedSubAssemblies: Record<number, string>,
        macAddress: string,
        code?: string,
        batchId?: number | null,
    ) => {
        if (!detectorId || !macAddress.trim()) {
            toast.warn("Please fill in all required fields.", {
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                style: {
                    backgroundColor: 'white',
                    color: 'black',
                    fontSize: '16px',
                },
            });
            return;
        }

        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        const payload = {
            mac_address: macAddress,
            subassemblies: Object.values(selectedSubAssemblies),
            assembled_by: loggedFnnUserId,
            detector: detectorId,
            code: (code ? code : null),
            belongs_to_batch: batchId ?? null,
        };

        try {
            const response = await fetch(apiUrl("/api/inventory_built_dets/"), {
                method: "POST",
                credentials: credentials(),
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": csrfToken,
                },
                body: JSON.stringify(payload),
            });

            if (response.ok) {
                // Refresh data
                fetchBuiltSubAssemblies();
                fetchbuiltDetectors();
                fetchBatches();
                if (batchId != null) {
                    fetchBatchProgress(batchId);
                }
                setShowSectionPopup(false);
                setAssembleDetectorId(null);
                setAssembleSelectedSubAssemblies({});
                setAssembleMacAddress("");
                setAssembleDetCode("");
                setAssembleSelectedBatch(null);

                setAssembleDetectorRequests([{
                    id: Date.now(),
                    macAddress: "",
                    detCode: "",
                    selectedSubAssemblies: {}
                }]);
                setAssembleSharedDetectorId(null);


                toast.success("Detector assembled successfully!", {
                    autoClose: 3000,
                    style: { backgroundColor: 'white', color: 'black', fontSize: '16px' },
                });
            } else {
                const errorData = await response.json();
                console.error(errorData);

                // Check for specific MAC address conflict
                if (errorData.mac_address && Array.isArray(errorData.mac_address)) {
                    toast.error(errorData.mac_address[0], {
                        autoClose: 4000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        style: {
                            backgroundColor: 'white',
                            color: 'black',
                            fontSize: '16px',
                        },
                    });
                } else {
                    toast.error("Failed to assemble detector. Please try again.", {
                        autoClose: 4000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        style: {
                            backgroundColor: 'white',
                            color: 'black',
                            fontSize: '16px',
                        },
                    });
                }
            }
        } catch (error) {
            console.error(error);
            toast.error("An unexpected error occurred.", {
                autoClose: 4000,
                style: { backgroundColor: 'white', color: 'black', fontSize: '16px' },
            });
        }
    };

    // Updating the detector's code
    const handleCodeUpdate = async (det: any) => {
        const newCode = editCodes[det.mac_address]?.trim() || null;

        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        const payload = {
            mac_address: det.mac_address,
            subassemblies: det.subassemblies || [],
            assembled_by: det.assembled_by,
            detector: det.detector,
            code: newCode,
            status: det.status,
        };

        try {
            const response = await fetch(apiUrl(`/api/inventory_built_dets/${det.mac_address}/`), {
                method: "PUT",
                credentials: credentials(),
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": csrfToken,
                },
                body: JSON.stringify(payload),
            });

            if (response.ok) {
                toast.success("Detector code updated succesfully!", toastStyle);
                fetchbuiltDetectors(); // refresh list
            } else {
                const errorData = await response.json();
                console.error(errorData);
                toast.error("Failed to update detector code.", toastStyle);
            }
        } catch (error) {
            console.error(error);
            toast.error("An unexpected error occurred.", toastStyle);
        }
    };

    // Updating the detector's MAC
    const handleMacUpdate = async (det: any) => {
        const newMac = editMacs[det.mac_address]?.trim();
        if (!newMac || newMac === det.mac_address) return;

        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        try {
            // Step 1: Update current detector to new MAC (PUT)
            const updateResponse = await fetch(apiUrl(`/api/inventory_built_dets/${det.mac_address}/`), {
                method: "PUT",
                credentials: credentials(),
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": csrfToken,
                },
                body: JSON.stringify({
                    ...det,
                    mac_address: newMac,
                }),
            });

            if (!updateResponse.ok) {
                const errorData = await updateResponse.json();
                console.error(errorData);
                toast.error("Failed to update MAC.", toastStyle);
                return;
            }

            // Step 2: Delete old MAC entry (only if backend creates a new record on PUT)
            const deleteResponse = await fetch(apiUrl(`/api/inventory_built_dets/${det.mac_address}/`), {
                method: "DELETE",
                credentials: credentials(),
                headers: {
                    "X-CSRFToken": csrfToken,
                },
            });

            if (!deleteResponse.ok) {
                toast.warn("MAC updated but old record could not be deleted.", toastStyle);
            }

            // Step 3: Re-link sub-assemblies to new MAC (important!)
            const subassemblyIds: string[] = det.subassemblies || [];

            const subUpdateResults = await Promise.all(
                subassemblyIds.map(async (subId) => {
                    const subObj = builtSubAssemblies.find(bsa => bsa.id === subId);
                    if (!subObj) return true;

                    const subResp = await fetch(apiUrl(`/api/inventory_built_subs/${subId}/`), {
                        method: "PUT",
                        credentials: credentials(),
                        headers: {
                            "Content-Type": "application/json",
                            "X-CSRFToken": csrfToken,
                        },
                        body: JSON.stringify({
                            ...subObj,
                            used_by: newMac,
                            status: "installed"
                        }),
                    });

                    return subResp.ok;
                })
            );

            if (subUpdateResults.some(ok => !ok)) {
                toast.error("Some sub-assemblies could not be re-linked.", toastStyle);
                return;
            }

            toast.success("MAC address updated successfully!", toastStyle);
            fetchbuiltDetectors();
            fetchBuiltSubAssemblies();

        } catch (error) {
            console.error(error);
            toast.error("An unexpected error occurred.", toastStyle);
        }
    };

    // Updating the detector's version
    const handleDetectorVersionChange = async (
        det: any,
        newDetectorId: number | null
    ) => {
        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        const payload = {
            ...det,
            detector: newDetectorId,
        };

        try {
            const response = await fetch(apiUrl(
                `/api/inventory_built_dets/${det.mac_address}/`),
                {
                    method: "PUT",
                    credentials: credentials(),
                    headers: {
                        "Content-Type": "application/json",
                        "X-CSRFToken": csrfToken,
                    },
                    body: JSON.stringify(payload),
                }
            );

            if (response.ok) {
                toast.success("Detector version updated!", toastStyle);
                fetchbuiltDetectors();
                fetchBuiltSubAssemblies();
            } else {
                const err = await response.json();
                console.error(err);
                toast.error("Failed to update version.", toastStyle);
            }
        } catch (error) {
            console.error(error);
            toast.error("Unexpected error.", toastStyle);
        }
    };

    const analyzeVersionChange = (det: any, newDetectorId: number) => {
        const oldSubs = builtSubAssemblies.filter(
            bsa => bsa.used_by === det.mac_address
        );

        const newDetector = detectors.find(d => d.id === newDetectorId);
        if (!newDetector) return null;

        const allowedNew = new Set(
            newDetector.subassemblies.map(s => s.subassembly)
        );

        const oldSubIds: number[] = oldSubs.map(s => Number(s.subassembly));

        // ❌ incompatible (will be removed)
        const incompatible = oldSubs.filter(
            s => !allowedNew.has(s.subassembly)
        );

        // ➕ required but missing
        const missing: number[] = newDetector.subassemblies
            .map((s: any) => Number(s.subassembly)) // force number
            .filter((subId: number) => !oldSubIds.includes(subId));

        return {
            incompatible,
            missing,
            newDetector
        };
    };


    // Functions for managing built units
    const [selectedUnitStatus, setSelectedUnitStatus] = useState<string | null>("available");
    const [selectedDetectorStatus, setSelectedDetectorStatus] = useState<string>("in-stock");
    const [showSelectDetectorPopup, setShowSelectDetectorPopup] = useState(false);
    const [currentSubAssemblyId, setCurrentSubAssemblyId] = useState<string | null>(null);

    // Confirmation popup variables
    const [showStatusConfirmPopup, setShowStatusConfirmPopup] = useState(false);
    const [statusToConfirm, setStatusToConfirm] = useState<string | null>(null);
    const [subAssemblyToConfirmId, setSubAssemblyToConfirmId] = useState<string | null>(null);

    const handleUnitStatusChange = async (
        id: string,
        newStatus: string,
        usedByMac?: string | null
    ) => {
        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        try {
            // If marking as "installed" and no detector provided, open the popup
            if (newStatus === "installed" && !usedByMac) {
                setCurrentSubAssemblyId(id);
                setShowSelectDetectorPopup(true);
                return;
            }

            const targetSub = builtSubAssemblies.find(bsa => bsa.id === id);
            if (!targetSub) return toast.error("Sub-assembly not found", { autoClose: 4000 });

            // If "installed", update detector
            if (newStatus === "installed" && usedByMac) {
                const targetDetector = builtDetectors.find(det => det.mac_address === usedByMac);
                if (!targetDetector) return toast.error("Invalid detector selected", { autoClose: 4000 });

                // Remove any previous subassembly of the same type
                const updatedSubIds = targetDetector.subassemblies.filter(subId => {
                    const subObj = builtSubAssemblies.find(bsa => bsa.id === subId);
                    return subObj?.subassembly !== targetSub!.subassembly;
                });
                updatedSubIds.push(targetSub.id);

                const detectorPayload = {
                    ...targetDetector,
                    subassemblies: updatedSubIds,
                };

                const detResp = await fetch(apiUrl(
                    `/api/inventory_built_dets/${usedByMac}/`),
                    {
                        method: "PUT",
                        headers: {
                            "Content-Type": "application/json",
                            "X-CSRFToken": csrfToken,
                        },
                        credentials: credentials(),
                        body: JSON.stringify(detectorPayload),
                    }
                );

                if (!detResp.ok) return toast.error("❌ Failed to link sub-assembly to detector", { autoClose: 4000 });
            }

            // PUT subassembly update
            const subResp = await fetch(apiUrl(
                `/api/inventory_built_subs/${id}/`),
                {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                        "X-CSRFToken": csrfToken,
                    },
                    credentials: credentials(),
                    body: JSON.stringify({
                        ...targetSub,
                        status: newStatus,
                        used_by: usedByMac || null,
                    }),
                }
            );

            if (!subResp.ok) return toast.error("❌ Failed to update sub-assembly", { autoClose: 4000 });

            await Promise.all([fetchBuiltSubAssemblies(), fetchbuiltDetectors()]);
            toast.success("Status updated successfully", { autoClose: 3000 });
        } catch (error) {
            console.error("❌ Error updating status:", error);
            toast.error("❌ Error updating status", { autoClose: 4000 });
        }
    };

    // Add this at the top of your component
    const [openSlips, setOpenSlips] = useState<{ [key: string]: boolean }>({});

    // Toggle slip open/close
    const toggleSlip = (id: string | number) => {
        const key = String(id); // ✅ ensure it's a string
        setOpenSlips(prev => ({ ...prev, [key]: !prev[key] }));
    };

    // Function to change status of assembled detector
    const handleDetectorStatusChange = async (macAddress: string, newStatus: string) => {
        const det = builtDetectors.find(d => d.mac_address === macAddress);
        if (!det) {
            console.error("Detector not found for MAC:", macAddress);
            return;
        }

        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        try {
            const bodyData = {
                mac_address: det.mac_address,
                built_at: det.built_at,
                assembled_by: det.assembled_by,
                detector: det.detector,
                subassemblies: det.subassemblies,
                status: newStatus,
            };

            const response = await fetch(apiUrl(`/api/inventory_built_dets/${macAddress}/`), {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": csrfToken,
                },
                credentials: credentials(), // ✅ ensure session cookie is sent
                body: JSON.stringify(bodyData),
            });

            if (!response.ok) {
                const errText = await response.text();
                console.error("PUT failed:", errText);
                throw new Error("Failed to update detector status");
            }

            // ✅ Update local state
            setBuiltDetectors(prev =>
                prev.map(det =>
                    det.mac_address === macAddress ? { ...det, status: newStatus } : det
                )
            );

            toast.success("Detector status updated!.", toastStyle);

        } catch (error) {
            console.error("Error updating detector status:", error);
            toast.error("Failed to update detector status.", toastStyle);
        }
    };

    const [expandedSlips, setExpandedSlips] = useState<Record<number, boolean>>({});
    const [editedValuesMap, setEditedValuesMap] = useState<Record<number, Partial<InventoryItems>>>({});

    const stopClick = (e: React.MouseEvent) => e.stopPropagation();

    const allowedSubNames = selectedBatchProgress
        ? new Set(Object.keys(selectedBatchProgress.subassemblies.bom_per_detector))
        : null;

    const filteredSubAssembliesBuilds = allowedSubNames
        ? subAssemblies.filter(sa => allowedSubNames.has(sa.name))
        : subAssemblies;


    /* NEW CODE FOR NEW DESIGN */
    const [overviewViewMode, setOverviewViewMode] = useState<'dashboard' | 'table'>('dashboard')
    const [activeInvTab, setActiveInvTab] = useState("overview");
    const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 1600);
    const donutTotalFontSize = isSmallScreen ? '12px' : '20px';
    const valueSpacing = isSmallScreen ? -20 : -25;
    const nameSpacing = isSmallScreen ? 18 : 20;

    // Handling legend positioning
    const [legendPosition, setLegendPosition] = useState<'right' | 'bottom'>(
        window.innerWidth <= 500 ? 'bottom' : 'right'
    );

    useEffect(() => {
        const handleResize = () => {
            setLegendPosition(window.innerWidth <= 500 ? 'bottom' : 'right');
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);


    useEffect(() => {
        const handleResize = () => setIsSmallScreen(window.innerWidth < 1600);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    /* Sub-Assembly Chart */
    const subAssemblyTotal = subAssemblySeries.reduce((sum, val) => sum + val, 0);

    const subAssemblyChartOptions: ApexOptions = {
        chart: { type: 'donut' },

        labels: subAssemblyLabels,

        colors: [
            '#1e6296ff',
            '#16517B',
            '#28AEE1',
            '#0e2943ff',
            '#3A7CA5',
            '#6096C5',
            '#9EC9E2'
        ],

        legend: {
            position: legendPosition,
            width: legendPosition === 'right' ? 160 : undefined,
            formatter: (seriesName: string) => {
                return seriesName.length > 15
                    ? seriesName.slice(0, 15) + '...'
                    : seriesName;
            },
        },

        plotOptions: {
            pie: {
                donut: {
                    size: '75%',
                    labels: {
                        show: true,

                        total: {
                            show: true,
                            label: 'Total',
                            fontSize: donutTotalFontSize,
                            fontWeight: 600,
                            color: '#373d3f',
                            formatter: () => subAssemblyTotal.toString(),
                        },

                        name: {
                            show: true,
                            offsetY: nameSpacing,
                            formatter: (val: string) => val.split(' ')[0],
                        },

                        value: {
                            show: true,
                            fontSize: donutTotalFontSize,
                            color: '#373d3f',
                            offsetY: valueSpacing,
                        },
                    },
                },
            },
        },

        dataLabels: { enabled: false },

        tooltip: {
            y: {
                formatter: (val: number) => {
                    const percent = subAssemblyTotal
                        ? ((val / subAssemblyTotal) * 100).toFixed(1)
                        : 0;
                    return `${val} item(s) (${percent}%)`;
                },
            },
        },
    };

    /* Build Detectors chart logic */
    // Compute detectors built per month
    const buildCountsPerMonth = useMemo(() => {
        const today = new Date();
        // last 12 months, oldest first (11 months ago) → current month last
        const monthsArr = Array.from({ length: 12 }, (_, i) => {
            const d = new Date(today.getFullYear(), today.getMonth() - 11 + i, 1);
            return { year: d.getFullYear(), month: d.getMonth() }; // month: 0-11
        });

        const counts = monthsArr.map(() => 0);

        builtDetectors.forEach(det => {
            const date = new Date(det.built_at);
            const index = monthsArr.findIndex(m => m.year === date.getFullYear() && m.month === date.getMonth());
            if (index !== -1) counts[index]++;
        });

        const monthsLabels = monthsArr.map(m => {
            const d = new Date(m.year, m.month, 1);
            return d.toLocaleString('default', { month: 'short', year: '2-digit' });
        });

        return { months: monthsLabels, counts };
    }, [builtDetectors]);


    // Logic for pending count table
    const getPriority = (item: InventoryItems) => {
        const color = getColorTooltip(item.lastCounted);

        if (color.startsWith("Red")) return 1;      // Highest priority
        if (color.startsWith("Orange")) return 2;
        if (color.startsWith("Gray")) return 3;     // Lowest priority
        return 4;
    };

    const sortedCountData = [...invInfo]
        .filter(item => {
            const c = getColorTooltip(item.lastCounted);
            return c.startsWith("Red") || c.startsWith("Orange") || c.startsWith("Gray");
        })
        .sort((a, b) => getPriority(a) - getPriority(b));


    // Filtering per Status for the stock availability table
    const filteredStockData = invInfo  // ← all inventory items
        .map(item => ({
            item,
            stock: getStockStatus(item, desiredPartsNeeded)
        }))
        .filter(({ stock }) =>
            stock.status.startsWith("Critical") ||
            stock.status.startsWith("Low")
        );

    type StockPriority = "Critical" | "Low";

    const priorityOrder: Record<StockPriority, number> = {
        Critical: 0,
        Low: 1
    };

    const sortedStockData = filteredStockData.sort((a, b) => {
        const aKey = a.stock.status.split(" ")[0] as StockPriority;
        const bKey = b.stock.status.split(" ")[0] as StockPriority;

        return priorityOrder[aKey] - priorityOrder[bKey];
    });

    // Checking if scrollbar exists
    const [hasScrollbar, setHasScrollbar] = useState(false);
    const tableScrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = tableScrollRef.current;
        if (!el) return;

        const checkScroll = () => {
            setHasScrollbar(el.scrollHeight > el.clientHeight);
        };

        checkScroll();

        const observer = new ResizeObserver(checkScroll);
        observer.observe(el);

        return () => observer.disconnect();
    }, [sortedCountData]); // ← also fix: was sortedStockData, should be sortedCountData

    // Order Request based on stock
    const [requestPackets, setRequestPackets] = useState<number>(0);

    // Helper: calculate how many units (or packets) needed to reach 100% stock
    function getOrderToAdequate(item: InventoryItems, desiredPartsNeeded: Record<number, number>) {
        const stockTarget = item.stock_target ?? 0;
        const requiredFromDetector = desiredPartsNeeded[item.partID];
        const current = item.currentAmount ?? 0;

        const required = stockTarget > 0 ? stockTarget : (requiredFromDetector ?? 0);
        if (required === 0) return null; // N/A items — nothing to order

        const deficit = required - current;
        if (deficit <= 0) return null; // already adequate or excess

        if (item.sold_in_packets && item.amount_per_packet && item.amount_per_packet > 0) {
            // Round up to the nearest whole packet
            const packetsNeeded = Math.ceil(deficit / item.amount_per_packet);
            return { units: packetsNeeded * item.amount_per_packet, packets: packetsNeeded };
        }

        return { units: deficit, packets: null };
    }

    const handleRegisterExistingDetector = async () => {
        if (!assembleDetectorId || !assembleMacAddress.trim()) {
            toast.warn("Please fill in all required fields.", toastStyle);
            return;
        }

        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        const detectorDef = detectors.find(d => d.id === assembleDetectorId);
        if (!detectorDef) return;

        try {
            const createdSubIds: Record<number, string> = {};

            for (const ds of detectorDef.subassemblies) {
                const subId = typeof ds.subassembly === "number" ? ds.subassembly : ds.subassembly.id;

                // Same payload and endpoint as REGISTER_EXISTING in handleSubAssemblyBuildAction
                const subResp = await fetch(apiUrl("/api/inventory_built_subs/"), {
                    method: "POST",
                    credentials: credentials(),
                    headers: { "Content-Type": "application/json", "X-CSRFToken": csrfToken },
                    body: JSON.stringify({
                        subassembly: subId,
                        built_by: loggedFnnUserId,
                        status: "available",
                    }),
                });

                if (!subResp.ok) {
                    const err = await subResp.json();
                    console.error(err);
                    toast.error(`Failed to register sub-assembly ${subId}.`, toastStyle);
                    return;
                }

                const created = await subResp.json();
                createdSubIds[subId] = created.id;
            }

            // Step 2: Assemble the detector with the registered sub-assembly IDs
            await assembleDetector(
                assembleDetectorId,
                createdSubIds,
                assembleMacAddress,
                assembleDetCode
            );

        } catch (error) {
            console.error(error);
            toast.error("An unexpected error occurred.", toastStyle);
        }
    };

    // Util for exporting item list
    const exportInventory = () => {
        if (!sortedInventory || sortedInventory.length === 0) {
            toast.warning('No inventory items to export');
            return;
        }

        const headers = [
            'Part ID',
            'Part Name',
            'Current Amount',
            'Unit Cost',
            'Total Cost',
            'Category',
            'Last Counted',
            'Storage Location',
            'Description',
            'MPN',
            'Manufacturer',
            'Stock Target',
            'Sold In Pack',
            'Amount Per Pack',
            'Part Image URL',
            'Store Link',
            'Storage Location Image URL',
            'Document URL',
        ];

        const cleanUrl = (url: string) => {
            try {
                const u = new URL(url);
                // Keep only the path, drop all query params
                return `${u.origin}${u.pathname}`;
            } catch {
                return url; // Return as-is if not a valid URL
            }
        };

        const formatDate = (val: any) => {
            if (!val) return '';
            const d = new Date(val);
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${y}/${m}/${day}`;
        };

        const rows = sortedInventory.map(item => {
            const totalCost = (item.unitCost || 0) * (item.currentAmount || 0);

            return [
                item.partID,
                item.partName || '',
                item.currentAmount ?? 0,
                item.unitCost?.toFixed(2) ?? '0.00',
                totalCost.toFixed(2),
                item.category || '',
                formatDate(item.lastCounted),
                item.location || '',
                item.description || '',
                item.mpn || '',
                item.manufacturer || '',
                item.stock_target ?? '',
                item.sold_in_packets ? 'Yes' : 'No',
                item.amount_per_packet ?? '',
                item.partPicture ? cleanUrl(item.partPicture) : '',
                item.partLink ? cleanUrl(item.partLink) : '',
                item.location_picture ? cleanUrl(item.location_picture) : '',
                item.partDocument ? cleanUrl(item.partDocument) : '',
            ];
        });

        const csvContent = [
            headers.join(','),
            ...rows.map(r =>
                r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')
            )
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');

        link.href = URL.createObjectURL(blob);
        link.setAttribute(
            'download',
            `inventory_${new Date().toISOString().split('T')[0]}.csv`
        );

        document.body.appendChild(link);
        link.click();
        link.remove();

        toast.success(`${rows.length} item${rows.length !== 1 ? 's' : ''} exported`);
    };

    return (
        <div className='inventory-wrapper'>

            <TeamHeader
                title="Inventory"
                inventoryTabs={tabs}
                activeTab={activeInvTab}
                onTabChange={setActiveInvTab}
            />

            <ToastContainer
                style={{ zIndex: 99999 }}
            />

            <div className='inventory-overall-wrapper'>

                <div className="inventory-tab-selection">
                    {tabs.map((tab) => (
                        <button
                            key={tab.key}
                            className={activeInvTab === tab.key ? "isActive" : ""}
                            onClick={() => setActiveInvTab(tab.key)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Inventory Overview */}
                {activeInvTab === "overview" &&
                    <div className='inventory-section-wrapper'>


                        <div className='inventory-summary-section'>

                            <div className="carousel-container">

                                {isWidgetCarousel && (
                                    <button className="carousel-arrow left" onClick={handleWidgetPrev}>
                                        &lt;
                                    </button>
                                )}

                                <div className="inventory-summary-section-info-widgets">
                                    {getVisibleWidgets().map(widget => (
                                        <React.Fragment key={widget.key}>
                                            {widget.component}
                                        </React.Fragment>
                                    ))}
                                </div>

                                {isWidgetCarousel && (
                                    <button className="carousel-arrow right" onClick={handleWidgetNext}>
                                        &gt;
                                    </button>
                                )}

                            </div>

                            { /* Big Widgets */}
                            <div className='inventory-highlights-wrapper'>

                                {/* Stock Availability Widget */}
                                <div className='inventory-highlight-widget no-gap'>
                                    <h1 className='inventory-section-title'>Stock Availability</h1>

                                    {(() => {
                                        const counts = { critical: 0, low: 0, adequate: 0, excess: 0, unassigned: 0 };

                                        invInfo.forEach(item => {
                                            const status = getStockStatus(item, desiredPartsNeeded).color;
                                            if (status === 'red') counts.critical++;
                                            else if (status === 'orange') counts.low++;
                                            else if (status === 'green') counts.adequate++;
                                            else if (status === 'purple') counts.excess++;
                                            else counts.unassigned++;
                                        });

                                        const total = invInfo.length || 1;

                                        const segments = [
                                            { key: 'critical', label: 'Critical', count: counts.critical, color: 'red', className: 'stock-red' },
                                            { key: 'low', label: 'Low', count: counts.low, color: 'orange', className: 'stock-orange' },
                                            { key: 'adequate', label: 'Adequate', count: counts.adequate, color: 'green', className: 'stock-green' },
                                            { key: 'excess', label: 'Excess', count: counts.excess, color: 'purple', className: 'stock-purple' },
                                            { key: 'unassigned', label: 'Unset', count: counts.unassigned, color: 'black', className: 'stock-na' },
                                        ];

                                        return (
                                            <>
                                                <div className='inventory-stock-legend'>
                                                    {segments.map(seg => (
                                                        <p key={seg.key}>
                                                            <FaCircle className={seg.className} />
                                                            {seg.label}
                                                        </p>
                                                    ))}
                                                </div>

                                                <div className='inventory-stock-bar'>
                                                    <div className="stock-progress-bar" style={{ display: 'flex', overflow: 'hidden' }}>
                                                        {segments.map(seg => {
                                                            const pct = (seg.count / total) * 100;
                                                            if (pct === 0) return null;
                                                            return (
                                                                <div
                                                                    key={seg.key}
                                                                    className="stock-progress-fill"
                                                                    style={{
                                                                        width: `${pct}%`,
                                                                        backgroundColor: seg.color === 'black'
                                                                            ? 'var(--color-border-secondary)'
                                                                            : seg.color,
                                                                        borderRadius: 0,
                                                                        minWidth: '2px',
                                                                    }}
                                                                />
                                                            );
                                                        })}
                                                    </div>
                                                </div>

                                                <div className='inventory-stock-list'>
                                                    {segments.map(seg => (
                                                        <div className='inventory-stock-list-item' key={seg.key}>
                                                            <p>{seg.label}</p>
                                                            <p><strong>{seg.count}</strong></p>
                                                        </div>

                                                    ))}
                                                </div>
                                            </>
                                        );
                                    })()}
                                </div>

                                {/* Items Pending Count Widget */}
                                <div className='inventory-highlight-widget'>
                                    <h1 className='inventory-section-title'>Items Pending Count</h1>

                                    {(() => {
                                        const counts = { red: 0, orange: 0, gray: 0 };

                                        invInfo.forEach(item => {
                                            const tooltip = getColorTooltip(item.lastCounted);
                                            if (tooltip.startsWith('Red')) counts.red++;
                                            else if (tooltip.startsWith('Orange')) counts.orange++;
                                            else if (tooltip.startsWith('Gray')) counts.gray++;
                                        });

                                        const pendingTotal = counts.red + counts.orange + counts.gray;

                                        const pendingLabels = ['Over 60 days', 'Over 30 days', 'Never Counted'];
                                        const pendingSeries = [counts.red, counts.orange, counts.gray];

                                        const pendingChartOptions: ApexOptions = {
                                            chart: { type: 'donut' },
                                            labels: pendingLabels,
                                            colors: ['#E24B4A', '#EF9F27', 'gray'],
                                            legend: {
                                                position: legendPosition,
                                                width: legendPosition === 'right' ? 160 : undefined,
                                                formatter: (seriesName: string) => {
                                                    return seriesName.length > 15
                                                        ? seriesName.slice(0, 15) + '...'
                                                        : seriesName;
                                                },
                                            },
                                            plotOptions: {
                                                pie: {
                                                    donut: {
                                                        size: '75%',
                                                        labels: {
                                                            show: true,
                                                            total: {
                                                                show: true,
                                                                label: 'Total',
                                                                fontSize: donutTotalFontSize,
                                                                fontWeight: 600,
                                                                color: '#373d3f',
                                                                formatter: () => pendingTotal.toString(),
                                                            },
                                                            name: {
                                                                show: true,
                                                                offsetY: nameSpacing,
                                                                formatter: (val: string) => val.split(' ').slice(0, 2).join(' '),
                                                            },
                                                            value: {
                                                                show: true,
                                                                fontSize: donutTotalFontSize,
                                                                color: '#373d3f',
                                                                offsetY: valueSpacing,
                                                            },
                                                        },
                                                    },
                                                },
                                            },
                                            dataLabels: { enabled: false },
                                            tooltip: {
                                                y: {
                                                    formatter: (val: number) => {
                                                        const percent = pendingTotal
                                                            ? ((val / pendingTotal) * 100).toFixed(1)
                                                            : 0;
                                                        return `${val} item(s) (${percent}%)`;
                                                    },
                                                },
                                            },
                                        };

                                        if (pendingTotal === 0) {
                                            return (
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, color: 'var(--color-text-secondary)', fontSize: '0.9rem' }}>
                                                    All items have been counted recently.
                                                </div>
                                            );
                                        }

                                        return (
                                            <div style={{ flex: 1, width: '100%' }}>
                                                <Chart
                                                    options={pendingChartOptions}
                                                    series={pendingSeries}
                                                    type="donut"
                                                    width="100%"
                                                    height="100%"
                                                />
                                            </div>
                                        );
                                    })()}
                                </div>


                                {/* Sub-Assembly Breakdown Widget */}
                                <div className='inventory-highlight-widget'>

                                    <div className='inventory-highlight-widget-title-container'>
                                        <h1 className='inventory-section-title'>Monthly Budget Overview</h1>

                                        <button className='inv-order-btn'
                                            onClick={() => {
                                                setActiveSection('Analytics');
                                                setShowSectionPopup(true);
                                            }}
                                        >
                                            <IoBarChart className='inv-order-btn-bag-svg' />
                                            <span>Report</span>
                                        </button>

                                    </div>

                                    <div className='inventory-budget-group'>

                                        <div className='inventory-budget-item'>

                                            <div className='inventory-budget-item-icon-wrapper'>
                                                <BsBoxFill />
                                            </div>

                                            <div className='inventory-budget-text'>
                                                <p>Inventory Value</p>
                                                <strong>${getFullInventoryCost(invInfo).toFixed(2)}</strong>
                                            </div>

                                        </div>

                                        <div className='inventory-budget-item'>

                                            <div className='inventory-budget-item-icon-wrapper'>
                                                <IoBag />
                                            </div>

                                            <div className='inventory-budget-text'>
                                                <p>Orders Placed</p>
                                                <strong>{getOrdersPlacedThisMonth(forecastInfo)}</strong>
                                            </div>

                                        </div>

                                        <div className='inventory-budget-item'>

                                            <div className='inventory-budget-item-icon-wrapper'>
                                                <FaLayerGroup />
                                            </div>

                                            <div className='inventory-budget-text'>
                                                <p>Value of Built Sub-Assemblies</p>
                                                <strong>${totalSubAssemblyProductionCost.toFixed(2)}</strong>
                                            </div>

                                        </div>

                                        <div className='inventory-budget-item'>

                                            <div className='inventory-budget-item-icon-wrapper'>
                                                <IoBagCheck />
                                            </div>

                                            <div className='inventory-budget-text'>
                                                <p>Orders Fullfilled</p>
                                                <strong>{getFulfilledOrdersCountThisMonth(forecastInfo)}</strong>
                                            </div>

                                        </div>

                                        <div className='inventory-budget-item'>

                                            <div className='inventory-budget-item-icon-wrapper'>
                                                <MdShoppingCart />
                                            </div>

                                            <div className='inventory-budget-text'>
                                                <p>Cost of Fulfilled Orders</p>
                                                <strong>${getFulfilledOrdersCostThisMonth(forecastInfo).toFixed(2)}</strong>
                                            </div>

                                        </div>

                                        <div className='inventory-budget-item'>

                                            <div className='inventory-budget-item-icon-wrapper'>
                                                <FaClipboardList />
                                            </div>

                                            <div className='inventory-budget-text'>
                                                <p>Items Ordered</p>
                                                <strong>{getItemsOrderedThisMonth(forecastInfo)}</strong>
                                            </div>

                                        </div>

                                    </div>
                                </div>

                                {/* Category Breakdown Widget */}
                                <div className='inventory-highlight-widget'>

                                    <h1 className='inventory-section-title'>Breakdown Per Category</h1>

                                    <div className='inventory--progress-container'>
                                        {(() => {
                                            const total = categorySeries.reduce((sum, val) => sum + val, 0);
                                            return categoryLabels.map((label, index) => {
                                                const count = categorySeries[index];
                                                const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                                                return (
                                                    <div className="mh-progress-row" key={label}>
                                                        <div className="mh-progress-header">
                                                            <div className="inventory-progress-label">{label}</div>
                                                            <div className='mh-regions-and-total'>
                                                                <div className="mh-progress-value">
                                                                    <span className="mh-pct">{pct}%</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="mh-progress-bar">
                                                            <div
                                                                className="inventory-progress-fill"
                                                                style={{ width: `${pct}%` }}
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            });
                                        })()}
                                    </div>

                                </div>

                                {/* Sub-Assembly Breakdown Widget */}
                                <div className='inventory-highlight-widget'>
                                    <div className='inventory-highlight-widget-title-container'>
                                        <h1 className='inventory-section-title'>Breakdown Per Sub-Assembly</h1>
                                        <select
                                            className='detector-version-select'
                                            value={subAssemblyChartDetectorId ?? ''}
                                            onChange={(e) => setSubAssemblyChartDetectorId(Number(e.target.value))}
                                        >
                                            {detectors.map(det => (
                                                <option key={det.id} value={det.id}>
                                                    v{det.version}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div style={{ flex: 1, width: "100%" }}>
                                        <Chart
                                            options={subAssemblyChartOptions}
                                            series={subAssemblySeries}
                                            type="donut"
                                            width="100%"
                                            height="100%"
                                        />
                                    </div>
                                </div>

                                {/* Build Activity Widget */}
                                <div className='inventory-highlight-widget build-wid-ext'>
                                    <h1 className='inventory-section-title'>Build Activity</h1>

                                    <h2 className='total-det-built-text'><strong>{builtDetectors.length}</strong> Total Detectors Assembled</h2>

                                    <div className='inventory-vertical-bar-chart'>
                                        {buildCountsPerMonth.months.map((month, i) => {
                                            const count = buildCountsPerMonth.counts[i];
                                            const maxCount = Math.max(...buildCountsPerMonth.counts, 1);
                                            const heightPercent = (count / maxCount) * 100;

                                            return (
                                                <div
                                                    key={month}
                                                    className='inventory-bar-container'
                                                    onMouseEnter={(e) => {
                                                        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                                        setBarTooltip({
                                                            text: `${count} assembled`,
                                                            x: rect.left + rect.width / 2,
                                                            y: rect.top,
                                                        });
                                                    }}
                                                    onMouseLeave={() => setBarTooltip(null)}
                                                >
                                                    <div className="inventory-bar-background">
                                                        <div
                                                            className='inventory-bar'
                                                            style={{ height: `${heightPercent}%` }}
                                                        />
                                                    </div>
                                                    <div className='inventory-bar-label'>{month.split(' ')[0]}</div>
                                                </div>
                                            );
                                        })}
                                    </div>

                                </div>

                            </div>

                        </div>


                    </div>
                }

                {/* Item List */}
                {activeInvTab === "items" &&
                    <div className='inventory-section-wrapper'>

                        <div className='inventory-count-list-header-wrapper'>

                            {/* Never Counted */}
                            <div className='inventory-count-list-header count-gray'
                                onClick={() => (setActiveSection('ItemsPending'), setShowSectionPopup(true))}
                            >

                                <h2>{invInfo.filter(item => getColorTooltip(item.lastCounted).startsWith('Gray')).length}</h2>
                                <h1>Never Counted</h1>

                            </div>

                            {/* Over 30 Days */}
                            <div className='inventory-count-list-header count-orange'
                                onClick={() => (setActiveSection('ItemsPending'), setShowSectionPopup(true))}
                            >
                                <h2>{invInfo.filter(item => getColorTooltip(item.lastCounted).startsWith('Orange')).length}</h2>
                                <h1>{isMobileSmall ? "30+ Since Count" : "Over 30 Days Uncounted"}</h1>
                            </div>


                            {/* Over 60 Days */}
                            <div className='inventory-count-list-header count-red'
                                onClick={() => (setActiveSection('ItemsPending'), setShowSectionPopup(true))}
                            >
                                <h2>{invInfo.filter(item => getColorTooltip(item.lastCounted).startsWith('Red')).length}</h2>
                                <h1>{isMobileSmall ? "60+ Since Count" : "Over 60 Days Uncounted"}</h1>
                            </div>



                        </div>

                        <div className='inv-filter-and-table'>

                            <div className='inv-stat-filters-and-opener'>

                                <div className="inv-sort-dropdown-wrapper">

                                    <div className="inv-search-wrapper">
                                        <input
                                            type="text"
                                            placeholder="Search"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="inv-search-input"
                                        />
                                    </div>

                                    <div className='inv-filter-sort'>
                                        <FilterDropdown
                                            dropdownClassName={
                                                isMobileSmall
                                                    ? "inv-filter-dropdown left-inv-dropdown"
                                                    : "inv-filter-dropdown"
                                            }
                                            sideClass="inv-side-drop"
                                            initialSelected={{
                                                subAssemblies: [],
                                                categories: [],
                                            }}
                                            resetValue={{
                                                subAssemblies: [],
                                                categories: [],
                                            }}
                                            sections={[
                                                {
                                                    key: "subAssemblies",
                                                    title: "Sub-Assembly",
                                                    options: subAssemblies.map(sa => sa.name),
                                                    type: "checkbox",
                                                },
                                                {
                                                    key: "categories",
                                                    title: "Category",
                                                    options: CATEGORY_OPTIONS.map(opt => opt.value),
                                                    type: "checkbox",
                                                    formatLabel: (option) =>
                                                        CATEGORY_OPTIONS.find(opt => opt.value === option)?.label ?? option,
                                                },
                                            ]}
                                            onChange={(filters) => {
                                                const selectedSubNames = filters.subAssemblies || [];
                                                const matchedSubIds = subAssemblies
                                                    .filter(sa => selectedSubNames.includes(sa.name))
                                                    .map(sa => sa.id);

                                                setFilterSelectedSubAssemblies(matchedSubIds);
                                                setSelectedCategories(filters.categories || []);
                                            }}
                                        />

                                        <div
                                            className={`inv-sort-dropdown ${isDropdownOpen ? 'open' : ''}`}
                                            onClick={() => setIsDropdownOpen(prev => !prev)}
                                        >
                                            <span className="inv-sort-selected">
                                                {sortOptions.find(o => o.value === sortMode)?.label}
                                            </span>
                                            <span className={`inv-sort-arrow ${isDropdownOpen ? 'rotated' : ''}`}>▼</span>
                                            {isDropdownOpen && (
                                                <div className="inv-sort-options">
                                                    {sortOptions.map(option => (
                                                        <div
                                                            key={option.value}
                                                            className={`inv-sort-option ${sortMode === option.value ? 'active' : ''}`}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSortMode(option.value as any);
                                                                setIsDropdownOpen(false);
                                                            }}
                                                        >
                                                            {option.label}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                </div>

                                <div className='inv-add-item-and-cards'>

                                    <button className='inv-order-btn'
                                        onClick={() => setShowAddItemPopup(true)}
                                    >
                                        <IoAddCircleOutline className='inv-order-btn-bag-svg' />
                                        <span>Add Item</span>
                                    </button>

                                    <button
                                        className='inv-order-btn order-btn2'
                                        onClick={() => {
                                            setNewStockAmount(desiredDetectors ?? 0); // preload value
                                            setShowSetStockPopup(true);
                                        }}
                                    >
                                        <BsBoxFill className='inv-order-btn-box-svg' />
                                        <span>Set Stock Status</span>
                                    </button>

                                    <button
                                        className='inv-order-btn order-btn2'
                                        onClick={exportInventory}
                                    >
                                        <FaFileCsv className='inv-order-btn-box-svg' />
                                        <span>Export</span>
                                    </button>

                                </div>

                            </div>

                            <div className="inventory-slip-wrapper">
                                {sortedInventory.map((item) => {
                                    const isOpen = !!expandedSlips[item.partID];
                                    const editedValues = editedValuesMap[item.partID] || {};

                                    const handleToggle = () =>
                                        setExpandedSlips(prev => ({ ...prev, [item.partID]: !prev[item.partID] }));

                                    const handleToggleDesktop = () =>
                                        setExpandedSlips(prev => ({ ...prev, [item.partID]: true }));

                                    const handleSave = (e: React.MouseEvent) => {
                                        e.stopPropagation();
                                        if (Object.keys(editedValues).length > 0) {
                                            updateInventoryItem(item.partID, editedValues);
                                            setEditedValuesMap(prev => ({ ...prev, [item.partID]: {} }));
                                        }
                                        setExpandedSlips(prev => ({ ...prev, [item.partID]: false }));
                                    };

                                    const handleKeyDown = (e: React.KeyboardEvent) => {
                                        if (e.key === "Enter" && !e.shiftKey) {
                                            e.preventDefault();
                                            handleSave(e as any); // trigger same logic as clicking save
                                        }
                                    };

                                    if (isMobile) {
                                        return (
                                            <div
                                                key={item.partID}
                                                className="inventory-item-container"
                                                style={{ display: "flex", flexDirection: "column" }}
                                            >
                                                {/* Closed Slip (Mobile) */}
                                                {!isOpen && (
                                                    <div
                                                        className="inventory-item-slip mobile-slip"
                                                        onClick={handleToggle}
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                        }}
                                                    >
                                                        <img
                                                            src={item.partPicture || fnnBlueLogo}
                                                            className="part-image"
                                                            alt={item.partName}
                                                        />
                                                        <div className="inventory-item-section">
                                                            <p>{renderPartName(item)}</p>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Open Slip (Mobile) */}
                                                {isOpen && (
                                                    <div
                                                        className="inventory-item-slip mobile-slip open"
                                                        onClick={handleToggle}
                                                        style={{
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            width: "100%",
                                                        }}
                                                    >

                                                        {/* --- Image Upload Field --- */}
                                                        <div className="inventory-item-section">
                                                            <p><strong>Image</strong></p>
                                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                                                                <img
                                                                    src={item.partPicture || fnnBlueLogo}
                                                                    alt={item.partName}
                                                                    className="part-image"
                                                                    style={{ cursor: 'pointer', maxWidth: '150px', maxHeight: '150px', objectFit: 'contain' }}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        const fileInput = document.getElementById(`img-input-${item.partID}`);
                                                                        if (fileInput) fileInput.click();
                                                                    }}
                                                                />
                                                            </div>
                                                            <input
                                                                type="file"
                                                                id={`img-input-${item.partID}`}
                                                                style={{ display: 'none' }}
                                                                accept="image/*"
                                                                onClick={(e) => e.stopPropagation()}
                                                                onChange={(e) => {
                                                                    e.stopPropagation();
                                                                    const file = e.target.files?.[0];
                                                                    if (!file) return;
                                                                    handleFileUpload(item.partID, file, "image"); // Reuse your upload handler
                                                                }}
                                                            />
                                                        </div>

                                                        {/* --- Part Name --- */}
                                                        <div className="inventory-item-section">
                                                            <p><strong>Part Name</strong></p>
                                                            <input
                                                                type="text"
                                                                defaultValue={item.partName}
                                                                onClick={stopClick}
                                                                onChange={(e) =>
                                                                    setEditedValuesMap(prev => ({
                                                                        ...prev,
                                                                        [item.partID]: { ...prev[item.partID], partName: e.target.value },
                                                                    }))
                                                                }
                                                                onKeyDown={handleKeyDown}
                                                            />

                                                        </div>

                                                        {/* --- Unit Amount --- */}
                                                        <div className="inventory-item-section">
                                                            <p><strong>Unit Amount</strong></p>
                                                            {isOpen ? (
                                                                <input
                                                                    type="number"
                                                                    onWheel={(e) => e.currentTarget.blur()}
                                                                    defaultValue={item.currentAmount}
                                                                    onClick={stopClick}
                                                                    onChange={(e) =>
                                                                        setEditedValuesMap(prev => ({
                                                                            ...prev,
                                                                            [item.partID]: { ...prev[item.partID], currentAmount: Number(e.target.value) }
                                                                        }))
                                                                    }
                                                                    onKeyDown={handleKeyDown}
                                                                />
                                                            ) : (
                                                                (() => {

                                                                    return (
                                                                        <p>{item.currentAmount}</p>
                                                                    );
                                                                })()
                                                            )}
                                                        </div>

                                                        <div className="inventory-item-section">
                                                            <div className='inventory-mobile-checkbox'>
                                                                <p style={{ margin: 0 }}>
                                                                    <strong>
                                                                        {(
                                                                            editedValuesMap[item.partID]?.sold_in_packets ??
                                                                            item.sold_in_packets
                                                                        )
                                                                            ? "Amount in Pack"
                                                                            : "Sold in Packs?"
                                                                        }
                                                                    </strong>
                                                                </p>

                                                                <div style={{ display: 'flex', alignItems: "flex-end", gap: "0.5rem", flexDirection: 'column' }}>
                                                                    {/* Checkbox */}
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={
                                                                            editedValuesMap[item.partID]?.sold_in_packets ??
                                                                            !!item.sold_in_packets
                                                                        }
                                                                        onClick={stopClick}
                                                                        onChange={(e) =>
                                                                            setEditedValuesMap(prev => ({
                                                                                ...prev,
                                                                                [item.partID]: {
                                                                                    ...prev[item.partID],
                                                                                    sold_in_packets: e.target.checked,
                                                                                    amount_per_packet: e.target.checked
                                                                                        ? prev[item.partID]?.amount_per_packet ?? item.amount_per_packet ?? null
                                                                                        : null, // clear when unchecked
                                                                                },
                                                                            }))
                                                                        }
                                                                    />

                                                                    {/* Amount per packet (only if checked) */}
                                                                    {(editedValuesMap[item.partID]?.sold_in_packets ??
                                                                        item.sold_in_packets) && (
                                                                            <input
                                                                                type="number"
                                                                                onWheel={(e) => e.currentTarget.blur()}
                                                                                min={1}
                                                                                placeholder="How many?"
                                                                                defaultValue={
                                                                                    editedValuesMap[item.partID]?.amount_per_packet ??
                                                                                    item.amount_per_packet ??
                                                                                    ""
                                                                                }
                                                                                onClick={stopClick}
                                                                                onChange={(e) =>
                                                                                    setEditedValuesMap(prev => ({
                                                                                        ...prev,
                                                                                        [item.partID]: {
                                                                                            ...prev[item.partID],
                                                                                            amount_per_packet:
                                                                                                e.target.value === ""
                                                                                                    ? null
                                                                                                    : Number(e.target.value),
                                                                                        },
                                                                                    }))
                                                                                }
                                                                                onKeyDown={handleKeyDown}
                                                                            />
                                                                        )}
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* --- Stock Target --- */}
                                                        <div className="inventory-item-section">

                                                            <div style={{ display: "flex", gap: "0.3rem" }}>
                                                                <p><strong>Stock Target</strong></p>
                                                                {showInfoTooltip("Desired amount to be kept in stock.")}
                                                            </div>
                                                            <input
                                                                type="number"
                                                                onWheel={(e) => e.currentTarget.blur()}
                                                                defaultValue={item.stock_target ?? ''}  // empty string if null
                                                                onClick={stopClick}
                                                                onChange={(e) =>
                                                                    setEditedValuesMap(prev => ({
                                                                        ...prev,
                                                                        [item.partID]: {
                                                                            ...prev[item.partID],
                                                                            stock_target: e.target.value === '' ? null : Number(e.target.value), // handle clear as null
                                                                        },
                                                                    }))
                                                                }
                                                                onKeyDown={handleKeyDown}
                                                            />

                                                        </div>

                                                        {/* --- Stock Status --- */}
                                                        <div className="inventory-item-section">
                                                            <p><strong>Stock Status</strong></p>
                                                            <span
                                                                style={{
                                                                    color: getStockStatus(item, desiredPartsNeeded).color,
                                                                    fontWeight: 600,
                                                                    cursor: "help",
                                                                    paddingRight: '1rem',
                                                                    paddingTop: '0.25rem',
                                                                    paddingBottom: '0.25rem'
                                                                }}
                                                                className="tooltip-wrapper"
                                                            >
                                                                {getStockStatus(item, desiredPartsNeeded).status}
                                                                <div className="custom-tooltip">
                                                                    {getStockStatus(item, desiredPartsNeeded).tooltip}
                                                                </div>
                                                            </span>
                                                        </div>

                                                        {/* --- Unit Cost --- */}
                                                        <div className="inventory-item-section">
                                                            <div style={{ display: "flex", gap: "0.3rem" }}>
                                                                <p><strong>Unit Cost</strong></p>
                                                                {showInfoTooltip("Price of a single unit of this item. NOT the cost of the pack")}
                                                            </div>
                                                            <input
                                                                type="number"
                                                                onWheel={(e) => e.currentTarget.blur()}
                                                                step="0.01"
                                                                defaultValue={item.unitCost}
                                                                onClick={stopClick}
                                                                onChange={(e) =>
                                                                    setEditedValuesMap(prev => ({
                                                                        ...prev,
                                                                        [item.partID]: { ...prev[item.partID], unitCost: Number(e.target.value) },
                                                                    }))
                                                                }
                                                                onKeyDown={handleKeyDown}
                                                            />
                                                        </div>

                                                        {/* --- Category --- */}
                                                        <div className="inventory-item-section">
                                                            <p><strong>Category</strong></p>
                                                            <select
                                                                defaultValue={item.category}
                                                                onClick={stopClick}
                                                                onChange={(e) =>
                                                                    setEditedValuesMap(prev => ({
                                                                        ...prev,
                                                                        [item.partID]: { ...prev[item.partID], category: e.target.value },
                                                                    }))
                                                                }
                                                            >
                                                                {CATEGORY_OPTIONS.map(opt => (
                                                                    <option key={opt.value} value={opt.value}>
                                                                        {opt.label}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>

                                                        {/* --- Manufacturer --- */}
                                                        <div className="inventory-item-section">
                                                            <p><strong>Manufacturer</strong></p>
                                                            <input
                                                                type="text"
                                                                defaultValue={item.manufacturer || ""}
                                                                onClick={stopClick}
                                                                onChange={(e) =>
                                                                    setEditedValuesMap(prev => ({
                                                                        ...prev,
                                                                        [item.partID]: { ...prev[item.partID], manufacturer: e.target.value },
                                                                    }))
                                                                }
                                                                onKeyDown={handleKeyDown}
                                                            />
                                                        </div>

                                                        {/* --- MPN --- */}
                                                        <div className="inventory-item-section">
                                                            <div style={{ display: "flex", gap: "0.3rem" }}>
                                                                <p><strong>MPN</strong></p>
                                                                {showInfoTooltip("Manufacturer Part Number - normally found in the part link.")}
                                                            </div>
                                                            <input
                                                                type="text"
                                                                defaultValue={item.mpn || ""}
                                                                onClick={stopClick}
                                                                onChange={(e) =>
                                                                    setEditedValuesMap(prev => ({
                                                                        ...prev,
                                                                        [item.partID]: { ...prev[item.partID], mpn: e.target.value },
                                                                    }))
                                                                }
                                                                onKeyDown={handleKeyDown}
                                                            />
                                                        </div>

                                                        {/* --- Bundle Sub-Assembly --- */}
                                                        <div className="inventory-item-section">
                                                            <div style={{ display: "flex", gap: "0.3rem" }}>
                                                                <p><strong>Bundle Sub-Assembly</strong></p>
                                                                {showInfoTooltip("If this item is a bundle (e.g. a screw box), link it to a sub-assembly. Counting this item will also update the count of all parts in that sub-assembly.")}
                                                            </div>
                                                            <select
                                                                defaultValue={item.bundle_subassembly ?? ""}
                                                                onClick={stopClick}
                                                                onChange={(e) =>
                                                                    setEditedValuesMap(prev => ({
                                                                        ...prev,
                                                                        [item.partID]: {
                                                                            ...prev[item.partID],
                                                                            bundle_subassembly: e.target.value === "" ? null : Number(e.target.value),
                                                                        },
                                                                    }))
                                                                }
                                                            >
                                                                <option value="">— None —</option>
                                                                {subAssemblies.map(sa => (
                                                                    <option key={sa.id} value={sa.id}>
                                                                        {sa.name}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>

                                                        {/* --- Link --- */}
                                                        <div className="inventory-item-section">
                                                            <p><strong>Link</strong></p>
                                                            <input
                                                                type="url"
                                                                defaultValue={item.partLink || ""}
                                                                onClick={stopClick}
                                                                onChange={(e) =>
                                                                    setEditedValuesMap(prev => ({
                                                                        ...prev,
                                                                        [item.partID]: { ...prev[item.partID], partLink: e.target.value },
                                                                    }))
                                                                }
                                                                onKeyDown={handleKeyDown}
                                                            />
                                                        </div>

                                                        {/* --- Document Upload --- */}
                                                        <div className="inventory-item-section">
                                                            <div style={{ display: "flex", gap: "0.3rem" }}>
                                                                <p><strong>Document</strong></p>
                                                                {showInfoTooltip("Normally used for custom parts indicating its SOP.")}
                                                            </div>

                                                            <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'right' }}>
                                                                {item.partDocument ? (
                                                                    <a
                                                                        href={item.partDocument}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="clickable-inv-option"
                                                                        onClick={stopClick}
                                                                    >
                                                                        Download
                                                                    </a>
                                                                ) : (
                                                                    <span>None</span>
                                                                )}
                                                                <button
                                                                    className="counted-button mobile-counted-button"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        const fileInput = document.getElementById(`doc-input-${item.partID}`);
                                                                        if (fileInput) fileInput.click();
                                                                    }}
                                                                >
                                                                    Upload
                                                                </button>
                                                            </div>

                                                            <input
                                                                type="file"
                                                                id={`doc-input-${item.partID}`}
                                                                style={{ display: "none" }}
                                                                accept=".pdf,.doc,.docx,.txt"
                                                                onClick={(e) => e.stopPropagation()}
                                                                onChange={(e) => {
                                                                    e.stopPropagation();
                                                                    const file = e.target.files?.[0];
                                                                    if (!file) return;
                                                                    handleFileUpload(item.partID, file, "document");
                                                                }}
                                                            />
                                                        </div>

                                                        {/* --- Description --- */}
                                                        <div className="inventory-item-section">
                                                            <p><strong>Description</strong></p>
                                                            <textarea
                                                                defaultValue={item.description || ""}
                                                                onClick={stopClick}
                                                                rows={4}
                                                                onChange={(e) =>
                                                                    setEditedValuesMap(prev => ({
                                                                        ...prev,
                                                                        [item.partID]: { ...prev[item.partID], description: e.target.value },
                                                                    }))
                                                                }
                                                                onKeyDown={handleKeyDown}
                                                            />
                                                        </div>

                                                        {/* --- Location --- */}
                                                        <div className="inventory-item-section">
                                                            <p><strong>Location</strong></p>
                                                            <textarea
                                                                defaultValue={item.location || ""}
                                                                onClick={stopClick}
                                                                rows={4}
                                                                onChange={(e) =>
                                                                    setEditedValuesMap(prev => ({
                                                                        ...prev,
                                                                        [item.partID]: { ...prev[item.partID], location: e.target.value },
                                                                    }))
                                                                }
                                                                onKeyDown={handleKeyDown}
                                                            />
                                                        </div>

                                                        <div className="inventory-item-section">
                                                            <p><strong>Picture of Location</strong></p>

                                                            <div
                                                                className="location-picture-container"
                                                                style={{ backgroundColor: item.location_picture ? "transparent" : "#e2dedeff" }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    const fileInput = document.getElementById(`location-input-${item.partID}`);
                                                                    if (fileInput) fileInput.click();
                                                                }}
                                                            >
                                                                {item.location_picture ? (
                                                                    <>
                                                                        <img
                                                                            src={item.location_picture}
                                                                            alt="Location"
                                                                            className="location-picture"
                                                                        />
                                                                    </>
                                                                ) : (
                                                                    <AiFillPicture className="location-placeholder-icon" />
                                                                )}

                                                                <div className="location-picture-overlay">
                                                                    <span>Click to Upload</span>
                                                                </div>
                                                            </div>

                                                            <input
                                                                type="file"
                                                                id={`location-input-${item.partID}`}
                                                                style={{ display: "none" }}
                                                                accept="image/*"
                                                                onClick={(e) => e.stopPropagation()}
                                                                onChange={(e) => {
                                                                    e.stopPropagation();
                                                                    const file = e.target.files?.[0];
                                                                    if (!file) return;
                                                                    handleFileUpload(item.partID, file, "location");
                                                                }}
                                                            />
                                                        </div>

                                                        {/* --- Counted Button --- */}
                                                        <div className="inventory-item-section" style={{ alignItems: "center" }}>
                                                            <p><strong>Mark as Counted</strong></p>
                                                            <button
                                                                className="delete-part-button mobile-confirm-part-button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setItemToCount(item);
                                                                    setShowCountedPopup(true);
                                                                }}
                                                            >
                                                                ✓
                                                            </button>
                                                        </div>

                                                        {/* --- Save / Delete Buttons --- */}
                                                        <div
                                                            className="inventory-item-open-slip-buttons"
                                                            style={{ display: "flex", gap: "0.5rem" }}
                                                            onClick={handleToggle}
                                                        >
                                                            <button onClick={handleSave}>Save</button>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setItemToDeleteId(item.partID);
                                                                    setShowDeleteItemPopup(true);
                                                                }}
                                                            >
                                                                Delete
                                                            </button>

                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setExpandedSlips(prev => ({ ...prev, [item.partID]: false }));
                                                                    setEditingPartId(null);
                                                                    setEditedValues({});
                                                                }}
                                                            >
                                                                Close
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    }


                                    return (
                                        <div key={item.partID} className="inventory-item-container">
                                            {/* --- Collapsed / Header Row --- */}
                                            <div className={`inventory-item-slip ${isOpen ? "open" : ""}`} onClick={handleToggleDesktop}>
                                                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                                    {/* --- Image Upload Field --- */}
                                                    <div
                                                        style={{
                                                            position: "relative",
                                                            display: "inline-block",
                                                            cursor: isOpen ? "pointer" : "default"
                                                        }}
                                                        onClick={(e) => {
                                                            if (!isOpen) return;
                                                            e.stopPropagation();
                                                            const fileInput = document.getElementById(`img-input-${item.partID}`);
                                                            if (fileInput) fileInput.click();
                                                        }}
                                                    >
                                                        <img
                                                            src={item.partPicture || fnnBlueLogo}
                                                            className="part-image desktop-upload-image"
                                                            alt={item.partName}
                                                        />

                                                        {item.partPicture && (
                                                            <div className="part-image-tooltip">
                                                                <img src={item.partPicture} alt="Preview" />
                                                            </div>
                                                        )}

                                                        {isOpen && (
                                                            <>
                                                                <div className="image-upload-overlay">
                                                                    <span>Click to Upload</span>
                                                                </div>

                                                            </>
                                                        )}
                                                    </div>

                                                    <input
                                                        type="file"
                                                        id={`img-input-${item.partID}`}
                                                        style={{ display: "none" }}
                                                        accept="image/*"
                                                        onClick={(e) => e.stopPropagation()}
                                                        onChange={(e) => {
                                                            e.stopPropagation();
                                                            const file = e.target.files?.[0];
                                                            if (!file) return;
                                                            handleFileUpload(item.partID, file, "image"); // Reuse your handler
                                                        }}
                                                    />

                                                    {/* --- Part Name --- */}
                                                    <div className="inventory-item-section">
                                                        <p><strong>Part Name</strong></p>
                                                        {isOpen ? (
                                                            <input
                                                                type="text"
                                                                defaultValue={item.partName}
                                                                onClick={stopClick}
                                                                onChange={(e) =>
                                                                    setEditedValuesMap(prev => ({
                                                                        ...prev,
                                                                        [item.partID]: { ...prev[item.partID], partName: e.target.value }
                                                                    }))
                                                                }
                                                                onKeyDown={handleKeyDown}
                                                            />
                                                        ) : (
                                                            <p>{renderPartName(item)}</p>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="inventory-item-section">
                                                    <p><strong>Unit Amount</strong></p>
                                                    {isOpen ? (
                                                        <input
                                                            type="number"
                                                            onWheel={(e) => e.currentTarget.blur()}
                                                            defaultValue={item.currentAmount}
                                                            onClick={stopClick}
                                                            onChange={(e) =>
                                                                setEditedValuesMap(prev => ({
                                                                    ...prev,
                                                                    [item.partID]: { ...prev[item.partID], currentAmount: Number(e.target.value) }
                                                                }))
                                                            }
                                                            onKeyDown={handleKeyDown}
                                                        />
                                                    ) : (
                                                        (() => {

                                                            return (
                                                                <p>{item.currentAmount}</p>
                                                            );

                                                        })()
                                                    )}
                                                </div>


                                                <div className="inventory-item-section">
                                                    <div style={{ display: 'flex', gap: '0.3rem' }}>
                                                        <p style={{ whiteSpace: 'nowrap' }}><strong>Unit Cost</strong></p>
                                                        {showInfoTooltip("Price of a single unit of this item. NOT the cost of the pack.")}
                                                    </div>

                                                    {isOpen ? (
                                                        <input
                                                            type="number"
                                                            onWheel={(e) => e.currentTarget.blur()}
                                                            step="0.01"
                                                            defaultValue={item.unitCost}
                                                            onClick={stopClick}
                                                            onChange={(e) =>
                                                                setEditedValuesMap(prev => ({
                                                                    ...prev,
                                                                    [item.partID]: { ...prev[item.partID], unitCost: Number(e.target.value) }
                                                                }))
                                                            }
                                                            onKeyDown={handleKeyDown}
                                                        />
                                                    ) : (
                                                        <p>${item.unitCost.toFixed(2)}</p>
                                                    )}
                                                </div>

                                                <div className="inventory-item-section">
                                                    <p><strong>Total Cost</strong></p>

                                                    {isOpen ? (
                                                        <p style={{ marginTop: '0.3rem' }}>${(item.unitCost * item.currentAmount).toFixed(2)}</p>
                                                    ) : (
                                                        <p>${(item.unitCost * item.currentAmount).toFixed(2)}</p>
                                                    )}
                                                </div>

                                                <div className="inventory-item-section">
                                                    <p><strong>Category</strong></p>
                                                    {isOpen ? (
                                                        <select
                                                            defaultValue={item.category}
                                                            onClick={stopClick}
                                                            onChange={(e) =>
                                                                setEditedValuesMap(prev => ({
                                                                    ...prev,
                                                                    [item.partID]: { ...prev[item.partID], category: e.target.value }
                                                                }))
                                                            }
                                                            onKeyDown={handleKeyDown}
                                                        >
                                                            {CATEGORY_OPTIONS.map(opt => (
                                                                <option key={opt.value} value={opt.value}>
                                                                    {opt.label}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    ) : (
                                                        <p>{CATEGORY_OPTIONS.find(opt => opt.value === item.category)?.label || item.category}</p>
                                                    )}
                                                </div>


                                                <div className="inventory-item-section">
                                                    <p><strong>Stock Status</strong></p>
                                                    {isOpen ? (
                                                        <span
                                                            style={{
                                                                color: getStockStatus(item, desiredPartsNeeded).color,
                                                                fontWeight: 600,
                                                                cursor: "help",
                                                                marginTop: '0.3rem'
                                                            }}
                                                            className="tooltip-wrapper"
                                                        >
                                                            {getStockStatus(item, desiredPartsNeeded).status}
                                                            <div className="custom-tooltip">
                                                                {getStockStatus(item, desiredPartsNeeded).tooltip}
                                                            </div>
                                                        </span>
                                                    ) : (
                                                        <span
                                                            style={{
                                                                color: getStockStatus(item, desiredPartsNeeded).color,
                                                                fontWeight: 600,
                                                                cursor: "help"
                                                            }}
                                                            className="tooltip-wrapper"
                                                        >
                                                            {getStockStatus(item, desiredPartsNeeded).status}
                                                            <div className="custom-tooltip">
                                                                {getStockStatus(item, desiredPartsNeeded).tooltip}
                                                            </div>
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            {/* --- Expanded Details --- */}
                                            {isOpen && (
                                                <>
                                                    <div className="inventory-item-slip invetory-item-selected-slip" onClick={handleToggleDesktop}>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                                            <div className="inventory-item-section">
                                                                <p><strong>Link</strong></p>
                                                                <input
                                                                    type="url"
                                                                    defaultValue={item.partLink || undefined}
                                                                    placeholder='E.g "www.store.com"'
                                                                    onClick={stopClick}
                                                                    onChange={(e) =>
                                                                        setEditedValuesMap(prev => ({
                                                                            ...prev,
                                                                            [item.partID]: { ...prev[item.partID], partLink: e.target.value }
                                                                        }))
                                                                    }
                                                                    onKeyDown={handleKeyDown}
                                                                />
                                                            </div>
                                                            <div className="inventory-item-section">

                                                                <div style={{ display: 'flex', gap: '0.3rem' }}>
                                                                    <p><strong>Document</strong></p>
                                                                    {showInfoTooltip("Normally used for custom parts indicating its SOP.")}
                                                                </div>

                                                                {item.partDocument ? (
                                                                    <a
                                                                        href={item.partDocument}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="clickable-inv-option"
                                                                        onClick={stopClick}
                                                                    >
                                                                        {item.partDocument.split("/").pop()} {/* Shows the file name */}
                                                                    </a>
                                                                ) : (
                                                                    <span>None</span>
                                                                )}

                                                                <button
                                                                    className="counted-button mobile-counted-button"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        const fileInput = document.getElementById(`doc-input-${item.partID}`);
                                                                        if (fileInput) fileInput.click();
                                                                    }}
                                                                >
                                                                    Upload
                                                                </button>

                                                                <input
                                                                    type="file"
                                                                    id={`doc-input-${item.partID}`}
                                                                    style={{ display: "none" }}
                                                                    accept=".pdf,.doc,.docx,.txt"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    onChange={(e) => {
                                                                        e.stopPropagation();
                                                                        const file = e.target.files?.[0];
                                                                        if (!file) return;
                                                                        handleFileUpload(item.partID, file, "document");
                                                                    }}
                                                                />
                                                            </div>

                                                            <div className="inventory-item-section" style={{ marginTop: '2rem', width: 'fit-content' }}>
                                                                <p><strong>Mark as Counted</strong></p>
                                                                <button
                                                                    className="counted-button mobile-counted-button"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setItemToCount(item);
                                                                        setShowCountedPopup(true);
                                                                    }}
                                                                >
                                                                    Confirm
                                                                </button>
                                                            </div>

                                                        </div>

                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>

                                                            <div className="inventory-item-section">

                                                                <div style={{ display: "flex", gap: "0.3rem" }}>
                                                                    <p><strong>Stock Target</strong></p>
                                                                    {showInfoTooltip("Desired amount to be kept in stock.")}
                                                                </div>
                                                                <input
                                                                    type="number"
                                                                    onWheel={(e) => e.currentTarget.blur()}
                                                                    defaultValue={item.stock_target ?? ''}  // empty string if null
                                                                    onClick={stopClick}
                                                                    onChange={(e) =>
                                                                        setEditedValuesMap(prev => ({
                                                                            ...prev,
                                                                            [item.partID]: {
                                                                                ...prev[item.partID],
                                                                                stock_target: e.target.value === '' ? null : Number(e.target.value), // handle clear as null
                                                                            },
                                                                        }))
                                                                    }
                                                                    onKeyDown={handleKeyDown}
                                                                />

                                                            </div>


                                                            <div className="inventory-item-section">
                                                                <div>

                                                                    <p style={{ margin: 0 }}>
                                                                        <strong>
                                                                            {(
                                                                                editedValuesMap[item.partID]?.sold_in_packets ??
                                                                                item.sold_in_packets
                                                                            )
                                                                                ? "Amount in Pack"
                                                                                : "Sold in Packs?"
                                                                            }
                                                                        </strong>
                                                                    </p>

                                                                    <div style={{ display: 'flex', alignItems: "center", gap: "0.5rem" }}>
                                                                        {/* Checkbox */}
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={
                                                                                editedValuesMap[item.partID]?.sold_in_packets ??
                                                                                !!item.sold_in_packets
                                                                            }
                                                                            onClick={stopClick}
                                                                            onChange={(e) =>
                                                                                setEditedValuesMap(prev => ({
                                                                                    ...prev,
                                                                                    [item.partID]: {
                                                                                        ...prev[item.partID],
                                                                                        sold_in_packets: e.target.checked,
                                                                                        amount_per_packet: e.target.checked
                                                                                            ? prev[item.partID]?.amount_per_packet ?? item.amount_per_packet ?? null
                                                                                            : null,
                                                                                    },
                                                                                }))
                                                                            }
                                                                            style={{
                                                                                marginBottom: !(
                                                                                    editedValuesMap[item.partID]?.sold_in_packets ??
                                                                                    !!item.sold_in_packets
                                                                                )
                                                                                    ? "1rem"
                                                                                    : "0"
                                                                            }}
                                                                        />

                                                                        {/* Amount per packet (only if checked) */}
                                                                        {(editedValuesMap[item.partID]?.sold_in_packets ??
                                                                            item.sold_in_packets) && (
                                                                                <input
                                                                                    type="number"
                                                                                    onWheel={(e) => e.currentTarget.blur()}
                                                                                    min={1}
                                                                                    placeholder="How many?"
                                                                                    defaultValue={
                                                                                        editedValuesMap[item.partID]?.amount_per_packet ??
                                                                                        item.amount_per_packet ??
                                                                                        ""
                                                                                    }
                                                                                    onClick={stopClick}
                                                                                    onChange={(e) =>
                                                                                        setEditedValuesMap(prev => ({
                                                                                            ...prev,
                                                                                            [item.partID]: {
                                                                                                ...prev[item.partID],
                                                                                                amount_per_packet:
                                                                                                    e.target.value === ""
                                                                                                        ? null
                                                                                                        : Number(e.target.value),
                                                                                            },
                                                                                        }))
                                                                                    }
                                                                                    onKeyDown={handleKeyDown}
                                                                                />
                                                                            )}
                                                                    </div>

                                                                </div>
                                                            </div>


                                                            <div className="inventory-item-section">
                                                                <div style={{ display: 'flex', gap: '0.3rem' }}>
                                                                    <p><strong>MPN</strong></p>
                                                                    {showInfoTooltip("Manufacturer Part Number - hardware or building materials normally have this number in its store link.")}
                                                                </div>

                                                                <input
                                                                    type="text"
                                                                    defaultValue={item.mpn || ""}
                                                                    onClick={stopClick}
                                                                    onChange={(e) =>
                                                                        setEditedValuesMap(prev => ({
                                                                            ...prev,
                                                                            [item.partID]: { ...prev[item.partID], mpn: e.target.value }
                                                                        }))
                                                                    }
                                                                    onKeyDown={handleKeyDown}
                                                                />
                                                            </div>

                                                            {/* --- Bundle Sub-Assembly --- */}
                                                            <div className="inventory-item-section">
                                                                <div style={{ display: "flex", gap: "0.3rem" }}>
                                                                    <p><strong>Bundle Sub-Assembly</strong></p>
                                                                    {showInfoTooltip("If this item is a bundle (e.g. a screw box), link it to a sub-assembly. Counting this item will also update the count of all parts in that sub-assembly.")}
                                                                </div>
                                                                <select
                                                                    defaultValue={item.bundle_subassembly ?? ""}
                                                                    onClick={stopClick}
                                                                    onChange={(e) =>
                                                                        setEditedValuesMap(prev => ({
                                                                            ...prev,
                                                                            [item.partID]: {
                                                                                ...prev[item.partID],
                                                                                bundle_subassembly: e.target.value === "" ? null : Number(e.target.value),
                                                                            },
                                                                        }))
                                                                    }
                                                                >
                                                                    <option value="">— None —</option>
                                                                    {subAssemblies.map(sa => (
                                                                        <option key={sa.id} value={sa.id}>
                                                                            {sa.name}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            </div>

                                                        </div>

                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

                                                            <div className="inventory-item-section">
                                                                <p><strong>Description</strong></p>
                                                                <textarea
                                                                    defaultValue={item.description || undefined}
                                                                    placeholder='E.g "Screw used to mount Dome to Pole"'
                                                                    onClick={stopClick}
                                                                    rows={4}
                                                                    onChange={(e) =>
                                                                        setEditedValuesMap(prev => ({
                                                                            ...prev,
                                                                            [item.partID]: { ...prev[item.partID], description: e.target.value }
                                                                        }))
                                                                    }
                                                                    onKeyDown={handleKeyDown}
                                                                />
                                                            </div>

                                                            <div className="inventory-item-section">

                                                                <p><strong>Manufacturer</strong></p>
                                                                <input
                                                                    type="text"
                                                                    defaultValue={item.manufacturer || ""}
                                                                    onClick={stopClick}
                                                                    onChange={(e) =>
                                                                        setEditedValuesMap(prev => ({
                                                                            ...prev,
                                                                            [item.partID]: { ...prev[item.partID], manufacturer: e.target.value }
                                                                        }))
                                                                    }
                                                                    onKeyDown={handleKeyDown}
                                                                />
                                                            </div>
                                                        </div>



                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                                            <div className="inventory-item-section">
                                                                <p><strong>Location</strong></p>
                                                                <textarea
                                                                    defaultValue={item.location || undefined}
                                                                    placeholder='E.g "Stored in room X, first drawer"'
                                                                    onClick={stopClick}
                                                                    rows={4}
                                                                    onChange={(e) =>
                                                                        setEditedValuesMap(prev => ({
                                                                            ...prev,
                                                                            [item.partID]: { ...prev[item.partID], location: e.target.value }
                                                                        }))
                                                                    }
                                                                    onKeyDown={handleKeyDown}
                                                                />
                                                            </div>

                                                            <div className="inventory-item-section">
                                                                <p><strong>Picture of Location</strong></p>

                                                                <div
                                                                    className="location-picture-container"
                                                                    style={{ backgroundColor: item.location_picture ? "transparent" : "#e2dedeff" }}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        const fileInput = document.getElementById(`location-input-${item.partID}`);
                                                                        if (fileInput) fileInput.click();
                                                                    }}
                                                                >
                                                                    {item.location_picture ? (
                                                                        <>
                                                                            <img
                                                                                src={item.location_picture}
                                                                                alt="Location"
                                                                                className="location-picture"
                                                                            />
                                                                            {/* Large preview tooltip */}
                                                                            <div className="location-picture-tooltip">
                                                                                <img src={item.location_picture} alt="Preview" />
                                                                            </div>
                                                                        </>
                                                                    ) : (
                                                                        <AiFillPicture className="location-placeholder-icon" />
                                                                    )}

                                                                    <div className="location-picture-overlay">
                                                                        <span>Click to Upload</span>
                                                                    </div>
                                                                </div>

                                                                <input
                                                                    type="file"
                                                                    id={`location-input-${item.partID}`}
                                                                    style={{ display: "none" }}
                                                                    accept="image/*"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    onChange={(e) => {
                                                                        e.stopPropagation();
                                                                        const file = e.target.files?.[0];
                                                                        if (!file) return;
                                                                        handleFileUpload(item.partID, file, "location");
                                                                    }}
                                                                />
                                                            </div>


                                                        </div>

                                                    </div>

                                                    <div className="inventory-item-open-slip-buttons">
                                                        <button onClick={handleSave}>Save</button>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setExpandedSlips(prev => ({ ...prev, [item.partID]: false }));
                                                                setItemToDeleteId(item.partID);
                                                                setShowDeleteItemPopup(true);
                                                            }}
                                                        >
                                                            Delete
                                                        </button>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setExpandedSlips(prev => ({ ...prev, [item.partID]: false }));
                                                                setEditingPartId(null);
                                                                setEditedValues({});
                                                            }}
                                                        >
                                                            Close
                                                        </button>

                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                        </div>
                    </div>
                }

                {/* Ordering Parts */}
                {activeInvTab === "orders" &&
                    <div className='inventory-section-wrapper'>

                        <div className="wrapper-for-orders">

                            {isMobile2 && (
                                <div style={{ width: '100%' }}>
                                    <div className='mobile-order-filter-and-search'>
                                        <div className="inv-stat-filters">
                                            {Object.keys(statusPriority).map((status) => (
                                                <button
                                                    key={status}
                                                    className={`inv-stats-opener ${selectedStatus === status ? 'active' : ''}`}
                                                    onClick={() => setSelectedStatus(status as keyof typeof statusPriority)}
                                                >
                                                    {status.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase())}
                                                </button>
                                            ))}

                                        </div>

                                        <div className="inv-search-wrapper">
                                            <input
                                                type="text"
                                                placeholder="Search"
                                                value={orderSearchTerm}
                                                onChange={(e) => setOrderSearchTerm(e.target.value)}
                                                className="inv-search-input"
                                            />
                                        </div>

                                        <select
                                            value={selectedBatchFilter}
                                            onChange={(e) => {
                                                const val = e.target.value;

                                                if (val === "all") {
                                                    setSelectedBatchFilter("all");
                                                } else if (val === "none") {
                                                    setSelectedBatchFilter("none");
                                                } else {
                                                    setSelectedBatchFilter(Number(val));
                                                }
                                            }}
                                            className="inv-search-input inv-select-batch"
                                        >
                                            <option value="all">All</option>
                                            <option value="none">Batchless</option>

                                            {batches.map(b => (
                                                <option key={b.batchID} value={b.batchID}>
                                                    {b.batch_name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="mobile-table-header">
                                        <span className='mobile-table-header-span'>Forecast Orders</span>

                                        <div className="inv-search-wrapper">

                                            <button className='inv-order-btn'
                                                onClick={() => setShowTypeOrderPopup(true)}
                                            >
                                                <IoBagAdd className='inv-order-btn-bag-svg' />
                                                <span>New Order</span>
                                            </button>

                                            <button className='inv-order-btn order-btn2'
                                                onClick={() => (setActiveSection('StockAvailability'), setShowSectionPopup(true))}
                                            >
                                                <BsBoxFill className='inv-order-btn-box-svg' />
                                                <span>Stock Availability</span>
                                            </button>
                                        </div>

                                    </div>

                                </div>
                            )}

                            {!isMobile2 ? (
                                // DESKTOP TABLE HERE_ORDER
                                <div className='inventory-table-wrapper2'>

                                    <div className='inv-stat-filters-and-opener order-head'>

                                        <div className='inv-stat-filters-and-search'>

                                            <div className='inv-order-half1'>
                                                <div className="inv-stat-filters order-inv-header-ext">
                                                    {Object.keys(statusPriority).map((status) => (
                                                        <button
                                                            key={status}
                                                            className={`inv-stats-opener ${selectedStatus === status ? 'active' : ''}`}
                                                            onClick={() => setSelectedStatus(status as keyof typeof statusPriority)}
                                                        >
                                                            {status.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase())}
                                                        </button>
                                                    ))}

                                                </div>

                                                <input
                                                    type="text"
                                                    placeholder="Search"
                                                    value={orderSearchTerm}
                                                    onChange={(e) => setOrderSearchTerm(e.target.value)}
                                                    className="inv-search-input"
                                                />

                                                <select
                                                    value={selectedBatchFilter}
                                                    onChange={(e) => {
                                                        const val = e.target.value;

                                                        if (val === "all") {
                                                            setSelectedBatchFilter("all");
                                                        } else if (val === "none") {
                                                            setSelectedBatchFilter("none");
                                                        } else {
                                                            setSelectedBatchFilter(Number(val));
                                                        }
                                                    }}
                                                    className="inv-search-input inv-select-batch"
                                                >
                                                    <option value="all">All</option>
                                                    <option value="none">Batchless</option>

                                                    {batches.map(b => (
                                                        <option key={b.batchID} value={b.batchID}>
                                                            {b.batch_name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className="inv-search-wrapper">

                                                <button className='inv-order-btn'
                                                    onClick={() => setShowTypeOrderPopup(true)}
                                                >
                                                    <IoBagAdd className='inv-order-btn-bag-svg' />
                                                    <span>New Order</span>
                                                </button>

                                                <button className='inv-order-btn order-btn2'
                                                    onClick={() => (setActiveSection('StockAvailability'), setShowSectionPopup(true))}
                                                >
                                                    <BsBoxFill className='inv-order-btn-box-svg' />
                                                    <span>Stock Availability</span>
                                                </button>
                                            </div>

                                        </div>

                                    </div>

                                    <div className="inventory-table-scroll-container">

                                        <table className="inventory-table">
                                            <thead className="main-thead">
                                                <tr>
                                                    <th>Part</th>
                                                    <th>Unit Amount</th>

                                                    {/* ✅ Order Date column only for Requested (disabled), Ordered, or Arrived/Returned/Cancelled */}
                                                    {selectedStatus !== "Requested" && (
                                                        <th>Order Date</th>
                                                    )}

                                                    {/* ✅ Arrival Date column only for Arrived/Returned/Cancelled */}
                                                    {["Ordered"].includes(selectedStatus) && (
                                                        <th><div style={{ display: 'flex', gap: '0.3rem' }}>
                                                            <span style={{ whiteSpace: 'nowrap' }}>Expected By</span>
                                                            {showInfoTooltip("Day in which the item is supposed to arrive. Text will become red if expected arrival has passed and item still hasn't arrived.")}
                                                        </div></th>
                                                    )}

                                                    {/* ✅ Arrival Date column only for Arrived/Returned/Cancelled */}
                                                    {["Arrived", "Returned", "Cancelled"].includes(selectedStatus) && (
                                                        <th>Arrival Date</th>
                                                    )}

                                                    <th>Created by</th>
                                                    <th>
                                                        <div className="column-row-div">
                                                            {["Arrived", "Returned", "Cancelled"].includes(selectedStatus)
                                                                ? "Status"
                                                                : selectedStatus === "Requested"
                                                                    ? (
                                                                        <div style={{ display: 'flex', gap: '0.3rem' }}>
                                                                            <span style={{ whiteSpace: 'nowrap' }}>Confirm Order</span>
                                                                            {showInfoTooltip("Click to confirm if an order was placed with a store.")}
                                                                        </div>
                                                                    )
                                                                    : selectedStatus === "Ordered"
                                                                        ? (
                                                                            <div style={{ display: 'flex', gap: '0.3rem' }}>
                                                                                <span style={{ whiteSpace: 'nowrap' }}>Confirm Arrival</span>
                                                                                {showInfoTooltip("Click to confirm how many items from this order has arrived.")}
                                                                            </div>
                                                                        )
                                                                        : ""}
                                                        </div>
                                                    </th>
                                                    <th><div className="column-row-div">Order Again</div></th>
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {filteredForecasts.map((forecast) => {
                                                    const matchingPart = invInfo.find(p => p.partID === forecast.part);

                                                    return (
                                                        <tr
                                                            key={forecast.id}
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={() => {
                                                                const part = matchingPart || { partName: '' };
                                                                setForecastPartImage((part as InventoryItems)?.partPicture || null);
                                                                setEditingForecastId(forecast.id);
                                                                setForecastPartID(forecast.part);
                                                                setForecastPartName(part.partName);
                                                                setPartSearchTerm(part.partName);
                                                                setForecastAmount(forecast.expectedAmount);
                                                                setForecastCost(forecast.cost);
                                                                setForecastOrderedDate(
                                                                    forecast.orderedDate
                                                                        ? new Date(forecast.orderedDate).toISOString().substring(0, 10)
                                                                        : ''
                                                                );
                                                                setForecastArrivalDate(
                                                                    forecast.arrivalDate
                                                                        ? new Date(forecast.arrivalDate).toISOString().substring(0, 10)
                                                                        : ''
                                                                );
                                                                setForecastStatus(forecast.status);
                                                                setForecastStoreLinks(forecast.sourceLinks || '');
                                                                setForecastRequestor(`${first_name} ${last_name}`);
                                                                setForecastNotes(forecast.notes || '');
                                                                setForecastPriority(forecast.priority || 'Low');
                                                                setForecastLeadTime(forecast.lead_time || '');
                                                                setForecastIsPacket(!!forecast.is_packet);
                                                                setForecastNumberOfPackets(forecast.number_of_packets || 0);
                                                                setForecastAmountPerPacket(forecast.amount_per_packet || 0);
                                                                setForecastPackCost(forecast.pack_cost || 0);
                                                                setShowAddOrderPopup(true);

                                                                setForecastExpectedArrivalDate(
                                                                    forecast.expectedArrivalDate
                                                                        ? new Date(forecast.expectedArrivalDate).toISOString().substring(0, 10)
                                                                        : ''
                                                                );

                                                                setForecastBatchId(forecast.belongs_to_batch ?? null);
                                                            }}
                                                        >
                                                            <td>
                                                                <div className="part-info-cell">
                                                                    <img src={matchingPart?.partPicture ? matchingPart?.partPicture : fnnBlueLogo} alt={matchingPart?.partName} className="part-image" />
                                                                    <span>{renderPartNameWithPriority(forecast)}</span>
                                                                </div>
                                                            </td>
                                                            <td>{renderUnitAmountWithPackInfo(forecast)}</td>


                                                            {/* ✅ Show Order Date only when not in Requested */}
                                                            {selectedStatus !== "Requested" && (
                                                                <td>{formatDateForDisplay(forecast.orderedDate)}</td>
                                                            )}

                                                            {/* ✅ Show Expected Arrival Date only in Ordered */}
                                                            {["Ordered"].includes(selectedStatus) && (() => {
                                                                if (!forecast.expectedArrivalDate) {
                                                                    return <td>Unknown</td>;
                                                                }

                                                                const expectedDate = new Date(forecast.expectedArrivalDate);
                                                                const today = new Date();

                                                                // Normalize both to midnight UTC to fully ignore timezone and time portion
                                                                expectedDate.setUTCHours(0, 0, 0, 0);
                                                                today.setUTCHours(0, 0, 0, 0);

                                                                // Red only if expectedArrivalDate is strictly BEFORE today
                                                                const isLate = expectedDate.getTime() < today.getTime();

                                                                return (
                                                                    <td style={{ color: isLate ? 'red' : 'inherit' }}>
                                                                        {formatDateForDisplay(forecast.expectedArrivalDate)}
                                                                    </td>
                                                                );
                                                            })()}


                                                            {/* ✅ Show Arrival Date only in Arrived/Returned/Cancelled */}
                                                            {["Arrived", "Returned", "Cancelled"].includes(selectedStatus) && (
                                                                <td>{formatDateForDisplay(forecast.arrivalDate)}</td>
                                                            )}

                                                            <td>{forecast.requestor}</td>

                                                            <td>
                                                                <div className="column-row-div">
                                                                    {["Arrived", "Returned", "Cancelled"].includes(selectedStatus) ? (
                                                                        <span>{forecast.status}</span>
                                                                    ) : selectedStatus === "Requested" ? (
                                                                        <div
                                                                            className="delete-part-button mobile-confirm-part-button"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                setActiveForecast(forecast);
                                                                                setOrderedAmount(forecast.expectedAmount);

                                                                                // Calculate packets if packet-based
                                                                                if (forecast.is_packet && forecast.amount_per_packet) {
                                                                                    setOrderedPackets(
                                                                                        forecast.expectedAmount / forecast.amount_per_packet
                                                                                    );
                                                                                }

                                                                                setShowOrderItemPopup(true);
                                                                            }}
                                                                        >
                                                                            ✓
                                                                        </div>
                                                                    ) : selectedStatus === "Ordered" ? (
                                                                        <div
                                                                            className="delete-part-button mobile-confirm-part-button"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                setActiveForecast(forecast);
                                                                                setArrivalAmount(forecast.expectedAmount);
                                                                                setShowArrivalItemPopup(true);
                                                                            }}
                                                                        >
                                                                            ✓
                                                                        </div>
                                                                    ) : (
                                                                        ""
                                                                    )}
                                                                </div>
                                                            </td>

                                                            <td>
                                                                <div className="column-row-div">
                                                                    <div className="delete-part-button reorder-button"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            const part = matchingPart || { partName: '' };
                                                                            setForecastPartImage((part as InventoryItems)?.partPicture || null);
                                                                            setForecastPartID(forecast.part);
                                                                            setForecastPartName(part.partName);
                                                                            setPartSearchTerm(part.partName);
                                                                            setForecastAmount(forecast.expectedAmount);
                                                                            setForecastCost(forecast.cost);
                                                                            setForecastStatus("Requested");
                                                                            setForecastStoreLinks(forecast.sourceLinks || '');
                                                                            setForecastRequestor(`${first_name} ${last_name}`);
                                                                            setForecastNotes(forecast.notes || '');
                                                                            setForecastPriority(forecast.priority || 'Low');
                                                                            setForecastLeadTime(forecast.lead_time || '');
                                                                            setForecastIsPacket(!!forecast.is_packet);
                                                                            setForecastNumberOfPackets(forecast.number_of_packets || 0);
                                                                            setForecastAmountPerPacket(forecast.amount_per_packet || 0);
                                                                            setForecastPackCost(forecast.pack_cost || 0);
                                                                            setForecastBatchId(null);
                                                                            setShowAddOrderPopup(true);
                                                                        }}>
                                                                        <MdOutlineReplay />
                                                                    </div>
                                                                </div>
                                                            </td>

                                                            {/* <td>
                                                            <div className="column-row-div">
                                                                <div
                                                                    className="delete-part-button"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setForecastToDeleteId(forecast.id);
                                                                        setShowDeleteForecastPopup(true);
                                                                    }}
                                                                >
                                                                    x
                                                                </div>
                                                            </div>
                                                        </td> */}
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>

                                    </div>

                                </div>
                            ) : (
                                // MOBILE TABLE
                                <div className="mobile-forecast-list">

                                    {filteredForecasts.map((forecast) => {
                                        const matchingPart = invInfo.find(p => p.partID === forecast.part);
                                        const isExpanded = expandedForecastId === forecast.id;

                                        return (
                                            <React.Fragment key={forecast.id}>
                                                <div
                                                    className="mobile-part-row2"
                                                    onClick={() => {
                                                        setExpandedForecastId(expandedForecastId === forecast.id ? null : forecast.id);
                                                    }}
                                                >
                                                    <div className="part-info-cell">
                                                        <img src={matchingPart?.partPicture ? matchingPart?.partPicture : fnnBlueLogo} alt={matchingPart?.partName} className="part-image" />
                                                        <span>{renderPartNameWithPriority(forecast)}</span>
                                                    </div>
                                                </div>

                                                {isExpanded && (
                                                    <div
                                                        className="mobile-details-row2"
                                                        onClick={() => {
                                                            // your form prefill logic here
                                                            const part = matchingPart || { partName: '' };
                                                            setForecastPartImage((part as InventoryItems)?.partPicture || null);
                                                            setEditingForecastId(forecast.id);
                                                            setForecastPartID(forecast.part);
                                                            setForecastPartName(part.partName);
                                                            setPartSearchTerm(part.partName);
                                                            setForecastAmount(forecast.expectedAmount);
                                                            setForecastCost(forecast.cost);
                                                            setForecastOrderedDate(
                                                                forecast.orderedDate ? new Date(forecast.orderedDate).toISOString().substring(0, 10) : ''
                                                            );
                                                            setForecastArrivalDate(
                                                                forecast.arrivalDate ? new Date(forecast.arrivalDate).toISOString().substring(0, 10) : ''
                                                            );
                                                            setForecastStatus(forecast.status);
                                                            setForecastStoreLinks(forecast.sourceLinks || '');
                                                            setForecastRequestor(`${first_name} ${last_name}`);
                                                            setForecastNotes(forecast.notes || '');
                                                            setForecastPriority(forecast.priority || 'Low');
                                                            setForecastLeadTime(forecast.lead_time || '');
                                                            setForecastIsPacket(!!forecast.is_packet);
                                                            setForecastNumberOfPackets(forecast.number_of_packets || 0);
                                                            setForecastAmountPerPacket(forecast.amount_per_packet || 0);
                                                            setForecastPackCost(forecast.pack_cost || 0);
                                                            setShowAddOrderPopup(true);

                                                            setForecastExpectedArrivalDate(
                                                                forecast.expectedArrivalDate
                                                                    ? new Date(forecast.expectedArrivalDate).toISOString().substring(0, 10)
                                                                    : ''
                                                            );

                                                            setForecastBatchId(forecast.belongs_to_batch ?? null);
                                                        }}
                                                    >
                                                        <div className="mobile-detail-group">
                                                            <strong>Unit Amount:</strong> {renderUnitAmountWithPackInfo(forecast)}
                                                        </div>

                                                        {/* Ordered Date — hide when Requested */}
                                                        {selectedStatus !== "Requested" && (
                                                            <div className="mobile-detail-group">
                                                                <strong>Ordered Date:</strong>{" "}
                                                                {forecast.orderedDate ? formatDateForDisplay(forecast.orderedDate) : 'Undefined'}
                                                            </div>
                                                        )}

                                                        {/* Expected Arrival Date — hide when Requested */}
                                                        {selectedStatus === "Ordered" && (
                                                            <div className="mobile-detail-group">
                                                                <strong>Expected By:</strong>{" "}

                                                                {["Ordered"].includes(selectedStatus) && (() => {
                                                                    if (!forecast.expectedArrivalDate) {
                                                                        return <td>Unknown</td>;
                                                                    }

                                                                    const expectedDate = new Date(forecast.expectedArrivalDate);
                                                                    const today = new Date();

                                                                    // Normalize both to midnight UTC to fully ignore timezone and time portion
                                                                    expectedDate.setUTCHours(0, 0, 0, 0);
                                                                    today.setUTCHours(0, 0, 0, 0);

                                                                    // Red only if expectedArrivalDate is strictly BEFORE today
                                                                    const isLate = expectedDate.getTime() < today.getTime() && forecast.alert_generated === false;

                                                                    return (
                                                                        <td style={{ color: isLate ? 'red' : 'inherit' }}>
                                                                            {formatDateForDisplay(forecast.expectedArrivalDate)}
                                                                        </td>
                                                                    );
                                                                })()}
                                                            </div>
                                                        )}

                                                        {/* Arrival Date — hide when Requested or Ordered */}
                                                        {selectedStatus !== "Requested" && selectedStatus !== "Ordered" && (
                                                            <div className="mobile-detail-group">
                                                                <strong>Arrival Date:</strong>{" "}
                                                                {forecast.arrivalDate ? formatDateForDisplay(forecast.arrivalDate) : 'Unknown'}
                                                            </div>
                                                        )}

                                                        <div className="mobile-detail-group">
                                                            <strong>
                                                                {["Arrived", "Returned", "Cancelled"].includes(selectedStatus)
                                                                    ? "Status"
                                                                    : selectedStatus === "Requested"
                                                                        ? "Confirm Order:"
                                                                        : selectedStatus === "Ordered"
                                                                            ? "Confirm Arrival"
                                                                            : ""}
                                                            </strong>

                                                            {["Arrived", "Returned", "Cancelled"].includes(selectedStatus) ? (
                                                                <span>{forecast.status}</span>
                                                            ) : selectedStatus === "Requested" ? (
                                                                <div
                                                                    className="delete-part-button mobile-confirm-part-button"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setActiveForecast(forecast);
                                                                        setOrderedAmount(forecast.expectedAmount);
                                                                        setExpandedForecastId(null);

                                                                        // Calculate packets if packet-based
                                                                        if (forecast.is_packet && forecast.amount_per_packet) {
                                                                            setOrderedPackets(
                                                                                forecast.expectedAmount / forecast.amount_per_packet
                                                                            );
                                                                        }

                                                                        setShowOrderItemPopup(true);
                                                                    }}
                                                                >
                                                                    ✓
                                                                </div>
                                                            ) : selectedStatus === "Ordered" ? (
                                                                <div
                                                                    className="delete-part-button mobile-confirm-part-button"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setActiveForecast(forecast);
                                                                        setArrivalAmount(forecast.expectedAmount);
                                                                        setShowArrivalItemPopup(true);
                                                                    }}
                                                                >
                                                                    ✓
                                                                </div>
                                                            ) : (
                                                                ""
                                                            )}
                                                        </div>

                                                        <div className="mobile-detail-group">
                                                            <strong>By:</strong> {forecast.requestor}
                                                        </div>


                                                        <div className="mobile-detail-delete">

                                                            <div className="delete-part-button reorder-button"
                                                                style={{ marginRight: '1rem' }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    const part = matchingPart || { partName: '' };
                                                                    setForecastPartImage((part as InventoryItems)?.partPicture || null);
                                                                    setForecastPartID(forecast.part);
                                                                    setForecastPartName(part.partName);
                                                                    setPartSearchTerm(part.partName);
                                                                    setForecastAmount(forecast.expectedAmount);
                                                                    setForecastCost(forecast.cost);
                                                                    setForecastStatus("Requested");
                                                                    setForecastStoreLinks(forecast.sourceLinks || '');
                                                                    setForecastRequestor(`${first_name} ${last_name}`);
                                                                    setForecastNotes(forecast.notes || '');
                                                                    setForecastPriority(forecast.priority || 'Low');
                                                                    setForecastLeadTime(forecast.lead_time || '');
                                                                    setForecastIsPacket(!!forecast.is_packet);
                                                                    setForecastNumberOfPackets(forecast.number_of_packets || 0);
                                                                    setForecastAmountPerPacket(forecast.amount_per_packet || 0);
                                                                    setForecastPackCost(forecast.pack_cost || 0);
                                                                    setForecastBatchId(null);
                                                                    setShowAddOrderPopup(true);
                                                                }}>
                                                                <MdOutlineReplay />
                                                            </div>

                                                            <div
                                                                className="delete-part-button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setForecastToDeleteId(forecast.id);
                                                                    setShowDeleteForecastPopup(true);
                                                                }}
                                                            >
                                                                x
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                            </React.Fragment>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                    </div>
                }

                {/* Version Management */}
                {activeInvTab === "structure" && (
                    <div className="inventory-section-wrapper inventory-table-wrapper2">

                        <div className="inv-stat-filters-and-opener order-head header-for-built">
                            {/* Switch between Detectors / Sub-Assemblies */}
                            <div className="inv-stat-filters order-inv-header-ext">
                                {["Sub-Assemblies", "Detectors"].map((type) => (
                                    <button
                                        key={type}
                                        className={`inv-stats-opener ${selectedManage === type ? 'active' : ''}`}
                                        onClick={() => setSelectedManage(type)}
                                    >
                                        {type}
                                    </button>
                                ))}
                            </div>

                            <button className='inv-order-btn'
                                onClick={() => selectedManage === "Detectors" ? setShowAddDetectorPopup(true) : setShowAddSubAssemblyPopup(true)}
                            >
                                <IoBagAdd className='inv-order-btn-bag-svg' />
                                <span>{selectedManage === "Detectors" ? "New Detector" : "New Sub-Assembly"}</span>
                            </button>
                        </div>

                        <div className="inventory-table-scroll-container">
                            <table className="inventory-table">
                                <thead>
                                    <tr>
                                        <th>{selectedManage === "Detectors" ? "Detectors" : "Sub-Assemblies"}</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {selectedManage === "Detectors"
                                        ? detectors.map((det) => (
                                            <tr key={det.id}>
                                                <td>
                                                    {det.name} - v{det.version}
                                                </td>
                                                <td>
                                                    <div className="column-row-div2" style={{ display: 'flex', gap: '0.5rem' }}>
                                                        {/* View Button */}
                                                        <button className='inv-order-btn order-btn2 order-btn2-ext'
                                                            onClick={() => {
                                                                setSelectedDetector2(det);
                                                                setShowDetBOMPopup(true);
                                                            }}
                                                        >
                                                            <span>View</span>
                                                        </button>

                                                        {/* Edit Button */}
                                                        <button className='inv-order-btn manage-edit-btn'
                                                            onClick={() => {
                                                                setEditingDetectorId(det.id);
                                                                setNewDetectorName(det.name);
                                                                setNewDetectorVersion(det.version);
                                                                setNewDetectorSubAssemblies(
                                                                    det.subassemblies.map((s: any) => ({
                                                                        subassembly:
                                                                            typeof s.subassembly === 'number' ? s.subassembly : s.subassembly.id,
                                                                        quantity_required: s.quantity_required,
                                                                    }))
                                                                );
                                                                setShowAddDetectorPopup(true);
                                                            }}
                                                        >
                                                            <LuPencil />
                                                            <span>Edit</span>
                                                        </button>

                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                        : subAssemblies.map((sub) => (
                                            <tr key={sub.id}>
                                                <td>
                                                    <div className="part-info-cell">
                                                        <img
                                                            src={sub.subPicture ? sub.subPicture : fnnBlueLogo}
                                                            alt={sub.name}
                                                            className="part-image"
                                                        />
                                                        {sub.subSOP ? (
                                                            <a
                                                                href={sub.subSOP}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="clickable-inv-option"
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                {sub.name}
                                                            </a>
                                                        ) : (
                                                            <span className="unclickable-inv-option">{truncateText(sub.name, 40)}</span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="column-row-div2" style={{ display: 'flex', gap: '0.5rem' }}>
                                                        {/* View Button */}
                                                        <button className='inv-order-btn order-btn2 order-btn2-ext'
                                                            onClick={() => {
                                                                setSelectedSubAssembly(sub);
                                                                setShowBOMPopup(true);
                                                            }}
                                                        >
                                                            <span>View</span>
                                                        </button>

                                                        {/* Edit Button */}
                                                        <button className='inv-order-btn manage-edit-btn'
                                                            onClick={() => {
                                                                setEditingSubAssemblyId(sub.id);
                                                                setNewSubAssemblyName(sub.name);
                                                                setNewSubAssemblyPic(sub.subPicture);
                                                                setNewSubAssemblySop(sub.subSOP);
                                                                setNewSubAssemblyParts(
                                                                    sub.parts.map((p) => ({
                                                                        part: p.part,
                                                                        quantity_required: p.quantity_required,
                                                                    }))
                                                                );
                                                                setShowAddSubAssemblyPopup(true);
                                                            }}
                                                        >
                                                            <LuPencil />
                                                            <span>Edit</span>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                </tbody>
                            </table>
                        </div>

                    </div>
                )}

                {/* Build Units */}
                {activeInvTab === "build" && (

                    <div className="inventory-section-wrapper inv-build-and-assemble-cards">

                        {/* Option to build new sub-assembly */}
                        <div className='inv-build-and-assemble-card'>
                            <div className='inv-build-and-assemble-card-header'>
                                <PiPuzzlePieceFill />
                            </div>

                            <div className='inv-build-and-assemble-card-text'>
                                <h1>Build a New Sub-Assembly</h1>
                                <p>
                                    Select a sub-assembly version to build. The required components will be automatically
                                    consumed from inventory based on its bill of materials.
                                </p>
                            </div>

                            <button
                                className="confirm-btn"
                                onClick={() => {
                                    setActiveSection("AddConsumeSubAssembly");
                                    setShowSectionPopup(true);
                                }}
                            >Select</button>
                        </div>

                        {/* Option to assemble a detector based on the built sub-assemblies */}
                        <div className='inv-build-and-assemble-card'>
                            <div className='inv-build-and-assemble-card-header'>
                                <FaBoltLightning />
                            </div>

                            <div className='inv-build-and-assemble-card-text'>
                                <h1>Assemble Detector From Sub-Assemblies</h1>
                                <p>
                                    Combine built sub-assemblies to create a detector. Selected sub-assemblies will be linked
                                    and marked as installed under the assembled detector unit.
                                </p>
                            </div>

                            <button
                                className="confirm-btn"
                                onClick={() => {
                                    setActiveSection("AssembleDetector");
                                    setAssembleDetectorId(null);
                                    setAssembleSelectedSubAssemblies({});
                                    setAssembleMacAddress("");
                                    setAssembleDetCode("");
                                    setShowSectionPopup(true);
                                }}
                            >Select</button>
                        </div>

                        {/* Option to register existing detectors without consuming anything from inventory*/}
                        <div className='inv-build-and-assemble-card'>
                            <div className='inv-build-and-assemble-card-header'>
                                <MdPlaylistAddCheckCircle />
                            </div>

                            <div className='inv-build-and-assemble-card-text'>
                                <h1>Register Existing Detector</h1>
                                <p>
                                    Add an already-built detector by entering its MAC address and selecting its version.
                                    Sub-assemblies are automatically generated and linked — no manual selection needed and no
                                    inventory is consumed.
                                </p>
                            </div>

                            <button
                                className="confirm-btn"
                                onClick={() => {
                                    setActiveSection("RegisterExistingDetector");
                                    setAssembleDetectorId(null);
                                    setAssembleMacAddress("");
                                    setAssembleDetCode("");
                                    setShowSectionPopup(true);
                                }}
                            >Select</button>
                        </div>

                    </div>

                )}

                {/* Built Units */}
                {activeInvTab === "units" && (
                    <div className="inventory-section-wrapper inventory-table-wrapper2">

                        <div className="inv-stat-filters-and-opener order-head header-for-built">
                            {/* Switch between Detectors / Sub-Assemblies */}
                            <div className="inv-stat-filters order-inv-header-ext">
                                {["Sub-Assemblies", "Detectors"].map((type) => (
                                    <button
                                        key={type}
                                        className={`inv-stats-opener ${selectedUnits === type ? 'active' : ''}`}
                                        onClick={() => setSelectedUnits(type)}
                                    >
                                        {type}
                                    </button>
                                ))}
                            </div>

                            {/* --- Status Filters --- */}
                            <div className="inv-stat-filters filter-for-order">
                                {selectedUnits === "Sub-Assemblies" &&
                                    ["available", "installed", "faulty", "discarded"].map((status) => (
                                        <button
                                            key={status}
                                            className={`inv-stats-opener ${selectedUnitStatus === status ? "active" : ""}`}
                                            onClick={() => setSelectedUnitStatus(status)}
                                        >
                                            {status.charAt(0).toUpperCase() + status.slice(1)}
                                        </button>
                                    ))
                                }
                                {selectedUnits === "Detectors" &&
                                    ["in-stock", "deployed", "testing", "maintenance"].map((status) => (
                                        <button
                                            key={status}
                                            className={`inv-stats-opener ${selectedDetectorStatus === status ? "active" : ""}`}
                                            onClick={() => setSelectedDetectorStatus(status)}
                                        >
                                            {status.charAt(0).toUpperCase() + status.slice(1)}
                                        </button>
                                    ))
                                }
                            </div>
                        </div>


                        <div className="inventory-table-scroll-container">

                            {/* ===================== SUB-ASSEMBLIES ===================== */}
                            {selectedUnits === "Sub-Assemblies" && (() => {
                                const filteredSubs = builtSubAssemblies.filter(bsa => bsa.status === selectedUnitStatus);
                                if (!filteredSubs.length)
                                    return <p className="no-items-msg">No {selectedUnitStatus} sub-assemblies found.</p>;

                                return subAssemblies
                                    .filter(sa => filteredSubs.some(bsa => bsa.subassembly === sa.id))
                                    .map(sa => {
                                        const subRows = filteredSubs.filter(bsa => bsa.subassembly === sa.id);
                                        return (
                                            <div key={sa.id} className={`slip ${openSlips[String(sa.id)] ? 'open' : ''}`}>

                                                <div className="slip-header" onClick={() => toggleSlip(sa.id)}>
                                                    {sa.name}

                                                    <div className='slip-header-right'>
                                                        <span className="slip-count-badge">{subRows.length}</span>
                                                        <span className="slip-header-arrow">▼</span>
                                                    </div>
                                                </div>

                                                {openSlips[String(sa.id)] && (
                                                    <div className="slip-content">
                                                        {!isMobile4 && (
                                                            <table className="inventory-table" style={{ tableLayout: 'fixed', width: '100%' }}>
                                                                <thead>
                                                                    <tr>
                                                                        <th>ID</th>
                                                                        <th>Built On</th>
                                                                        <th>Built By</th>
                                                                        <th>Batch</th>
                                                                        <th>Status</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {subRows.map(bsa => (
                                                                        <tr key={bsa.id}>
                                                                            <td>{bsa.id}</td>
                                                                            <td>{new Date(bsa.built_at).toLocaleString("en-US", { timeZone: "America/New_York" })}</td>
                                                                            <td>{getFnnUserName(bsa.built_by)}</td>
                                                                            <td>{bsa.belongs_to_batch ? bsa.batch_name : "None"}</td>
                                                                            <td>
                                                                                <select
                                                                                    className="form-select select-built"
                                                                                    value={bsa.status}
                                                                                    onChange={(e) => {
                                                                                        const newStatus = e.target.value;
                                                                                        if (newStatus === "installed") {
                                                                                            handleUnitStatusChange(bsa.id, newStatus);
                                                                                        } else if (newStatus === "discarded") {
                                                                                            setDiscardTargetId(String(bsa.id));
                                                                                            setDiscardSubAssemblyTypeId(String(bsa.subassembly));
                                                                                            setShowDiscardPopup(true);
                                                                                        } else {
                                                                                            setSubAssemblyToConfirmId(bsa.id);
                                                                                            setStatusToConfirm(newStatus);
                                                                                            setShowStatusConfirmPopup(true);
                                                                                        }
                                                                                    }}
                                                                                >
                                                                                    {["available", "installed", "faulty", "discarded"].map(statusOption => (
                                                                                        <option key={statusOption} value={statusOption}>
                                                                                            {statusOption.charAt(0).toUpperCase() + statusOption.slice(1)}
                                                                                        </option>
                                                                                    ))}
                                                                                </select>
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        )}
                                                        {isMobile4 && (
                                                            <div className="warehouse-mobile-list">
                                                                {subRows.map((bsa) => (
                                                                    <div key={bsa.id} className="warehouse-card">
                                                                        <div className="warehouse-row">
                                                                            <span className="warehouse-label">ID:</span>
                                                                            <span className="warehouse-value">{bsa.id}</span>
                                                                        </div>
                                                                        <div className="warehouse-row">
                                                                            <span className="warehouse-label">Built On:</span>
                                                                            <span className="warehouse-value">
                                                                                {new Date(bsa.built_at).toLocaleString("en-US", { timeZone: "America/New_York" })}
                                                                            </span>
                                                                        </div>
                                                                        <div className="warehouse-row">
                                                                            <span className="warehouse-label">Built By:</span>
                                                                            <span className="warehouse-value">{getFnnUserName(bsa.built_by)}</span>
                                                                        </div>

                                                                        <div className="warehouse-row">
                                                                            <span className="warehouse-label">Batch:</span>
                                                                            <span className="warehouse-value">{bsa.belongs_to_batch ? bsa.batch_name : "None"}</span>
                                                                        </div>

                                                                        <div className="warehouse-row">
                                                                            <span className="warehouse-label">Status:</span>
                                                                            <select
                                                                                className="form-select select-built warehouse-select"
                                                                                value={bsa.status}
                                                                                onChange={(e) => {
                                                                                    const newStatus = e.target.value;
                                                                                    if (newStatus === "installed") {
                                                                                        handleUnitStatusChange(bsa.id, newStatus);
                                                                                    } else if (newStatus === "discarded") {
                                                                                        setDiscardTargetId(String(bsa.id));
                                                                                        setDiscardSubAssemblyTypeId(String(bsa.subassembly));
                                                                                        setShowDiscardPopup(true);
                                                                                    } else {
                                                                                        setSubAssemblyToConfirmId(bsa.id);
                                                                                        setStatusToConfirm(newStatus);
                                                                                        setShowStatusConfirmPopup(true);
                                                                                    }
                                                                                }}
                                                                            >
                                                                                {["available", "installed", "faulty", "discarded"].map((statusOption) => (
                                                                                    <option key={statusOption} value={statusOption}>
                                                                                        {statusOption.charAt(0).toUpperCase() + statusOption.slice(1)}
                                                                                    </option>
                                                                                ))}
                                                                            </select>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    });
                            })()}

                            {/* ===================== DETECTORS ===================== */}
                            {selectedUnits === "Detectors" && (() => {
                                const filteredDetectors = builtDetectors.filter(
                                    det => det.status.toLowerCase() === selectedDetectorStatus
                                );
                                if (!filteredDetectors.length)
                                    return <p className="no-items-msg">No {selectedDetectorStatus} detectors found.</p>;

                                return filteredDetectors.map(det => {
                                    const subRows = builtSubAssemblies.filter(bsa => bsa.used_by === det.mac_address);
                                    const detectorInfo = detectors.find(d => d.id === det.detector);
                                    const detectorName = detectorInfo
                                        ? `${detectorInfo.name} v${detectorInfo.version}`
                                        : `Detector ID ${det.detector}`;

                                    return (
                                        <div
                                            key={det.mac_address}
                                            ref={(el) => (slipRefs.current[det.mac_address] = el)}
                                            className={`slip ${openSlips[det.mac_address] ? 'open' : ''}`}
                                        >
                                            <div className="slip-header" onClick={() => toggleSlip(det.mac_address)}>
                                                <span>{det.code ? `${det.code} - ` : ""}{det.mac_address}</span>

                                                <div className='slip-header-right'>
                                                    <span className={`detector-status ${(det.belongs_to_batch && det.batch_name) ? det.batch_name.toLowerCase() : "none"}`}>
                                                        {det.belongs_to_batch ? det.batch_name : "No Batch"}
                                                    </span>

                                                    <span className="slip-header-arrow">▼</span>
                                                </div>


                                            </div>

                                            {openSlips[det.mac_address] && (
                                                <div className="slip-content">
                                                    {/* Detector build info header */}
                                                    <div className="detector-build-info">
                                                        <div className="detector-build-info-item">
                                                            <strong>Assembled On:</strong> {new Date(det.built_at).toLocaleString("en-US", { timeZone: "America/New_York" })}
                                                        </div>
                                                        <div className="detector-build-info-item">
                                                            <strong>Assembled By:</strong> {getFnnUserName(det.assembled_by)}
                                                        </div>

                                                        <div className="detector-build-info-item">
                                                            <strong
                                                                className='warehouse-clickable-sub'
                                                                onClick={() => {
                                                                    setTimeout(() => {
                                                                        setSelectedDetector2(detectorInfo ? detectorInfo : null);
                                                                        setShowDetBOMPopup(true);
                                                                    }, 50);
                                                                }}

                                                            > Detector Version:</strong>

                                                            <select
                                                                className="form-select select-built"
                                                                value={det.detector ?? ""}
                                                                onChange={(e) => {
                                                                    const newId = e.target.value ? Number(e.target.value) : null;
                                                                    if (!newId) return;

                                                                    const analysis = analyzeVersionChange(det, newId);

                                                                    if (!analysis) return;

                                                                    // If nothing changes → proceed normally
                                                                    if (analysis.incompatible.length === 0 && analysis.missing.length === 0) {
                                                                        handleDetectorVersionChange(det, newId);
                                                                        return;
                                                                    }

                                                                    // Otherwise open popup
                                                                    setPendingVersionChange({
                                                                        det,
                                                                        newDetectorId: newId,
                                                                        ...analysis
                                                                    });

                                                                    setShowVersionChangePopup(true);
                                                                }}
                                                            >
                                                                <option value="">-- Select Detector --</option>

                                                                {detectors.map(d => (
                                                                    <option key={d.id} value={d.id}>
                                                                        {d.name} v{d.version}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>

                                                        {/* NEW — MAC address */}
                                                        <div className="detector-build-info-item">
                                                            <strong>MAC:</strong>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                value={editMacs[det.mac_address] ?? det.mac_address}
                                                                onChange={(e) =>
                                                                    setEditMacs((prev) => ({ ...prev, [det.mac_address]: e.target.value }))
                                                                }
                                                                onKeyDown={(e) => {
                                                                    if (e.key === "Enter") {
                                                                        handleMacUpdate(det);
                                                                    }
                                                                }}
                                                                placeholder="Enter MAC address"
                                                            />
                                                        </div>
                                                        <div className="detector-build-info-item">
                                                            <strong>Code:</strong>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                value={editCodes[det.mac_address] ?? det.code ?? ""}
                                                                onChange={(e) =>
                                                                    setEditCodes((prev) => ({ ...prev, [det.mac_address]: e.target.value }))
                                                                }
                                                                onKeyDown={(e) => {
                                                                    if (e.key === "Enter") {
                                                                        handleCodeUpdate(det);
                                                                    }
                                                                }}
                                                                placeholder="Enter code"
                                                            />
                                                        </div>
                                                        <div className="detector-build-info-item">
                                                            <strong>Status:</strong>
                                                            <select
                                                                className="form-select select-built"
                                                                value={det.status}
                                                                style={{ textTransform: "capitalize" }}
                                                                onChange={(e) => handleDetectorStatusChange(det.mac_address, e.target.value)}
                                                            >
                                                                {["in-stock", "deployed", "testing", "maintenance"].map(statusOption => (
                                                                    <option key={statusOption} value={statusOption}>
                                                                        {statusOption.charAt(0).toUpperCase() + statusOption.slice(1)}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    </div>

                                                    {!isMobile4 && (
                                                        <table className="inventory-table" style={{ tableLayout: 'fixed', width: '100%' }}>
                                                            <thead>
                                                                <tr>
                                                                    <th>ID</th>
                                                                    <th>Sub-Assembly</th>
                                                                    <th>Built On</th>
                                                                    <th>Built By</th>
                                                                    <th>Batch</th>
                                                                    <th>Status</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {subRows.map(bsa => (
                                                                    <tr key={bsa.id}>
                                                                        <td>{bsa.id}</td>
                                                                        <td
                                                                            className='warehouse-clickable-sub'
                                                                            onClick={() => {
                                                                                setTimeout(() => {
                                                                                    const selected = subAssemblies.find(s => s.id === bsa.subassembly);
                                                                                    setSelectedSubAssembly(selected || null);
                                                                                    setShowBOMPopup(true);
                                                                                }, 50);
                                                                            }}
                                                                        >
                                                                            {subAssemblies.find(s => s.id === bsa.subassembly)?.name || bsa.subassembly}
                                                                        </td>
                                                                        <td>{new Date(bsa.built_at).toLocaleString("en-US", { timeZone: "America/New_York" })}</td>
                                                                        <td>{getFnnUserName(bsa.built_by)}</td>
                                                                        <td>{bsa.belongs_to_batch ? bsa.batch_name : "None"}</td>
                                                                        <td>
                                                                            <select
                                                                                className="form-select select-built"
                                                                                value={bsa.status}
                                                                                onChange={(e) => {
                                                                                    const newStatus = e.target.value;
                                                                                    if (newStatus === "installed") {
                                                                                        handleUnitStatusChange(bsa.id, newStatus);
                                                                                    } else {
                                                                                        setSubAssemblyToConfirmId(bsa.id);
                                                                                        setStatusToConfirm(newStatus);
                                                                                        setShowStatusConfirmPopup(true);
                                                                                    }
                                                                                }}
                                                                            >
                                                                                {["available", "installed", "faulty", "discarded"].map(statusOption => (
                                                                                    <option key={statusOption} value={statusOption}>
                                                                                        {statusOption.charAt(0).toUpperCase() + statusOption.slice(1)}
                                                                                    </option>
                                                                                ))}
                                                                            </select>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    )}

                                                    {isMobile4 && (
                                                        <div className="warehouse-mobile-list">
                                                            {subRows.map((bsa) => (
                                                                <div key={bsa.id} className="warehouse-card">
                                                                    <div className="warehouse-row">
                                                                        <span className="warehouse-label">ID:</span>
                                                                        <span className="warehouse-value">{bsa.id}</span>
                                                                    </div>
                                                                    <div className="warehouse-row">
                                                                        <span className="warehouse-label">Sub-Assembly:</span>
                                                                        <span
                                                                            className="warehouse-value warehouse-clickable-sub"
                                                                            onClick={() => {
                                                                                setOpenSlips({});
                                                                                setShowSectionPopup(false);
                                                                                setTimeout(() => {
                                                                                    const selected = subAssemblies.find(s => s.id === bsa.subassembly);
                                                                                    setSelectedSubAssembly(selected || null);
                                                                                    setFlowOrTableSub("flow");
                                                                                    setActiveSection('SubAssemblyBOM');
                                                                                    setShowSectionPopup(true);
                                                                                }, 50);
                                                                            }}
                                                                        >
                                                                            {subAssemblies.find(s => s.id === bsa.subassembly)?.name || bsa.subassembly}
                                                                        </span>
                                                                    </div>
                                                                    <div className="warehouse-row">
                                                                        <span className="warehouse-label">Built On:</span>
                                                                        <span className="warehouse-value">
                                                                            {new Date(bsa.built_at).toLocaleString("en-US", { timeZone: "America/New_York" })}
                                                                        </span>
                                                                    </div>
                                                                    <div className="warehouse-row">
                                                                        <span className="warehouse-label">Built By:</span>
                                                                        <span className="warehouse-value">{getFnnUserName(bsa.built_by)}</span>
                                                                    </div>

                                                                    <div className="warehouse-row">
                                                                        <span className="warehouse-label">Batch</span>
                                                                        <span className="warehouse-value">{bsa.belongs_to_batch ? bsa.batch_name : "None"}</span>
                                                                    </div>

                                                                    <div className="warehouse-row">
                                                                        <span className="warehouse-label">Status:</span>
                                                                        <select
                                                                            className="form-select select-built warehouse-select"
                                                                            value={bsa.status}
                                                                            onChange={(e) => {
                                                                                const newStatus = e.target.value;
                                                                                if (newStatus === "installed") {
                                                                                    handleUnitStatusChange(bsa.id, newStatus);
                                                                                } else {
                                                                                    setSubAssemblyToConfirmId(bsa.id);
                                                                                    setStatusToConfirm(newStatus);
                                                                                    setShowStatusConfirmPopup(true);
                                                                                }
                                                                            }}
                                                                        >
                                                                            {["available", "installed", "faulty", "discarded"].map((statusOption) => (
                                                                                <option key={statusOption} value={statusOption}>
                                                                                    {statusOption.charAt(0).toUpperCase() + statusOption.slice(1)}
                                                                                </option>
                                                                            ))}
                                                                        </select>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                });
                            })()}

                        </div>


                    </div>
                )}

            </div>

            {/* Built Units Version Replacement Popup */}
            {showVersionChangePopup && pendingVersionChange && (
                <div className="add-item-popup-overlay">
                    <div className="built-det-for-sub-content">

                        <h3>Version Change Impact</h3>

                        {/* ❌ Incompatible */}
                        {pendingVersionChange.incompatible.length > 0 && (
                            <>
                                <p><strong>The following sub-assemblies will be removed:</strong></p>
                                <ul>
                                    {pendingVersionChange.incompatible.map((sub: any) => (
                                        <li key={sub.id}>
                                            {subAssemblies.find(s => s.id === sub.subassembly)?.name} (ID {sub.id})
                                        </li>
                                    ))}
                                </ul>
                            </>
                        )}

                        {/* ➕ Missing */}
                        {pendingVersionChange.missing.length > 0 && (
                            <>
                                <p><strong>Select replacements:</strong></p>

                                {pendingVersionChange.missing.map((subId: number) => {
                                    const availableSubs = builtSubAssemblies.filter(
                                        bsa =>
                                            bsa.subassembly === subId &&
                                            bsa.status === "available"
                                    );

                                    return (
                                        <div key={subId} className='version-change-inner'>
                                            <p>
                                                {subAssemblies.find(s => s.id === subId)?.name}
                                            </p>

                                            {availableSubs.length > 0 ? (
                                                availableSubs.map(sub => (
                                                    <div
                                                        key={sub.id}
                                                        className={`subassembly-option${replacementSelections[subId] === sub.id ? " selected" : ""}`}
                                                        onClick={() => {
                                                            setReplacementSelections(prev => ({
                                                                ...prev,
                                                                [subId]: sub.id
                                                            }));
                                                        }}
                                                    >
                                                        ID {sub.id}
                                                    </div>
                                                ))
                                            ) : (
                                                <>
                                                    <p className="no-options">
                                                        No available units for{" "}
                                                        <strong>{subAssemblies.find(s => s.id === subId)?.name}</strong>.
                                                        You need to build one to continue.
                                                    </p>

                                                    <button
                                                        className="confirm-requests-button"
                                                        onClick={() => {
                                                            setShowVersionChangePopup(false);
                                                            setActiveSection("AddConsumeSubAssembly");
                                                            setShowSectionPopup(true);
                                                        }}
                                                    >
                                                        Build Sub-Assembly
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    );
                                })}
                            </>
                        )}

                        <div className="hh-duo hh-actions" style={{ paddingTop: '1.5rem' }}>
                            <button
                                className="cancel-btn"
                                onClick={() => {
                                    setShowVersionChangePopup(false);
                                    setPendingVersionChange(null);
                                    setReplacementSelections({});
                                }}
                            >
                                Cancel
                            </button>

                            <button
                                className="confirm-btn"
                                onClick={async () => {
                                    const { det, newDetectorId, missing, incompatible } = pendingVersionChange;

                                    // Ensure all replacements selected if needed
                                    const missingSelections = missing.filter(
                                        (id: number) => !replacementSelections[id]
                                    );

                                    if (missingSelections.length > 0) {
                                        toast.error("Please select all required replacements.", toastStyle);
                                        return;
                                    }

                                    // Build the updated subassemblies list:
                                    // - remove incompatible ones
                                    // - add newly selected replacements
                                    const incompatibleIds = new Set(incompatible.map((s: any) => s.id));
                                    const newReplacementIds = Object.values(replacementSelections).filter(Boolean) as string[];

                                    const updatedSubassemblies = [
                                        ...det.subassemblies.filter((id: string) => !incompatibleIds.has(id)),
                                        ...newReplacementIds,
                                    ];

                                    // 1. Update version + cleaned subassemblies list in one PUT
                                    const csrfToken = await fetchCsrfToken();
                                    if (!csrfToken) return;

                                    const payload = {
                                        ...det,
                                        detector: newDetectorId,
                                        subassemblies: updatedSubassemblies,
                                    };

                                    try {
                                        const response = await fetch(apiUrl(
                                            `/api/inventory_built_dets/${det.mac_address}/`),
                                            {
                                                method: "PUT",
                                                credentials: credentials(),
                                                headers: {
                                                    "Content-Type": "application/json",
                                                    "X-CSRFToken": csrfToken,
                                                },
                                                body: JSON.stringify(payload),
                                            }
                                        );

                                        if (!response.ok) {
                                            const err = await response.json();
                                            console.error(err);
                                            toast.error("Failed to update version.", toastStyle);
                                            return;
                                        }
                                    } catch (error) {
                                        console.error(error);
                                        toast.error("Unexpected error updating version.", toastStyle);
                                        return;
                                    }

                                    // 2. Mark incompatible subs as available
                                    for (const sub of incompatible) {
                                        const subObj = builtSubAssemblies.find(bsa => bsa.id === sub.id);
                                        if (!subObj) continue;
                                        await fetch(apiUrl(`/api/inventory_built_subs/${sub.id}/`), {
                                            method: "PUT",
                                            credentials: credentials(),
                                            headers: { "Content-Type": "application/json", "X-CSRFToken": csrfToken },
                                            body: JSON.stringify({ ...subObj, status: "available", used_by: null }),
                                        });
                                    }

                                    // 3. Mark new replacements as installed
                                    for (const subId of newReplacementIds) {
                                        const subObj = builtSubAssemblies.find(bsa => bsa.id === subId);
                                        if (!subObj) continue;
                                        await fetch(apiUrl(`/api/inventory_built_subs/${subId}/`), {
                                            method: "PUT",
                                            credentials: credentials(),
                                            headers: { "Content-Type": "application/json", "X-CSRFToken": csrfToken },
                                            body: JSON.stringify({ ...subObj, status: "installed", used_by: det.mac_address }),
                                        });
                                    }

                                    toast.success("Detector version updated!", toastStyle);
                                    fetchbuiltDetectors();
                                    fetchBuiltSubAssemblies();

                                    setShowVersionChangePopup(false);
                                    setPendingVersionChange(null);
                                    setReplacementSelections({});
                                }}
                            >
                                Confirm
                            </button>
                        </div>

                    </div>
                </div>
            )}

            {/* Items Pending Table Popup */}
            {activeSection === "ItemsPending" && showSectionPopup && (
                <div className="sub-popup-overlay">
                    <div className="stock-or-count-popup-content">

                        <div className='stock-or-count-popup-header'>
                            <h1>Items Pending Count</h1>

                            <button
                                className="new-close-btn"
                                onClick={() => setShowSectionPopup(false)}
                            >
                                x
                            </button>

                        </div>

                        {!isMobile ? (
                            <>

                                <div className={`table-scroll-wrapper ${hasScrollbar ? "has-scrollbar" : ""}`}>
                                    <table className="stock-or-count-popup-table">
                                        <thead>
                                            <tr>
                                                <th>Part Name</th>
                                                <th>Quantity</th>
                                                <th>Last Counted</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                    </table>
                                </div>

                                <div ref={tableScrollRef} className="table-scroll-container">
                                    <table className="stock-or-count-popup-table">
                                        <tbody>
                                            {sortedCountData.map(item => {

                                                const getShortStatus = (item: InventoryItems) => {
                                                    const color = getColorTooltip(item.lastCounted);

                                                    if (color.startsWith("Red")) return "Over 60 days";
                                                    if (color.startsWith("Orange")) return "Over 30 days";
                                                    if (color.startsWith("Gray")) return "Never";
                                                    return "Recent";
                                                };

                                                const status = getShortStatus(item);
                                                const color = getColorTooltip(item.lastCounted);

                                                return (
                                                    <tr key={item.partID}>
                                                        <td>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                                <img
                                                                    className='part-image'
                                                                    src={item.partPicture || fnnBlueLogo}
                                                                    alt={item.partName}
                                                                />
                                                                {renderPartName(item, false)}
                                                            </div>
                                                        </td>

                                                        <td>{item.currentAmount}</td>

                                                        <td>
                                                            <span
                                                                className={`status-badge 
                                                    ${color.startsWith("Red") ? "status-red" : ""}
                                                    ${color.startsWith("Orange") ? "status-orange" : ""}
                                                    ${color.startsWith("Gray") ? "status-gray" : ""}
                                                `}
                                                            >
                                                                {status}
                                                            </span>
                                                        </td>

                                                        <td>
                                                            <button
                                                                className='new-counted-button'
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setItemToCount(item);
                                                                    setShowCountedPopup(true);
                                                                }}
                                                            >
                                                                Count
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>

                                    </table>
                                </div>
                            </>

                        ) : (
                            <div className="mobile-forecast-list mobile-stock-list">
                                {sortedCountData.map(item => {
                                    const isExpanded = expandedPendingId === item.partID;

                                    const getShortStatus = (item: InventoryItems) => {
                                        const color = getColorTooltip(item.lastCounted);

                                        if (color.startsWith("Red")) return "Over 60 days";
                                        if (color.startsWith("Orange")) return "Over 30 days";
                                        if (color.startsWith("Gray")) return "Never";
                                        return "Recent";
                                    };

                                    const status = getShortStatus(item);
                                    const color = getColorTooltip(item.lastCounted);

                                    return (
                                        <React.Fragment key={item.partID}>
                                            {/* ROW */}
                                            <div
                                                className="mobile-part-row2"
                                                onClick={() =>
                                                    setExpandedPendingId(
                                                        expandedPendingId === item.partID ? null : item.partID
                                                    )
                                                }
                                            >
                                                <div className="part-info-cell2">
                                                    <div className="part-info-cell-half1">
                                                        <img
                                                            src={item.partPicture || fnnBlueLogo}
                                                            alt={item.partName}
                                                            className="part-image"
                                                        />
                                                        <span>{renderPartName(item, false)}</span>
                                                    </div>

                                                    <span className={`status-badge 
                                                        ${color.startsWith("Red") ? "status-red" : ""}
                                                        ${color.startsWith("Orange") ? "status-orange" : ""}
                                                        ${color.startsWith("Gray") ? "status-gray" : ""}
                                                    `} style={{ minWidth: "fit-content" }}>
                                                        {status}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* EXPANDED */}
                                            {isExpanded && (
                                                <div className="mobile-details-row2">

                                                    <div className="mobile-detail-group">
                                                        <strong>Quantity:</strong> {item.currentAmount}
                                                    </div>

                                                    <div className="mobile-detail-group">
                                                        <strong>Last Counted:</strong> {item.lastCounted}
                                                    </div>

                                                    <div className="mobile-detail-group">
                                                        <strong>Action:</strong>
                                                        <button
                                                            className="new-counted-button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setItemToCount(item);
                                                                setShowCountedPopup(true);
                                                                setExpandedPendingId(null);
                                                            }}
                                                        >
                                                            Count
                                                        </button>
                                                    </div>

                                                </div>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </div>
                        )}


                    </div>
                </div>
            )}

            {/* Stock Availability Table Popup */}
            {activeSection === "StockAvailability" && showSectionPopup && (
                <div className="sub-popup-overlay">
                    <div className="stock-or-count-popup-content">

                        <div className='stock-or-count-popup-header'>
                            <h1>Stock Availability</h1>

                            <button
                                className="new-close-btn"
                                onClick={() => setShowSectionPopup(false)}
                            >
                                x
                            </button>

                        </div>

                        {!isMobile ? (
                            <>
                                <div className={`table-scroll-wrapper ${hasScrollbar ? "has-scrollbar" : ""}`}>
                                    <table className="stock-or-count-popup-table">
                                        <thead>
                                            <tr>
                                                <th>Part Name</th>
                                                <th>Quantity</th>
                                                <th>Status</th>
                                                <th><div className="column-row-div">Request</div></th>
                                            </tr>
                                        </thead>
                                    </table>
                                </div>

                                <div ref={tableScrollRef} className="table-scroll-container">
                                    <table className="stock-or-count-popup-table">
                                        <tbody>
                                            {sortedStockData.map(({ item, stock }) => {

                                                return (
                                                    <tr key={item.partID}>
                                                        <td>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                                <img
                                                                    className='part-image'
                                                                    src={item.partPicture || fnnBlueLogo}
                                                                    alt={item.partName}
                                                                />
                                                                {renderPartName(item, false)}
                                                            </div>
                                                        </td>

                                                        <td>{item.currentAmount}</td>

                                                        <td>
                                                            <span
                                                                style={{
                                                                    color: getStockStatus(item, desiredPartsNeeded).color,
                                                                    fontWeight: 600,
                                                                    cursor: "help"
                                                                }}
                                                                className="tooltip-wrapper"
                                                            >
                                                                {getStockStatus(item, desiredPartsNeeded).status}
                                                                <div className="custom-tooltip">
                                                                    {getStockStatus(item, desiredPartsNeeded).tooltip}
                                                                </div>
                                                            </span>
                                                        </td>

                                                        <td>
                                                            {(() => {
                                                                const orderInfo = getOrderToAdequate(item, desiredPartsNeeded);
                                                                if (!orderInfo) return <span style={{ color: 'gray', fontSize: '0.75rem' }}>—</span>;

                                                                return (
                                                                    <div className="column-row-div">
                                                                        <div
                                                                            className="delete-part-button reorder-button"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                setActiveRequestPartID(item.partID);
                                                                                setRequestQuantity(orderInfo.units);
                                                                                setRequestPackets(orderInfo.packets ?? 0);
                                                                                setShowRequestItemPopup(true);
                                                                            }}
                                                                        >
                                                                            <IoBagAdd />
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })()}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>

                                    </table>
                                </div>
                            </>

                        ) : (
                            <div className="mobile-forecast-list mobile-stock-list">
                                {sortedStockData.map(({ item, stock }) => {
                                    const isExpanded = expandedStockId === item.partID;

                                    return (
                                        <React.Fragment key={item.partID}>
                                            {/* ROW HEADER */}
                                            <div
                                                className="mobile-part-row2"
                                                onClick={() =>
                                                    setExpandedStockId(
                                                        expandedStockId === item.partID ? null : item.partID
                                                    )
                                                }
                                            >
                                                <div className="part-info-cell2">

                                                    <div className='part-info-cell-half1'>
                                                        <img
                                                            src={item.partPicture || fnnBlueLogo}
                                                            alt={item.partName}
                                                            className="part-image"
                                                        />
                                                        <span>{renderPartName(item, false)}</span>
                                                    </div>

                                                    <span
                                                        style={{
                                                            color: getStockStatus(item, desiredPartsNeeded).color,
                                                            fontWeight: 500,
                                                            minWidth: "fit-content"
                                                        }}
                                                    >
                                                        {getStockStatus(item, desiredPartsNeeded).status}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* EXPANDED DETAILS */}
                                            {isExpanded && (
                                                <div className="mobile-details-row2">

                                                    <div className="mobile-detail-group">
                                                        <strong>Quantity:</strong> {item.currentAmount}
                                                    </div>

                                                    <div className="mobile-detail-group">
                                                        <strong>Details:</strong>{" "}
                                                        {getStockStatus(item, desiredPartsNeeded).tooltip}
                                                    </div>

                                                    <div className="mobile-detail-group">
                                                        <strong>Request:</strong>{" "}
                                                        {(() => {
                                                            const orderInfo = getOrderToAdequate(item, desiredPartsNeeded);

                                                            if (!orderInfo) {
                                                                return <span style={{ color: 'gray' }}>—</span>;
                                                            }

                                                            return (
                                                                <div
                                                                    className="delete-part-button reorder-button"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setActiveRequestPartID(item.partID);
                                                                        setRequestQuantity(orderInfo.units);
                                                                        setRequestPackets(orderInfo.packets ?? 0);
                                                                        setShowRequestItemPopup(true);
                                                                    }}
                                                                >
                                                                    <IoBagAdd />
                                                                </div>
                                                            );
                                                        })()}
                                                    </div>

                                                </div>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </div>

                        )}


                    </div>
                </div>
            )}

            {/* Type of New Order Popup */}
            {showTypeOrderPopup && (
                <div className="add-item-popup-overlay">
                    <div className="add-item-popup-content">
                        <div className="inv-small-pop">

                            <h1>Order Request</h1>

                            <div className="type-order-popup">

                                <div className='type-order-option' onClick={() => (setShowAddOrderPopup(true), setShowTypeOrderPopup(false))}>
                                    <TfiDropboxAlt />
                                    <span>Item</span>
                                </div>

                                <div
                                    className='type-order-option'
                                    onClick={() => {
                                        setShowSubAssemblyOrderPopup(true);
                                        setShowTypeOrderPopup(false);
                                    }}
                                >
                                    <PiPuzzlePieceFill />
                                    <span>Sub-Assembly</span>
                                </div>

                                <div
                                    className={`type-order-option ${!hasAvailableBatches ? 'disabled-option' : ''}`}
                                    onClick={() => {
                                        if (!hasAvailableBatches) return;
                                        setShowBatchRequestTypePopup(true);
                                    }}
                                >
                                    <FaBoxesStacked />
                                    <span>Batch</span>
                                </div>

                            </div>

                            <button
                                className='to-link-class2'
                                onClick={() => {
                                    setActiveSection('PartsForXMore');
                                    setShowSectionPopup(true);
                                }}
                            >
                                Parts Required for Building More Detectors
                            </button>

                            <div className="hh-duo hh-actions">
                                <button
                                    className="cancel-btn"
                                    onClick={() => setShowTypeOrderPopup(false)}
                                >
                                    Cancel
                                </button>
                            </div>

                        </div>
                    </div>
                </div>
            )}

            {/* Type of Batch */}
            {showBatchRequestTypePopup && (
                <div className="add-item-popup-overlay">
                    <div className="add-item-popup-content">
                        <div className="inv-small-pop">

                            <h1>Batch Request</h1>

                            <div className="type-order-popup type-batch-popup">

                                <div
                                    className='type-order-option'
                                    onClick={() => {
                                        setConsiderStock(true);
                                        setShowBatchOrderPopup(true);
                                        setShowBatchRequestTypePopup(false);
                                        setShowTypeOrderPopup(false);
                                    }}
                                >
                                    <HiArchiveBox />
                                    <span>Consider Available Stock</span>
                                </div>

                                <div
                                    className='type-order-option'
                                    onClick={() => {
                                        setConsiderStock(false);
                                        setShowBatchOrderPopup(true);
                                        setShowBatchRequestTypePopup(false);
                                        setShowTypeOrderPopup(false);
                                    }}
                                >
                                    <HiArchiveBoxXMark />
                                    <span>Don&apos;t Consider Available Stock</span>
                                </div>

                            </div>

                            <div className="hh-duo hh-actions">
                                <button
                                    className="cancel-btn"
                                    onClick={() => setShowBatchRequestTypePopup(false)}
                                >
                                    Cancel
                                </button>
                            </div>

                        </div>
                    </div>
                </div>
            )}

            {/* Placing full batch of order requests */}
            {showBatchOrderPopup && (
                <div className="active-products-popup-overlay">
                    <div className="active-products-popup-content">

                        <h2 className="edit-org-popup-header">Batch Request</h2>
                        <p>
                            {considerStock
                                ? "Create an order request only for missing parts based on current inventory."
                                : "Create an order request for ALL parts required for this batch, ignoring current inventory."
                            }
                        </p>

                        <div className="edit-org-form edit-manage edit-batch-request-form">

                            {/* Batch Selector */}
                            <label className="full-width-section">
                                <strong>Select Batch:</strong>
                                <select
                                    value={batchOrderSelectedId ?? ""}
                                    onChange={(e) => {
                                        const val = e.target.value ? Number(e.target.value) : null;
                                        setBatchOrderSelectedId(val);
                                        setBatchOrderProgress(null);
                                        setOpenBatchSlips({});
                                        if (val) fetchBatchProgress(val);
                                    }}
                                >
                                    <option value="">-- Select a Batch --</option>
                                    {batches
                                        .filter(b => b.status !== 'completed' && b.status !== 'cancelled')
                                        .map(b => (
                                            <option key={b.batchID} value={b.batchID}>
                                                {b.batch_name} ({b.target_quantity} detectors)
                                            </option>
                                        ))
                                    }
                                </select>
                            </label>

                            {/* Batch Summary */}
                            {batchOrderProgress && (
                                <div className="full-width-section">
                                    <span style={{ marginBottom: '0.5rem' }}>
                                        <span style={{ fontWeight: '700' }} > Batch Breakdown</span>
                                    </span>

                                    {/* One slip per sub-assembly */}
                                    {Object.entries(batchOrderProgress.subassemblies.bom_per_detector).map(([subName, qtyPerDetector]) => {
                                        const subAssemblyObj = subAssemblies.find(sa => sa.name === subName);
                                        const isOpen = !!openBatchSlips[subName];
                                        const target = batchOrderProgress.detectors.target;

                                        return (
                                            <div key={subName} className={`slip ${isOpen ? 'open' : ''}`}>
                                                <div
                                                    className="slip-header"
                                                    onClick={() => setOpenBatchSlips(prev => ({ ...prev, [subName]: !prev[subName] }))}
                                                >
                                                    <span>{subName}</span>
                                                    <div className="slip-header-right">
                                                        <span style={{ fontSize: '0.8rem', opacity: 0.7 }}>
                                                            {qtyPerDetector} per detector × {target} = {qtyPerDetector * target} total
                                                        </span>
                                                        <span className="slip-header-arrow">▼</span>
                                                    </div>
                                                </div>

                                                {isOpen && (
                                                    <div className="slip-content">
                                                        {!subAssemblyObj ? (
                                                            <p style={{ padding: '0.5rem', color: '#999' }}>Sub-assembly details not found.</p>
                                                        ) : subAssemblyObj.parts.length === 0 ? (
                                                            <p style={{ padding: '0.5rem', color: '#999' }}>No parts in BOM.</p>
                                                        ) : (
                                                            <table className="inventory-table" style={{ tableLayout: 'fixed', width: '100%' }}>
                                                                <thead>
                                                                    <tr>
                                                                        <th>Part</th>
                                                                        <th>Per Sub-Assembly</th>
                                                                        <th>Total to Order</th>
                                                                        <th>Total Price</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {subAssemblyObj.parts.map(partReq => {
                                                                        const part = invInfo.find(p => p.partID === partReq.part);
                                                                        const rawNeeded = partReq.quantity_required * target;
                                                                        const available = part?.currentAmount || 0;

                                                                        const totalNeeded = considerStock
                                                                            ? Math.max(rawNeeded - available, 0)
                                                                            : rawNeeded;

                                                                        const totalPrice = (part?.unitCost || 0) * totalNeeded;
                                                                        return (
                                                                            <tr key={partReq.part}>
                                                                                <td>
                                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                                                        {truncateText(
                                                                                            ((part?.mpn ? (part.mpn + " - ") : "") + (part?.partName || `Part ${partReq.part}`)),
                                                                                            40
                                                                                        )}
                                                                                    </div>
                                                                                </td>
                                                                                <td>{partReq.quantity_required}</td>
                                                                                <td>{totalNeeded}</td>
                                                                                <td>${totalPrice.toFixed(2)}</td>
                                                                            </tr>
                                                                        );
                                                                    })}
                                                                </tbody>
                                                            </table>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                        </div>

                        {/* Footer Actions */}
                        <div className="hh-duo hh-actions edit-org-popup-footer">
                            <button
                                className="cancel-btn"
                                onClick={() => {
                                    setShowBatchOrderPopup(false);
                                    setBatchOrderSelectedId(null);
                                    setBatchOrderProgress(null);
                                    setSelectedBatchProgress(null);
                                    setOpenBatchSlips({});
                                    setSelectedBatchForBuild(null);
                                }}
                            >
                                Cancel
                            </button>

                            <button
                                className="confirm-btn"
                                disabled={!batchOrderProgress}
                                onClick={() => {
                                    if (batchOrderProgress) handleBatchForecastRequest(batchOrderProgress);
                                }}
                            >
                                Place Request
                            </button>
                        </div>

                    </div>
                </div>
            )}

            {/* Placing sub-assemblies as orders */}
            {showSubAssemblyOrderPopup && (
                <div className="active-products-popup-overlay">
                    <div className="active-products-popup-content">

                        <h2 className="edit-org-popup-header">Sub-Assembly Order</h2>

                        <p>
                            Create order requests based on items in sub-assemblies.
                        </p>

                        <div className="edit-org-form edit-manage edit-batch-request-form edit-sub-request-form">

                            {/* =========================
                                ADD SUB-ASSEMBLY ROW
                             ========================== */}
                            <div className="full-width-section">

                                <div className="sub-part-and-amount-row">

                                    {/* Sub-assembly search */}
                                    <div className="field-group">
                                        <label style={{ position: 'relative' }}>
                                            <span>Sub-Assembly</span>
                                            <input
                                                type="text"
                                                placeholder="Search sub-assembly"
                                                value={subAssemblyOrderSearch}
                                                onChange={(e) => {
                                                    setSubAssemblyOrderSearch(e.target.value);
                                                    setShowSubAssemblyOrderDropdown(true);
                                                }}
                                                onFocus={() => setShowSubAssemblyOrderDropdown(true)}
                                                onBlur={() => setTimeout(() => setShowSubAssemblyOrderDropdown(false), 200)}
                                            />

                                            {showSubAssemblyOrderDropdown && (
                                                <ul className="autocomplete-dropdown">
                                                    {subAssemblies
                                                        .filter(sa =>
                                                            sa.name.toLowerCase().includes(subAssemblyOrderSearch.toLowerCase())
                                                        )
                                                        .map(sa => (
                                                            <li
                                                                key={sa.id}
                                                                onClick={() => {
                                                                    setSelectedSubAssemblyOrderId(sa.id);
                                                                    setSubAssemblyOrderSearch(sa.name);
                                                                    setShowSubAssemblyOrderDropdown(false);
                                                                }}
                                                            >
                                                                {sa.name}
                                                            </li>
                                                        ))}
                                                </ul>
                                            )}
                                        </label>
                                    </div>

                                    <div className="sub-part-amount-wrapper">
                                        {/* Quantity */}
                                        <div className="field-group">
                                            <label>
                                                <span>Quantity</span>
                                                <input
                                                    type="number"
                                                    min={1}
                                                    value={selectedSubAssemblyOrderQty}
                                                    onChange={(e) => setSelectedSubAssemblyOrderQty(Number(e.target.value))}
                                                />
                                            </label>
                                        </div>

                                        {/* Add button */}
                                        <button
                                            className="add-another-part"
                                            disabled={!selectedSubAssemblyOrderId || selectedSubAssemblyOrderQty <= 0}
                                            onClick={() => {
                                                if (!selectedSubAssemblyOrderId || selectedSubAssemblyOrderQty <= 0) return;

                                                const exists = subAssemblyOrderRequests.some(
                                                    r => r.subassemblyId === selectedSubAssemblyOrderId
                                                );

                                                if (exists) {
                                                    toast.warn("Cannot add the same sub-assembly twice!", toastStyle);
                                                    return;
                                                }

                                                setSubAssemblyOrderRequests(prev => [
                                                    ...prev,
                                                    {
                                                        subassemblyId: selectedSubAssemblyOrderId, // now guaranteed number
                                                        quantity: selectedSubAssemblyOrderQty
                                                    }
                                                ]);

                                                setSelectedSubAssemblyOrderId(null);
                                                setSelectedSubAssemblyOrderQty(1);
                                                setSubAssemblyOrderSearch('');
                                            }}
                                        >
                                            + Add
                                        </button>
                                    </div>

                                </div>

                                {/* Selected list */}
                                <ul className="inv-manage-items">
                                    {subAssemblyOrderRequests.map((req, idx) => {
                                        const sub = subAssemblies.find(sa => sa.id === req.subassemblyId);
                                        return (
                                            <li key={idx}>
                                                {sub?.name} – Qty: {req.quantity}
                                                <button
                                                    onClick={() =>
                                                        setSubAssemblyOrderRequests(prev =>
                                                            prev.filter((_, i) => i !== idx)
                                                        )
                                                    }
                                                >
                                                    <FaTrashAlt />
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>

                            </div>

                            {/* =========================
                                BREAKDOWN (LIKE BATCH)
                            ========================== */}
                            {subAssemblyOrderRequests.length > 0 && (
                                <div className="full-width-section">

                                    <span style={{ fontWeight: '700' }} > Order Breakdown</span>

                                    {subAssemblyOrderRequests.map((req) => {
                                        const sub = subAssemblies.find(sa => sa.id === req.subassemblyId);
                                        const isOpen = !!openBatchSlips[sub?.name || ""];

                                        if (!sub) return null;

                                        return (
                                            <div key={sub.id} className={`slip ${isOpen ? 'open' : ''}`}>
                                                <div
                                                    className="slip-header"
                                                    onClick={() =>
                                                        setOpenBatchSlips(prev => ({
                                                            ...prev,
                                                            [sub.name]: !prev[sub.name]
                                                        }))
                                                    }
                                                >
                                                    <span>{sub.name}</span>

                                                    <div className="slip-header-right">
                                                        <span style={{ fontSize: '0.8rem', opacity: 0.7 }}>
                                                            x{req.quantity}
                                                        </span>
                                                        <span className="slip-header-arrow">▼</span>
                                                    </div>
                                                </div>

                                                {isOpen && (
                                                    <div className="slip-content">
                                                        <table className="inventory-table">
                                                            <thead>
                                                                <tr>
                                                                    <th>Part</th>
                                                                    <th>Per Sub</th>
                                                                    <th>Total</th>
                                                                    <th>Total Price</th>
                                                                </tr>
                                                            </thead>

                                                            <tbody>
                                                                {sub.parts.map(partReq => {
                                                                    const part = invInfo.find(p => p.partID === partReq.part);

                                                                    const totalNeeded = partReq.quantity_required * req.quantity;

                                                                    const totalPrice = (part?.unitCost || 0) * totalNeeded;

                                                                    return (
                                                                        <tr key={partReq.part}>
                                                                            <td>
                                                                                {truncateText(
                                                                                    ((part?.mpn ? part.mpn + " - " : "") +
                                                                                        (part?.partName || `Part ${partReq.part}`)),
                                                                                    40
                                                                                )}
                                                                            </td>
                                                                            <td>{partReq.quantity_required}</td>
                                                                            <td>{totalNeeded}</td>
                                                                            <td>${totalPrice.toFixed(2)}</td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                        </div>

                        {/* =========================
                            FOOTER
                            ========================== */}
                        <div className="hh-duo hh-actions edit-org-popup-footer">
                            <button
                                className="cancel-btn"
                                onClick={() => {
                                    setShowSubAssemblyOrderPopup(false);
                                    resetSubAssemblyOrderState();
                                }}
                            >
                                Cancel
                            </button>

                            <button
                                className="confirm-btn"
                                disabled={subAssemblyOrderRequests.length === 0}
                                onClick={handleSubAssemblyForecastRequest}
                            >
                                Place Request
                            </button>
                        </div>

                    </div>
                </div>
            )}

            {/* Build Sub-Assembly */}
            {activeSection === "AddConsumeSubAssembly" && showSectionPopup && (
                <div className="sub-popup-overlay">
                    <div className="sub-popup-content">
                        <button
                            className="close-popup-button"
                            onClick={() => closeConsumeAddPopup('sub')}
                        >
                            x
                        </button>

                        <h2>Build Sub-Assembly</h2>

                        <div className='subassembly-manage-stack'>

                            {/* Batch Selector */}
                            <label className='batch-drop'>
                                <span>Batch (optional):</span>
                                <select
                                    value={selectedBatchForBuild ?? ""}
                                    onChange={async (e) => {
                                        const val = e.target.value ? Number(e.target.value) : null;

                                        setSelectedBatchForBuild(val);
                                        setSelectedBatchProgress(null);

                                        if (val) {
                                            const response = await fetch(apiUrl(
                                                `/api/inventory_batches/${val}/progress/`),
                                                { credentials: credentials() }
                                            );

                                            if (response.ok) {
                                                const data = await response.json();
                                                setSelectedBatchProgress(data);

                                                if (!data) return;

                                                const prevRequests = subAssemblyRequests.filter(r => r.selectedSubAssemblyId);

                                                const allowedSubs = new Set(
                                                    Object.keys(data.subassemblies?.bom_per_detector || {})
                                                );

                                                const newRequests: SubAssemblyRequest[] = [];
                                                const newSearchTerms: Record<number, string> = {};
                                                const newDropdownOpen: Record<number, boolean> = {};

                                                prevRequests.forEach((req, oldIndex) => {
                                                    const sub = subAssemblies.find(
                                                        sa => sa.id.toString() === req.selectedSubAssemblyId
                                                    );

                                                    const isValid = sub && allowedSubs.has(sub.name);

                                                    if (isValid) {
                                                        const newIndex = newRequests.length;

                                                        newRequests.push(req);

                                                        if (requestSearchTerms[oldIndex]) {
                                                            newSearchTerms[newIndex] = requestSearchTerms[oldIndex];
                                                        }

                                                        if (requestDropdownOpen[oldIndex]) {
                                                            newDropdownOpen[newIndex] = requestDropdownOpen[oldIndex];
                                                        }
                                                    }
                                                });

                                                // Update state (NO updater function)
                                                setSubAssemblyRequests(
                                                    newRequests.length > 0 ? newRequests : prevRequests
                                                );
                                                setRequestSearchTerms(newSearchTerms);
                                                setRequestDropdownOpen(newDropdownOpen);

                                                // Toast OUTSIDE state update (fixes duplicate issue)
                                                if (
                                                    prevRequests.length > 0 &&
                                                    newRequests.length !== prevRequests.length
                                                ) {
                                                    toast.warning(
                                                        "Some sub-assemblies were removed because they are not part of this batch.",
                                                        {
                                                            ...toastStyle,
                                                            toastId: "batch-filter-warning",
                                                        }
                                                    );
                                                }

                                            } else {
                                                setSelectedBatchProgress(null);
                                            }
                                        }
                                    }}
                                >
                                    <option value="">-- No Batch --</option>
                                    {batches
                                        .filter(b => b.status !== 'completed' && b.status !== 'cancelled')
                                        .map(b => (
                                            <option key={b.batchID} value={b.batchID}>
                                                {b.batch_name} ({b.detectors_built} out of {b.target_quantity} completed)
                                            </option>
                                        ))
                                    }
                                </select>
                            </label>

                            {/* Batch Progress Display */}
                            {selectedBatchProgress && (
                                <div className="batch-progress-panel">
                                    <h3>Batch Progress:</h3>

                                    <p>
                                        Detectors: <strong>{selectedBatchProgress.detectors.built} out of {selectedBatchProgress.detectors.target} assembled</strong>
                                    </p>

                                    <p>
                                        Subassemblies: <strong>{selectedBatchProgress.subassemblies.built} out of {selectedBatchProgress.subassemblies.total_needed} built</strong>
                                    </p>

                                    <h4>Breakdown</h4>

                                    {selectedBatchProgress && (
                                        <table className="inventory-table analytic-table-header">
                                            <thead className='main-thead'>
                                                <tr>
                                                    <th>SubAssembly</th>
                                                    <th>Built</th>
                                                    <th>Remaining</th>
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {Object.entries(selectedBatchProgress.subassemblies.built_breakdown).map(
                                                    ([name, count]) => {
                                                        const required =
                                                            selectedBatchProgress.detectors.target || 0;

                                                        const remaining = Math.max(required - count, 0);

                                                        return (
                                                            <tr key={name}>
                                                                <td>{name}</td>
                                                                <td>{count}</td>
                                                                <td>{remaining}</td>
                                                            </tr>
                                                        );
                                                    }
                                                )}
                                            </tbody>
                                        </table>
                                    )}

                                </div>
                            )}

                            {subAssemblyRequests.map((req, index) => (
                                <div className="subassembly-manage-table" key={req.id}>
                                    <label style={{ position: 'relative' }}>
                                        Sub-Assembly:
                                        <input
                                            type="text"
                                            placeholder="Search sub-assembly..."
                                            value={requestSearchTerms[index] ?? subAssemblies.find(sa => sa.id.toString() === req.selectedSubAssemblyId)?.name ?? ""}
                                            onChange={(e) => {
                                                setRequestSearchTerms(prev => ({ ...prev, [index]: e.target.value }));
                                                setRequestDropdownOpen(prev => ({ ...prev, [index]: true }));
                                            }}
                                            onFocus={() => setRequestDropdownOpen(prev => ({ ...prev, [index]: true }))}
                                            onBlur={() => setTimeout(() => setRequestDropdownOpen(prev => ({ ...prev, [index]: false })), 200)}
                                        />
                                        {requestDropdownOpen[index] && (
                                            <div className="autocomplete-dropdown">
                                                {filteredSubAssembliesBuilds
                                                    .filter(sa =>
                                                        sa.name.toLowerCase().includes((requestSearchTerms[index] ?? "").toLowerCase())
                                                    )
                                                    .length > 0 ? (
                                                    filteredSubAssembliesBuilds
                                                        .filter(sa =>
                                                            sa.name.toLowerCase().includes((requestSearchTerms[index] ?? "").toLowerCase())
                                                        )
                                                        .map(sa => (
                                                            <li
                                                                key={sa.id}
                                                                style={{ color: 'black' }}
                                                                onClick={() => {
                                                                    handleRequestChange(index, 'selectedSubAssemblyId', sa.id.toString());
                                                                    setRequestSearchTerms(prev => ({ ...prev, [index]: sa.name }));
                                                                    setRequestDropdownOpen(prev => ({ ...prev, [index]: false }));
                                                                }}
                                                            >
                                                                {sa.name}
                                                            </li>
                                                        ))
                                                ) : (
                                                    <li style={{ color: '#999' }}>No matches found</li>
                                                )}
                                            </div>
                                        )}
                                    </label>

                                    <div className='sub-part-amount-wrapper2'>
                                        <label>
                                            Quantity:
                                            <input
                                                type="number"
                                                onWheel={(e) => e.currentTarget.blur()}
                                                min="1"
                                                value={req.operationCount}
                                                onChange={(e) => handleRequestChange(index, 'operationCount', Number(e.target.value))}
                                            />
                                        </label>

                                        {subAssemblyRequests.length > 1 && (
                                            <button onClick={() => removeRequest(index)}>Remove</button>
                                        )}
                                    </div>
                                </div>
                            ))}



                            <button className='add-request-card' onClick={addNewRequest}>+ New Sub-Assembly</button>
                        </div>

                        <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                            <button
                                className="confirm-requests-button"
                                onClick={() => {
                                    // Check if any request has no selected sub-assembly
                                    const invalidRequest = subAssemblyRequests.some(r => !r.selectedSubAssemblyId);
                                    if (invalidRequest) {
                                        toast.error("Please select a valid sub-assembly for all requests.", toastStyle);
                                        return;
                                    }

                                    // Check if we have enough parts to build or if the build attempt would go over the batch
                                    if (!validateBuildRequests()) return;

                                    // Split requests by operation
                                    const buildNewSubs = subAssemblyRequests.filter(r => r.operation === "BUILD_NEW");
                                    const registerSubs = subAssemblyRequests.filter(r => r.operation === "REGISTER_EXISTING");
                                    const addSubs = subAssemblyRequests.filter(r => r.operation === "ADD_COMPONENTS");

                                    if (buildNewSubs.length === 0 && registerSubs.length === 0 && addSubs.length === 0) {
                                        toast.error("Please select at least one valid sub-assembly request.", toastStyle);
                                        return;
                                    }

                                    setBuildSummary({
                                        buildNew: buildNewSubs.map(r => ({
                                            sub: subAssemblies.find(s => s.id.toString() === r.selectedSubAssemblyId)?.name || `Sub ${r.selectedSubAssemblyId}`,
                                            qty: r.operationCount
                                        })),
                                        register: registerSubs.map(r => ({
                                            sub: subAssemblies.find(s => s.id.toString() === r.selectedSubAssemblyId)?.name || `Sub ${r.selectedSubAssemblyId}`,
                                            qty: r.operationCount
                                        })),
                                        addComponents: addSubs.map(r => ({
                                            sub: subAssemblies.find(s => s.id.toString() === r.selectedSubAssemblyId)?.name || `Sub ${r.selectedSubAssemblyId}`,
                                            qty: r.operationCount
                                        }))
                                    });
                                    setShowBuildConfirmPopup(true);
                                }}
                            >
                                Confirm
                            </button>

                        </div>

                    </div>
                </div>
            )}

            {/* Assemble Detector */}
            {activeSection === "AssembleDetector" && showSectionPopup && (
                <div className="sub-popup-overlay">
                    <div className="sub-popup-content assemble-multi-popup">
                        <button
                            className="close-popup-button"
                            onClick={() => {
                                setShowSectionPopup(false);
                                setAssembleDetectorRequests([{
                                    id: Date.now(),
                                    macAddress: "",
                                    detCode: "",
                                    selectedSubAssemblies: {}
                                }]);
                                setAssembleSelectedBatch(null);
                                setAssembleSharedDetectorId(null);
                            }}
                        >
                            x
                        </button>

                        <h2>Assemble Detectors</h2>

                        <div className="assemble-multi-header">
                            {/* Shared Batch Selector */}
                            <label className="batch-drop">
                                <span>Batch (optional):</span>
                                <select
                                    value={assembleSelectedBatch ?? ""}
                                    onChange={(e) => {
                                        const batchId = e.target.value ? Number(e.target.value) : null;
                                        setAssembleSelectedBatch(batchId);
                                        // Reset all sub-assembly selections when batch changes
                                        setAssembleDetectorRequests(prev => prev.map(r => ({ ...r, selectedSubAssemblies: {} })));

                                        if (batchId) {
                                            const batch = batches.find(b => b.batchID === batchId);
                                            if (batch) setAssembleSharedDetectorId(batch.detector_version);
                                        } else {
                                            setAssembleSharedDetectorId(null);
                                        }
                                    }}
                                >
                                    <option value="">-- No Batch --</option>
                                    {batches
                                        .filter(b => b.status !== 'completed' && b.status !== 'cancelled')
                                        .map(b => (
                                            <option key={b.batchID} value={b.batchID}>
                                                {b.batch_name} ({b.detectors_built} out of {b.target_quantity} completed)
                                            </option>
                                        ))
                                    }
                                </select>
                            </label>

                            {/* Shared Detector Version Selector */}
                            <label className="batch-drop">
                                <span>Detector Version:</span>
                                <select
                                    value={assembleSharedDetectorId ?? ""}
                                    disabled={assembleSelectedBatch !== null}
                                    onChange={(e) => {
                                        setAssembleSharedDetectorId(Number(e.target.value) || null);
                                        setAssembleDetectorRequests(prev => prev.map(r => ({ ...r, selectedSubAssemblies: {} })));
                                    }}
                                >
                                    <option value="" disabled>-- Select Detector --</option>
                                    {detectors.map(det => (
                                        <option key={det.id} value={det.id}>
                                            {det.name} v{det.version}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        </div>

                        {/* Detector Cards */}
                        <div className="assemble-detector-cards">
                            {assembleDetectorRequests.map((req, index) => {
                                const detectorDef = detectors.find(d => d.id === assembleSharedDetectorId);

                                return (
                                    <div key={req.id} className="assemble-detector-card">
                                        <div className="assemble-card-header">
                                            <span className="assemble-card-number">#{index + 1}</span>
                                            {assembleDetectorRequests.length > 1 && (
                                                <button
                                                    className="assemble-remove-card"
                                                    onClick={() =>
                                                        setAssembleDetectorRequests(prev => prev.filter((_, i) => i !== index))
                                                    }
                                                >
                                                    Remove
                                                </button>
                                            )}
                                        </div>

                                        <div className="assemble-card-fields">
                                            <label>
                                                <span>MAC Address</span>
                                                <input
                                                    type="text"
                                                    placeholder="e.g. 0xfb3ee462"
                                                    value={req.macAddress}
                                                    onChange={(e) =>
                                                        setAssembleDetectorRequests(prev => prev.map((r, i) =>
                                                            i === index ? { ...r, macAddress: e.target.value } : r
                                                        ))
                                                    }
                                                />
                                            </label>

                                            <label>
                                                <span>Detector Code</span>
                                                <input
                                                    type="text"
                                                    placeholder="e.g. GOE (optional)"
                                                    value={req.detCode}
                                                    onChange={(e) =>
                                                        setAssembleDetectorRequests(prev => prev.map((r, i) =>
                                                            i === index ? { ...r, detCode: e.target.value } : r
                                                        ))
                                                    }
                                                />
                                            </label>
                                        </div>

                                        {/* Sub-Assembly Selectors */}
                                        {assembleSharedDetectorId && detectorDef && (
                                            <div className="assemble-card-subassemblies">
                                                {detectorDef.subassemblies.map(ds => {
                                                    const subObj =
                                                        typeof ds.subassembly === "number"
                                                            ? subAssemblies.find(s => s.id === ds.subassembly)
                                                            : ds.subassembly;

                                                    if (!subObj) return null;

                                                    // Collect IDs already selected by OTHER cards for this sub-assembly type
                                                    const takenIds = new Set(
                                                        assembleDetectorRequests
                                                            .filter((_, i) => i !== index)
                                                            .map(r => r.selectedSubAssemblies[subObj.id])
                                                            .filter(Boolean)
                                                    );

                                                    const availableOptions = builtSubAssemblies.filter(bsa => {
                                                        if (bsa.subassembly !== subObj.id) return false;
                                                        if (bsa.status !== "available") return false;
                                                        if (assembleSelectedBatch !== null) {
                                                            return bsa.belongs_to_batch === assembleSelectedBatch;
                                                        } else {
                                                            return bsa.belongs_to_batch === null;
                                                        }
                                                    });

                                                    const selectedId = req.selectedSubAssemblies[subObj.id];

                                                    return (
                                                        <div key={subObj.id} className="assemble-sub-group">
                                                            <span className="assemble-sub-label">{subObj.name}</span>
                                                            <div className="subassembly-options-box">
                                                                {availableOptions.length === 0 ? (
                                                                    <p className="no-options">
                                                                        No available {subObj.name}s
                                                                        {assembleSelectedBatch ? " in this batch" : " without a batch"}
                                                                    </p>
                                                                ) : (
                                                                    availableOptions.map(opt => {
                                                                        const isTaken = takenIds.has(String(opt.id));
                                                                        const isSelected = selectedId === String(opt.id);

                                                                        return (
                                                                            <div
                                                                                key={opt.id}
                                                                                className={`subassembly-option ${isSelected ? "selected" : ""} ${isTaken ? "taken" : ""}`}
                                                                                onClick={() => {
                                                                                    if (isTaken) return;
                                                                                    setAssembleDetectorRequests(prev => prev.map((r, i) =>
                                                                                        i === index
                                                                                            ? { ...r, selectedSubAssemblies: { ...r.selectedSubAssemblies, [subObj.id]: String(opt.id) } }
                                                                                            : r
                                                                                    ));
                                                                                }}
                                                                                title={isTaken ? `Already assigned to detector #${assembleDetectorRequests.findIndex(r => r.selectedSubAssemblies[subObj.id] === String(opt.id)) + 1}` : `Select unit ${opt.id}`}
                                                                            >
                                                                                <span>{opt.id}</span>
                                                                                {isTaken && <span className="taken-badge">✕</span>}
                                                                            </div>
                                                                        );
                                                                    })
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        <div style={{ width: '100%', display: 'flex', alignItems: 'center', paddingLeft: '1rem', paddingRight: '1rem' }}>
                            <button
                                className="add-request-card"
                                onClick={() =>
                                    setAssembleDetectorRequests(prev => [...prev, {
                                        id: Date.now(),
                                        macAddress: "",
                                        detCode: "",
                                        selectedSubAssemblies: {}
                                    }])
                                }
                            >
                                + Add Another Detector
                            </button>
                        </div>

                        <div style={{ width: '100%', display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
                            <button
                                className="confirm-requests-button"
                                onClick={() => {
                                    // Validate all cards
                                    for (let i = 0; i < assembleDetectorRequests.length; i++) {
                                        const req = assembleDetectorRequests[i];

                                        if (!req.macAddress.trim()) {
                                            toast.error(`Detector #${i + 1}: MAC Address is required.`, toastStyle);
                                            return;
                                        }

                                        if (!assembleSharedDetectorId) {
                                            toast.error("Please select a detector version.", toastStyle);
                                            return;
                                        }

                                        const detectorDef = detectors.find(d => d.id === assembleSharedDetectorId);
                                        const missingSubAssembly = detectorDef?.subassemblies.some(ds => {
                                            const subId = typeof ds.subassembly === "number" ? ds.subassembly : ds.subassembly.id;
                                            return !req.selectedSubAssemblies[subId];
                                        });

                                        if (missingSubAssembly) {
                                            toast.error(`Detector #${i + 1}: Please select all required sub-assemblies.`, toastStyle);
                                            return;
                                        }
                                    }

                                    // Fire off all assemblies
                                    assembleDetectorRequests.forEach(req => {
                                        assembleDetector(
                                            assembleSharedDetectorId,
                                            req.selectedSubAssemblies,
                                            req.macAddress,
                                            req.detCode,
                                            assembleSelectedBatch
                                        );
                                    });
                                }}
                            >
                                Assemble {assembleDetectorRequests.length > 1 ? `${assembleDetectorRequests.length} Detectors` : "Detector"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Register Already Built Existing Detector */}
            {activeSection === "RegisterExistingDetector" && showSectionPopup && (
                <div className="sub-popup-overlay">
                    <div className="sub-popup-content" style={{ overflowY: 'unset' }}>
                        <button
                            className="close-popup-button"
                            onClick={() => {
                                setShowSectionPopup(false);
                                setAssembleDetectorId(null);
                                setAssembleMacAddress("");
                                setAssembleDetCode("");

                            }}
                        >x</button>

                        <h2>Register Existing Detector</h2>

                        <div className="inventory-assemble-dets">

                            <label>
                                <span>MAC Address</span>
                                <input
                                    type="text"
                                    placeholder="e.g. 0xfb3ee462"
                                    value={assembleMacAddress}
                                    onChange={(e) => setAssembleMacAddress(e.target.value)}
                                />
                            </label>

                            <label>
                                <span>Detector Code</span>
                                <input
                                    type="text"
                                    placeholder="e.g. GOE (Leave blank if still unknown)"
                                    value={assembleDetCode}
                                    onChange={(e) => setAssembleDetCode(e.target.value)}
                                />
                            </label>

                            <label>
                                <span>Select Detector Version</span>
                                <select
                                    value={assembleDetectorId || ""}
                                    onChange={(e) => setAssembleDetectorId(Number(e.target.value))}
                                >
                                    <option value="" disabled>-- Select Detector --</option>
                                    {detectors.map(det => (
                                        <option key={det.id} value={det.id}>
                                            {det.name} v{det.version}
                                        </option>
                                    ))}
                                </select>
                            </label>

                            {assembleDetectorId && (
                                <div className="register-existing-info">
                                    <p>
                                        The following sub-assemblies will be automatically generated and linked
                                        to this detector <strong>without consuming inventory</strong>:
                                    </p>
                                    <ul>
                                        {detectors.find(d => d.id === assembleDetectorId)?.subassemblies.map(ds => {
                                            const subObj = typeof ds.subassembly === "number"
                                                ? subAssemblies.find(s => s.id === ds.subassembly)
                                                : ds.subassembly;
                                            return subObj ? <li key={subObj.id}>{subObj.name}</li> : null;
                                        })}
                                    </ul>
                                </div>
                            )}

                            <div className="assemble-footer">
                                <button
                                    disabled={!assembleDetectorId || !assembleMacAddress.trim()}
                                    onClick={handleRegisterExistingDetector}
                                >
                                    Register Detector
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Detector BOM Flowchart */}
            {showDetBOMPopup && (
                <div className="sub-popup-overlay">
                    <div className="sub-popup-content">
                        <button
                            className="close-popup-button"
                            onClick={() => (setShowDetBOMPopup(false), setFlowOrTable('flow'))}
                        >
                            x
                        </button>

                        <h2>Detector Bill of Materials</h2>

                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <select
                                value={selectedDetector2?.id || ''} // Bind the selected value here
                                onChange={(e) => {
                                    const selected = detectors.find(d => d.id === Number(e.target.value));
                                    setSelectedDetector2(selected || null);
                                }}
                                style={{ marginBottom: "10px" }}
                                className='selecting-detec'
                            >
                                <option value="">Select Detector Version</option>
                                {detectors.map((detector) => (
                                    <option key={detector.id} value={detector.id}>
                                        {detector.name} (v{detector.version})
                                    </option>
                                ))}
                            </select>
                        </div>


                        <div className='bom-filters'>
                            <p
                                className={flowOrTable === "flow" ? "active" : ""}
                                onClick={() => setFlowOrTable("flow")}
                            >
                                Flow Chart
                            </p>
                            <p
                                className={flowOrTable === "table" ? "active" : ""}
                                onClick={() => setFlowOrTable("table")}
                            >
                                Table
                            </p>
                        </div>


                        {selectedDetector2 && flowOrTable === "flow" && (
                            <div className='flow-chart-wrapper'>
                                <ReactFlow
                                    nodes={generateFlowNodes(selectedDetector2, subAssemblies, invInfo)}
                                    edges={generateFlowEdges(selectedDetector2)}
                                    fitView
                                >
                                    {/* <MiniMap /> */}
                                    <Controls />
                                    <Background />
                                </ReactFlow>
                            </div>
                        )}

                        {selectedDetector2 && flowOrTable === "table" && (
                            <div className='bom-detector'>
                                <h1>{selectedDetector2.name} (v{selectedDetector2.version})</h1>
                                <p>Total: ${getDetectorCost(selectedDetector2, subAssemblies, invInfo).toFixed(2)}</p>

                                {selectedDetector2.subassemblies.map((ds, index) => {


                                    const sa =
                                        typeof ds.subassembly === 'object'
                                            ? ds.subassembly
                                            : subAssemblies.find(s => s.id === ds.subassembly);


                                    if (!sa) return null;

                                    return (
                                        <div key={sa.id || index} className='bom-subs'>

                                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: "1rem" }}>
                                                <>
                                                    <img
                                                        src={sa.subPicture ? sa.subPicture : fnnBlueLogo}
                                                        alt={sa.name}
                                                        className="part-image"
                                                        style={{
                                                            width: 35,
                                                            height: 35,
                                                            objectFit: 'cover',
                                                            marginRight: 8,
                                                            borderRadius: 4,
                                                        }}
                                                    />
                                                    <h3> {sa.name} - ${getSubAssemblyCost(sa, invInfo).toFixed(2)}</h3>
                                                </>

                                            </div>

                                            <div className='bom-parts'>
                                                {sa.parts.map((p, idx) => {
                                                    const invPart = invInfo.find(i => i.partID === p.part);
                                                    if (!invPart) return null;

                                                    const totalCost = (invPart.unitCost * p.quantity_required).toFixed(2);

                                                    return (
                                                        <div key={idx} className='bom-single-part' style={{ display: 'flex', alignItems: 'center', marginBottom: "0.5rem" }}>
                                                            <img
                                                                src={invPart.partPicture ? invPart.partPicture : fnnBlueLogo}
                                                                alt={invPart.partName}
                                                                className="part-image"
                                                                style={{
                                                                    width: 24,
                                                                    height: 24,
                                                                    objectFit: 'cover',
                                                                    marginRight: 8,
                                                                    borderRadius: 4,
                                                                }}
                                                            />
                                                            <span>{invPart.mpn ? `${invPart.mpn} - ` : ""}{invPart.partName} x{p.quantity_required} - ${totalCost}</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}



                    </div>
                </div>
            )}

            {/* Sub-Assembly BOM Flowchart */}
            {showBOMPopup && (
                <div className="sub-popup-overlay">
                    <div className="sub-popup-content">
                        <button
                            className="close-popup-button"
                            onClick={() => {
                                setShowBOMPopup(false);
                                setSelectedSubAssembly(null);
                                setFlowOrTableSub('flow');
                            }}
                        >
                            x
                        </button>

                        <h2>Sub-Assembly Bill of Materials</h2>

                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <select
                                value={selectedSubAssembly?.id || ''}
                                onChange={(e) => {
                                    const selected = subAssemblies.find(s => s.id === Number(e.target.value));
                                    setSelectedSubAssembly(selected || null);
                                }}
                                style={{ marginBottom: "10px" }}
                                className='selecting-detec'
                            >
                                <option value="">Select Sub-Assembly</option>
                                {subAssemblies.map(sa => (
                                    <option key={sa.id} value={sa.id}>
                                        {sa.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className='bom-filters'>
                            <p
                                className={flowOrTableSub === "flow" ? "active" : ""}
                                onClick={() => setFlowOrTableSub("flow")}
                            >
                                Flow Chart
                            </p>
                            <p
                                className={flowOrTableSub === "table" ? "active" : ""}
                                onClick={() => setFlowOrTableSub("table")}
                            >
                                Table
                            </p>
                        </div>

                        {selectedSubAssembly && flowOrTableSub === "flow" && (
                            <div className='flow-chart-wrapper'>
                                <ReactFlow
                                    nodes={generateSubAssemblyNodes(selectedSubAssembly, invInfo)}
                                    edges={generateSubAssemblyEdges(selectedSubAssembly)}
                                    fitView
                                >
                                    <Controls />
                                    <Background />
                                </ReactFlow>
                            </div>
                        )}

                        {selectedSubAssembly && flowOrTableSub === "table" && (
                            <div className='bom-detector'>

                                <div style={{ display: 'flex', alignItems: 'center', marginBottom: "0.5rem" }}>
                                    <>
                                        <img
                                            src={selectedSubAssembly.subPicture ? selectedSubAssembly.subPicture : fnnBlueLogo}
                                            alt={selectedSubAssembly.name}
                                            className="part-image"
                                            style={{
                                                width: 35,
                                                height: 35,
                                                objectFit: 'cover',
                                                marginRight: 8,
                                                borderRadius: 4,
                                            }}
                                        />
                                        <h3> {selectedSubAssembly.name}</h3>
                                    </>
                                </div>



                                <p>Total: ${getSubAssemblyCost(selectedSubAssembly, invInfo).toFixed(2)}</p>
                                <div>
                                    {selectedSubAssembly.parts.map((p, idx) => {
                                        const invPart = invInfo.find(i => i.partID === p.part);
                                        if (!invPart) return null;

                                        const totalCost = (invPart.unitCost * p.quantity_required).toFixed(2);

                                        return (
                                            <div
                                                key={idx}
                                                className='bom-single-part'
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    marginBottom: "0.5rem"
                                                }}
                                            >
                                                <img
                                                    src={invPart.partPicture ? invPart.partPicture : fnnBlueLogo}
                                                    alt={invPart.partName}
                                                    className="part-image"
                                                    style={{
                                                        width: 24,
                                                        height: 24,
                                                        objectFit: 'cover',
                                                        marginRight: 8,
                                                        borderRadius: 4,
                                                    }}
                                                />
                                                <span>{invPart.mpn ? `${invPart.mpn} - ` : ""}{invPart.partName} x{p.quantity_required} - ${totalCost}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Adding an item order */}
            {showAddOrderPopup && (
                <div className="active-products-popup-overlay">
                    <div className="active-products-popup-content">

                        <h2 className="edit-org-popup-header">
                            {editingForecastId ? 'Edit Order' : 'Create Order Request'}
                        </h2>

                        <p>
                            {editingForecastId
                                ? "Modify an existing order."
                                : "Create a new order request."}
                        </p>

                        <div className="edit-org-form">

                            {/* Part Image + Name Row */}
                            <div className="customer-edit-logo-name full-width-section">
                                <div className="customer-edit-img">
                                    {forecastPartImage ? (
                                        <img
                                            src={forecastPartImage}
                                            alt={forecastPartName || "Part Image"}
                                            className="customer-edit-img-img"
                                            onError={(e) => {
                                                (e.currentTarget as HTMLImageElement).src = "";
                                            }}
                                        />
                                    ) : (
                                        <div className="customer-edit-img-placeholder">
                                            <AiFillPicture size={30} />
                                        </div>
                                    )}
                                </div>

                            </div>

                            {/* Part Name Search */}
                            <label className="customer-edit-org-name full-width-section" style={{ position: "relative" }}>
                                <strong>Part Name:</strong>
                                <input
                                    type="text"
                                    placeholder="Search name or MPN"
                                    value={partSearchTerm}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setPartSearchTerm(value);
                                        const match = invInfo.find(p => p.partName.toLowerCase() === value.toLowerCase());
                                        if (!match) {
                                            setForecastCost(0);
                                            setForecastStoreLinks('');
                                            setForecastPartID(null);
                                            setForecastPartName(value);
                                            setShowDropdown(true);
                                        }
                                    }}
                                    onFocus={() => setShowDropdown(true)}
                                    onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                                    className={invalidFields.includes("part") ? "input-error" : ""}
                                />

                                {showDropdown && (
                                    <ul className="autocomplete-dropdown order-dropdown-ext">
                                        {filteredPartOptions.map((item) => (
                                            <li
                                                key={item.partID}
                                                onClick={() => {
                                                    setForecastPartName(item.partName);
                                                    setForecastPartID(item.partID);
                                                    setPartSearchTerm(item.partName);
                                                    setForecastCost(item.unitCost ?? 0);
                                                    setForecastStoreLinks(item.partLink ?? '');
                                                    setForecastPartImage(item.partPicture ?? null);
                                                    setForecastIsPacket(item.sold_in_packets ?? false);
                                                    setForecastAmountPerPacket(item.amount_per_packet ?? 0);
                                                    setForecastNumberOfPackets(0);
                                                    setForecastAmount(0);
                                                    setForecastPackCost(0);
                                                    setShowDropdown(false);
                                                }}
                                            >
                                                <img className='part-image' src={item.partPicture ? item.partPicture : fnnBlueLogo} alt={item.partName} />
                                                {truncateText(((item.mpn ? (item.mpn + " - ") : "") + item.partName), 40)}
                                            </li>
                                        ))}
                                        {filteredPartOptions.length === 0 && (
                                            <li style={{ color: '#999' }}>No matches found</li>
                                        )}
                                        <button className='inv-order-btn order-btn2'
                                            onClick={() => setShowAddItemPopup(true)}
                                            style={{ margin: '1rem 0.5rem' }}
                                        >
                                            Don&apos;t see my part</button>
                                    </ul>
                                )}
                            </label>

                            {/* Order Comes in Packs */}
                            <label>
                                <strong>Order Comes in Packs?</strong>
                                <input
                                    type="checkbox"
                                    checked={forecastIsPacket}
                                    onChange={(e) => {
                                        const checked = e.target.checked;
                                        setForecastIsPacket(checked);
                                        if (!checked) {
                                            setForecastNumberOfPackets(0);
                                            setForecastAmountPerPacket(0);
                                            setForecastPackCost(0);
                                            const selectedPart = invInfo.find(p => p.partID === forecastPartID);
                                            setForecastCost(selectedPart?.unitCost ?? 0);
                                        } else {
                                            const selectedPart = invInfo.find(p => p.partID === forecastPartID);
                                            setForecastAmountPerPacket(selectedPart?.amount_per_packet ?? 0);
                                            setForecastNumberOfPackets(0);
                                            setForecastAmount(0);
                                            setForecastPackCost(0);
                                        }
                                    }}
                                />
                            </label>

                            {/* Amount / Packet Fields */}
                            {!forecastIsPacket ? (
                                <label>
                                    <strong>Amount:</strong>
                                    <input
                                        type="number"
                                        onWheel={(e) => e.currentTarget.blur()}
                                        value={forecastAmount}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setForecastAmount(val === '' ? 0 : parseInt(val));
                                        }}
                                        className={invalidFields.includes("amount") ? "input-error" : ""}
                                    />
                                </label>
                            ) : (
                                <>
                                    <label>
                                        <strong>Number of Packs:</strong>
                                        <input
                                            type="number"
                                            onWheel={(e) => e.currentTarget.blur()}
                                            value={forecastNumberOfPackets}
                                            onChange={(e) => {
                                                const num = parseInt(e.target.value || "0");
                                                setForecastNumberOfPackets(num);
                                                setForecastAmount(num * forecastAmountPerPacket);
                                            }}
                                        />
                                    </label>

                                    <label>
                                        <strong>Amount per Pack:</strong>
                                        <input
                                            type="number"
                                            onWheel={(e) => e.currentTarget.blur()}
                                            value={forecastAmountPerPacket}
                                            onChange={(e) => {
                                                const num = parseInt(e.target.value || "0");
                                                setForecastAmountPerPacket(num);
                                                setForecastAmount(num * forecastNumberOfPackets);
                                                if (forecastPackCost > 0 && num > 0) {
                                                    setForecastCost(parseFloat((forecastPackCost / num).toFixed(2)));
                                                }
                                            }}
                                        />
                                    </label>

                                    <label>
                                        <strong>Total Amount:</strong>
                                        <input
                                            type="number"
                                            onWheel={(e) => e.currentTarget.blur()}
                                            value={forecastAmount}
                                            disabled
                                            readOnly
                                            style={{ backgroundColor: '#f0f0f0', cursor: "not-allowed" }}
                                        />
                                    </label>

                                    <label>
                                        <strong>Pack Cost:</strong>
                                        <input
                                            type="number"
                                            onWheel={(e) => e.currentTarget.blur()}
                                            step="0.01"
                                            value={forecastPackCost}
                                            onChange={(e) => {
                                                const packCost = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                                setForecastPackCost(packCost);
                                                if (forecastAmountPerPacket > 0) {
                                                    setForecastCost(parseFloat((packCost / forecastAmountPerPacket).toFixed(2)));
                                                }
                                            }}
                                        />
                                    </label>
                                </>
                            )}

                            {/* Unit Cost */}
                            <label>
                                <strong>Unit Cost:</strong>
                                <input
                                    type="number"
                                    onWheel={(e) => e.currentTarget.blur()}
                                    step="0.01"
                                    value={forecastCost}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setForecastCost(val === '' ? 0 : parseFloat(val));
                                    }}
                                    className={invalidFields.includes("cost") ? "input-error" : ""}
                                    disabled={forecastIsPacket}
                                    readOnly={forecastIsPacket}
                                    style={forecastIsPacket ? { backgroundColor: "#f0f0f0", cursor: "not-allowed" } : {}}
                                />
                            </label>

                            {/* Edit-only fields */}
                            {editingForecastId && (
                                <>
                                    <label>
                                        <strong>Order Date:</strong>
                                        <input
                                            type="date"
                                            value={forecastOrderedDate}
                                            onChange={(e) => setForecastOrderedDate(e.target.value)}
                                        />
                                    </label>

                                    <label>
                                        <strong>Expected By:</strong>
                                        <input
                                            type="date"
                                            value={forecastExpectedArrivalDate}
                                            onChange={(e) => setForecastExpectedArrivalDate(e.target.value)}
                                        />
                                    </label>

                                    <label>
                                        <strong>Arrival Date:</strong>
                                        <input
                                            type="date"
                                            value={forecastArrivalDate}
                                            onChange={(e) => setForecastArrivalDate(e.target.value)}
                                        />
                                    </label>

                                    <label>
                                        <strong>Status:</strong>
                                        <select
                                            value={forecastStatus}
                                            onChange={(e) => {
                                                const newStatus = e.target.value;
                                                setForecastStatus(newStatus);
                                                const today = new Date().toISOString().substring(0, 10);
                                                if (newStatus === "Ordered") setForecastOrderedDate(today);
                                                if (newStatus === "Arrived") setForecastArrivalDate(today);
                                            }}
                                            className={invalidFields.includes("status") ? "input-error" : ""}
                                        >
                                            <option value="Requested">Requested</option>
                                            <option value="Ordered">Ordered</option>
                                            <option value="Cancelled">Cancelled</option>
                                            <option value="Returned">Returned</option>
                                            <option value="Arrived">Arrived</option>
                                        </select>
                                    </label>
                                </>
                            )}

                            {/* Always-visible fields */}
                            {!editingForecastId && (
                                <label>
                                    <strong>Status:</strong>
                                    <select
                                        value={forecastStatus}
                                        onChange={(e) => {
                                            const newStatus = e.target.value;
                                            setForecastStatus(newStatus);
                                            const today = new Date().toISOString().substring(0, 10);
                                            if (newStatus === "Ordered") setForecastOrderedDate(today);
                                            if (newStatus === "Arrived") setForecastArrivalDate(today);
                                        }}
                                        className={invalidFields.includes("status") ? "input-error" : ""}
                                    >
                                        <option value="Requested">Requested</option>
                                        <option value="Ordered">Ordered</option>
                                        <option value="Cancelled">Cancelled</option>
                                        <option value="Returned">Returned</option>
                                        <option value="Arrived">Arrived</option>
                                    </select>
                                </label>
                            )}

                            <label>
                                <strong>Order Priority:</strong>
                                <select
                                    value={forecastPriority}
                                    onChange={(e) => setForecastPriority(e.target.value)}
                                >
                                    <option value="Low">Low</option>
                                    <option value="Medium">Medium</option>
                                    <option value="High">High</option>
                                </select>
                            </label>

                            <label
                                className={
                                    editingForecastId
                                        ? (!forecastIsPacket ? "full-width-section" : "")
                                        : (forecastIsPacket ? "full-width-section" : "")
                                }
                            >
                                <strong>Lead Time:</strong>
                                <input
                                    type="text"
                                    value={forecastLeadTime}
                                    onChange={(e) => setForecastLeadTime(e.target.value)}
                                    placeholder="How long it will take the manufacturer to produce this item"
                                />
                            </label>

                            <label className="full-width-section">
                                <strong>Store Links:</strong>
                                <textarea
                                    placeholder='E.g "www.storewebsite.com, ..."'
                                    value={forecastStoreLinks}
                                    onChange={(e) => setForecastStoreLinks(e.target.value)}
                                />
                            </label>

                            <label className="full-width-section">
                                <strong>Batch Item is Being Ordered For:</strong>
                                <select
                                    value={forecastBatchId ?? ""}
                                    onChange={(e) =>
                                        setForecastBatchId(e.target.value ? Number(e.target.value) : null)
                                    }
                                >
                                    <option value="">— No Batch —</option>
                                    {batches
                                        .filter(b => b.status !== 'completed' && b.status !== 'cancelled')
                                        .map(b => (
                                            <option key={b.batchID} value={b.batchID}>
                                                {b.batch_name}
                                            </option>
                                        ))}
                                </select>
                            </label>

                            <label className="full-width-section">
                                <strong>Notes:</strong>
                                <textarea
                                    placeholder='E.g "Item shipment was delayed"'
                                    value={forecastNotes}
                                    onChange={(e) => setForecastNotes(e.target.value)}
                                />
                            </label>

                        </div>

                        {/* Footer Actions */}
                        <div className="hh-duo hh-actions edit-org-popup-footer">
                            <button
                                className="cancel-btn"
                                onClick={() => {
                                    setShowAddOrderPopup(false);
                                    clearForecastForm();
                                    setInvalidFields([]);
                                }}
                            >
                                Cancel
                            </button>
                            {editingForecastId && (
                                <button
                                    className='hh-delete-btn'
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setForecastToDeleteId(editingForecastId);
                                        setShowDeleteForecastPopup(true);
                                    }}
                                >
                                    Delete
                                </button>
                            )}

                            <button className="confirm-btn" onClick={editOrAddForecast}>
                                {editingForecastId ? 'Save' : 'Confirm'}
                            </button>

                        </div>

                    </div>
                </div>
            )}

            {showDeleteForecastPopup && (
                <div className='add-item-popup-overlay'>
                    <div className='add-item-popup-content del-pop'>
                        <div className='inv-delete-inner-pop'>
                            <h3>Are You Sure You Want to Delete This Order?</h3>

                            <div className='inv-delete-buttons-div'>
                                <button onClick={() => {
                                    setShowDeleteForecastPopup(false);
                                    setForecastToDeleteId(null);
                                    setShowAddOrderPopup(false);
                                }}>
                                    Cancel
                                </button>
                                <button
                                    onClick={() => {
                                        if (forecastToDeleteId !== null) {
                                            handleDeleteForecast(forecastToDeleteId);
                                            setShowAddOrderPopup(false);
                                            clearForecastForm();
                                            setInvalidFields([]);

                                            toast.success("Order successfully deleted", {
                                                autoClose: 4000,
                                                hideProgressBar: false,
                                                closeOnClick: true,
                                                pauseOnHover: true,
                                                draggable: true,
                                                style: {
                                                    backgroundColor: 'white',
                                                    color: 'black',
                                                    fontSize: '16px',
                                                },
                                            });
                                        }
                                    }}
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Parts for X more detectors popup */}
            {activeSection === "PartsForXMore" && showSectionPopup && (
                <div className="sub-popup-overlay">
                    <div className="stock-or-count-popup-content">

                        {/* Header */}
                        <div className="stock-or-count-popup-header">
                            <h1>Parts for More Detectors</h1>

                            <button
                                className="new-close-btn"
                                onClick={() => {
                                    setShowSectionPopup(false);
                                    setNextXDetectors(1);
                                    setShowTypeOrderPopup(false);
                                }}
                            >
                                x
                            </button>
                        </div>

                        <div className='x-more-selectors'>

                            {/* Input Row */}
                            <div className="parts-req-and-input">
                                <p>Parts for</p>

                                <input
                                    type="number"
                                    onWheel={(e) => e.currentTarget.blur()}
                                    min={1}
                                    value={nextXDetectors}
                                    onChange={(e) => setNextXDetectors(Number(e.target.value))}
                                    className="parts-req-and-input-input"
                                />

                                <p>Additional Detector(s)</p>
                            </div>

                            <select
                                value={selectedDetectorId ?? ''}
                                onChange={e => setSelectedDetectorId(Number(e.target.value))}
                                className="detector-version-select"
                            >
                                <option value="" disabled>Select Detector Version</option>
                                {detectors.map(detector => (
                                    <option key={detector.id} value={detector.id}>
                                        {detector.name} v{detector.version}
                                    </option>
                                ))}
                            </select>

                        </div>

                        {!isMobile ? (
                            <>
                                {/* Table Header */}
                                <div className="table-scroll-wrapper">
                                    <table className="stock-or-count-popup-table">
                                        <thead>
                                            <tr>
                                                <th>Part Name</th>
                                                <th>Necessary</th>
                                                <th>Forecasted</th>
                                                <th><div className="column-row-div">Request</div></th>
                                            </tr>
                                        </thead>
                                    </table>
                                </div>

                                {/* Table Body */}
                                <div className="table-scroll-container">
                                    <table className="stock-or-count-popup-table">
                                        <tbody>
                                            {Object.entries(partsNeededForNext).map(([partID, quantity]) => {
                                                const part = invInfo.find(item => item.partID === Number(partID));
                                                const forecastedAmount = forecastAmountsByPart[Number(partID)] || 0;

                                                return (
                                                    <tr key={partID}>
                                                        <td>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                                <img
                                                                    className='part-image'
                                                                    src={part?.partPicture || fnnBlueLogo}
                                                                    alt={part?.partName}
                                                                />
                                                                {renderPartName(part, false)}
                                                            </div>
                                                        </td>

                                                        <td>{quantity}</td>

                                                        <td>{forecastedAmount}</td>

                                                        <td>
                                                            {(() => {
                                                                if (!part) return null;

                                                                const orderInfo = getOrderToAdequate(
                                                                    part,
                                                                    desiredPartsNeeded
                                                                );

                                                                return (
                                                                    <div className="column-row-div">
                                                                        <div
                                                                            className="delete-part-button reorder-button"
                                                                            onClick={() => {
                                                                                const part = invInfo.find(item => item.partID === Number(partID));
                                                                                const packetsNeeded = part?.sold_in_packets && part?.amount_per_packet
                                                                                    ? Math.ceil(quantity / part.amount_per_packet)
                                                                                    : 0;
                                                                                const adjustedQty = part?.sold_in_packets && part?.amount_per_packet
                                                                                    ? packetsNeeded * part.amount_per_packet
                                                                                    : quantity;

                                                                                setActiveRequestPartID(Number(partID));
                                                                                setRequestQuantity(adjustedQty); // Adjusts based on the number of packets and packed amount
                                                                                setRequestPackets(packetsNeeded);
                                                                                setShowRequestItemPopup(true);
                                                                            }}
                                                                        >
                                                                            <IoBagAdd />
                                                                        </div>


                                                                    </div>
                                                                );
                                                            })()}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </>


                        ) : (

                            <div className="mobile-forecast-list mobile-stock-list">
                                {Object.entries(partsNeededForNext).map(([partID, quantity]) => {
                                    const part = invInfo.find(item => item.partID === Number(partID));
                                    const forecastedAmount = forecastAmountsByPart[Number(partID)] || 0;
                                    const isExpanded = expandedPartsId === Number(partID);

                                    return (
                                        <React.Fragment key={partID}>
                                            {/* ROW */}
                                            <div
                                                className="mobile-part-row2"
                                                onClick={() =>
                                                    setExpandedPartsId(
                                                        expandedPartsId === Number(partID) ? null : Number(partID)
                                                    )
                                                }
                                            >
                                                <div className="part-info-cell2">
                                                    <div className="part-info-cell-half1">
                                                        <img
                                                            src={part?.partPicture || fnnBlueLogo}
                                                            alt={part?.partName}
                                                            className="part-image"
                                                        />
                                                        <span>{renderPartName(part, false)}</span>
                                                    </div>

                                                    <span style={{ fontWeight: 500 }}>
                                                        {quantity}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* EXPANDED */}
                                            {isExpanded && (
                                                <div className="mobile-details-row2">

                                                    <div className="mobile-detail-group">
                                                        <strong>Necessary:</strong> {quantity}
                                                    </div>

                                                    <div className="mobile-detail-group">
                                                        <strong>Forecasted:</strong> {forecastedAmount}
                                                    </div>

                                                    <div className="mobile-detail-group">
                                                        <strong>Request:</strong>
                                                        {part && (
                                                            <div
                                                                className="delete-part-button reorder-button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();

                                                                    const packetsNeeded =
                                                                        part.sold_in_packets && part.amount_per_packet
                                                                            ? Math.ceil(quantity / part.amount_per_packet)
                                                                            : 0;

                                                                    const adjustedQty =
                                                                        part.sold_in_packets && part.amount_per_packet
                                                                            ? packetsNeeded * part.amount_per_packet
                                                                            : quantity;

                                                                    setActiveRequestPartID(Number(partID));
                                                                    setRequestQuantity(adjustedQty);
                                                                    setRequestPackets(packetsNeeded);
                                                                    setShowRequestItemPopup(true);
                                                                    setExpandedPartsId(null);
                                                                }}
                                                            >
                                                                <IoBagAdd />
                                                            </div>
                                                        )}
                                                    </div>

                                                </div>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </div>

                        )}

                        {/* Footer Action */}
                        <div className="request-all-parts-wrapper">
                            <div
                                className="place-request-button"
                                onClick={async () => {
                                    const entries = Object.entries(partsNeededForNext);

                                    await Promise.all(
                                        entries.map(async ([id, qty]) => {
                                            const part = invInfo.find(item => item.partID === Number(id));

                                            const adjustedQty =
                                                part?.sold_in_packets && part?.amount_per_packet
                                                    ? Math.ceil((qty as number) / part.amount_per_packet) * part.amount_per_packet
                                                    : (qty as number);

                                            await handlePartForecastRequest(Number(id), adjustedQty, true);
                                        })
                                    );

                                    toast.success("All items requested successfully!", toastStyle);
                                }}
                            >
                                Request All
                            </div>
                        </div>

                    </div>
                </div>
            )}

            {/* Analytics Popup */}
            {activeSection === "Analytics" && showSectionPopup && (
                <div className="sub-popup-overlay">
                    <div className="sub-popup-content">

                        <button
                            className="close-popup-button"
                            onClick={() => setShowSectionPopup(false)}
                        >
                            x
                        </button>

                        <h2>
                            Analytics ({formatDate(firstDayOfMonth)} - {formatDate(today)})
                        </h2>


                        <div className='inv-analytics-card-stack' ref={reportRef}>

                            <div className='inv-analytics-card iac1'>

                                <div className='inv-analytics-icon-and-title'>
                                    <h1>Budget</h1>
                                    <div className="analytics-icon-wrapper ic1">
                                        <GiTwoCoins className='analytics-icon ic1' />
                                    </div>
                                </div>

                                <div className='inv-analytics-inner'>
                                    <p><strong> • Value of Full Inventory:</strong> ${getFullInventoryCost(invInfo).toFixed(2)}</p>
                                    <p><strong> • Cost of Fulfilled Orders this Month:</strong> ${getFulfilledOrdersCostThisMonth(forecastInfo).toFixed(2)}</p>
                                    <p><strong> • Most Expensive Item in Inventory:</strong> {getMostExpensiveItem(invInfo)}</p>

                                    <div className='inv-analytics-table-and-title'>
                                        {subAssemblyProduction.length === 0 ? (
                                            <p><strong> • Value of Sub-Assemblies Produced this Month:</strong> N/A</p>
                                        ) : (
                                            <>
                                                <p><strong> • Value of Sub-Assemblies Produced this Month:</strong></p>
                                                <table className="inventory-table analytic-table-header">
                                                    <thead className='main-thead'>
                                                        <tr>
                                                            <th>SubAssembly</th>
                                                            <th>Amount</th>
                                                            <th>Unit Cost</th>
                                                            <th>Total Cost</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {subAssemblyProduction.map((item, index) => {
                                                            const subAssembly = subAssemblies.find(s => s.id === item.object_id);
                                                            if (!subAssembly) return null;

                                                            const unitCost = getSubAssemblyCost(subAssembly, invInfo);
                                                            const totalCost = unitCost * item.amount_produced;

                                                            return (
                                                                <tr key={index}>
                                                                    <td>{item.object_name}</td>
                                                                    <td>{item.amount_produced}</td>
                                                                    <td>${unitCost.toFixed(2)}</td>
                                                                    <td>${totalCost.toFixed(2)}</td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                                <p style={{ paddingLeft: '1rem' }}><strong>Total from Sub-Assemblies: </strong>${totalSubAssemblyProductionCost.toFixed(2)}</p>
                                            </>
                                        )}
                                    </div>

                                    <div style={{ display: 'flex', gap: '0.8rem', alignItems: 'center', paddingTop: '1rem' }}>
                                        <TbReportMoney style={{ width: '1.5rem', height: '1.5rem' }} />
                                        <p style={{ width: '98%' }}>
                                            A total of <strong style={{ color: '#28aee1' }}>${totalSubAssemblyProductionCost.toFixed(2)}</strong> dollars were
                                            consumed from the inventory producing Sub-Assemblies.
                                        </p>
                                    </div>
                                </div>


                            </div>

                            <div className='inv-analytics-card iac2'>

                                <div className='inv-analytics-icon-and-title'>
                                    <h1>Orders</h1>
                                    <div className="analytics-icon-wrapper ic2">
                                        <BsBoxSeamFill className='analytics-icon ic2' />
                                    </div>
                                </div>

                                <div className='inv-analytics-inner'>
                                    <p> <strong> • Orders Placed this Month:</strong> {getOrdersPlacedThisMonth(forecastInfo)}</p>
                                    <p> <strong> • Orders Fulfilled this Month:</strong> {getFulfilledOrdersCountThisMonth(forecastInfo)}</p>
                                    <p> <strong> • Items Ordered this Month:</strong> {getItemsOrderedThisMonth(forecastInfo)}</p>
                                    <p> <strong> • Most Ordered Item this Month (By Amount):</strong> {getMostOrderedItemByAmount(forecastInfo, invInfo)}</p>
                                    <p> <strong> • Most Ordered Item this Month (By Orders):</strong> {getMostOrderedItemByOrders(forecastInfo, invInfo)}</p>
                                </div>


                            </div>

                        </div>

                        <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                            <button
                                onClick={() => handlePrint(reportRef, `Monthly_Inventory_Report_${new Date().toISOString().split('T')[0]}`)}
                                className="place-request-button print-rep-button"
                            >
                                Print Monthly Report
                            </button>
                        </div>


                    </div>
                </div>
            )}
            {/* Request Item Popup */}
            {showRequestItemPopup && activeRequestPartID !== null && (() => {
                const part = invInfo.find(p => p.partID === activeRequestPartID);
                const isPacket = !!part?.sold_in_packets && !!part?.amount_per_packet;

                return (
                    <div className="add-item-popup-overlay">
                        <div className="add-item-popup-content inv-inner-pop-extension">
                            <div className="inv-small-pop">

                                <div className="hh-delete-text-and-icon" style={{ width: '100%' }}>

                                    <div className="marketing-finish-icon-div">
                                        <IoBagAdd className="inv-count-icon" />
                                    </div>

                                    <div style={{ width: '100%' }}>
                                        <h3 style={{ fontWeight: '400', marginBottom: '1rem' }}>
                                            Request {part?.mpn ? `${part.mpn} - ` : ""}{part?.partName}
                                        </h3>

                                        {/* Packet Mode */}
                                        {isPacket ? (
                                            <>
                                                <label style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '0.8rem' }}>
                                                    <span>Packs to Order</span>
                                                    <input
                                                        type="number"
                                                        onWheel={(e) => e.currentTarget.blur()}
                                                        value={requestPackets}
                                                        min={1}
                                                        onChange={(e) => {
                                                            const pkts = Number(e.target.value);
                                                            setRequestPackets(pkts);
                                                            setRequestQuantity(pkts * (part?.amount_per_packet ?? 1));
                                                        }}
                                                        style={{ padding: '0.4rem', fontSize: '1rem' }}
                                                    />
                                                </label>

                                                <div style={{ fontSize: '0.95rem', opacity: 0.8 }}>
                                                    {requestPackets} × {part?.amount_per_packet} = {requestQuantity} units
                                                </div>
                                            </>
                                        ) : (
                                            <label style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                                                <span>Amount to Order</span>
                                                <input
                                                    type="number"
                                                    onWheel={(e) => e.currentTarget.blur()}
                                                    value={requestQuantity}
                                                    min={1}
                                                    onChange={(e) => setRequestQuantity(Number(e.target.value))}
                                                    style={{ padding: '0.4rem', fontSize: '1rem' }}
                                                />
                                            </label>
                                        )}
                                    </div>
                                </div>

                                <div>
                                    <strong>Note:</strong>{" "}
                                    {activeSection === "PartsForXMore"
                                        ? <>The prefilled amount represents the parts needed to build <strong>{nextXDetectors}</strong> additional detector(s) based on the selected version&apos;s BOM.</>
                                        : <>The prefilled amount showed in the input box represents the suggested purchase size to make the stock <strong style={{ color: 'green' }}>Adequate</strong>.</>
                                    }
                                </div>

                                <div className="hh-duo hh-actions">
                                    <button
                                        className="cancel-btn"
                                        onClick={() => setShowRequestItemPopup(false)}
                                    >
                                        Cancel
                                    </button>

                                    <button
                                        className="confirm-btn"
                                        onClick={() => {
                                            if (activeRequestPartID !== null) {
                                                handlePartForecastRequest(activeRequestPartID, requestQuantity);
                                                setShowRequestItemPopup(false);
                                            }
                                        }}
                                    >
                                        Confirm
                                    </button>
                                </div>

                            </div>
                        </div>
                    </div>
                );
            })()}

            {showArrivalItemPopup && activeForecast && (
                <div className='add-item-popup-overlay'>
                    <div className='add-item-popup-content'>

                        <button
                            className="close-popup-button"
                            onClick={() => setShowArrivalItemPopup(false)}
                        >
                            x
                        </button>

                        <div className="add-new-part-form">
                            <h3>
                                {isPacketOrder ? "How Many Packs Arrived?" : "How Many Arrived?"}
                            </h3>


                            <div className='add-new-part-table add-part-ext'>
                                <label>
                                    <span>Part Name</span>
                                    {invInfo.find(p => p.partID === activeForecast.part)?.partName}
                                </label>
                            </div>


                            <div className='add-new-part-table add-part-ext'>
                                {isPacketOrder ? (
                                    <label>
                                        <span>Packs</span>
                                        <input
                                            type="number"
                                            onWheel={(e) => e.currentTarget.blur()}
                                            min={1}
                                            max={activeForecast.number_of_packets ?? undefined}
                                            value={arrivedPackets}
                                            onChange={(e) => {
                                                const packets = Number(e.target.value);
                                                setArrivedPackets(packets);

                                                // 🔁 auto-calculate unit amount
                                                setArrivalAmount(
                                                    packets * (activeForecast.amount_per_packet ?? 1)
                                                );
                                            }}
                                        />
                                        <small>
                                            {activeForecast.amount_per_packet} units per pack
                                        </small>
                                    </label>
                                ) : (
                                    <label>
                                        <span>Amount</span>
                                        <input
                                            type="number"
                                            onWheel={(e) => e.currentTarget.blur()}
                                            min={1}
                                            max={activeForecast.expectedAmount}
                                            value={arrivalAmount}
                                            onChange={(e) => setArrivalAmount(Number(e.target.value))}
                                        />
                                    </label>
                                )}


                            </div>

                            <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                                <button
                                    onClick={() => {
                                        if (!isArrivalAmountValid()) {
                                            toast.warn("Please enter a valid arrival amount.");
                                            return;
                                        }
                                        setShowConfirmProceedPopup(true);
                                    }}
                                >
                                    Confirm
                                </button>

                            </div>
                        </div>

                    </div>
                </div>
            )}

            {/* CONFIMATION OF ORDER STATUS POPUP */}
            {showOrderItemPopup && activeForecast && (
                <div className='add-item-popup-overlay'>
                    <div className='add-item-popup-content'>
                        <button
                            className="close-popup-button"
                            onClick={() => (setShowOrderItemPopup(false), setForecastExpectedArrivalDate(''))}
                        >
                            x
                        </button>

                        <div className="add-new-part-form">
                            <h3>
                                {isPacketOrder ? "How Many Packs to Order?" : "How Many to Order?"}
                            </h3>

                            <div className='add-new-part-table add-part-ext'>
                                <label>
                                    <span>Part Name</span>
                                    {invInfo.find(p => p.partID === activeForecast.part)?.partName}
                                </label>

                                <label>
                                    <span>Expected Arrival</span>
                                    <input
                                        type="date"
                                        value={forecastExpectedArrivalDate}
                                        onChange={(e) => setForecastExpectedArrivalDate(e.target.value)}
                                        required
                                    />
                                </label>
                            </div>

                            <div className='add-new-part-table add-part-ext'>
                                {isPacketOrder ? (
                                    <label>
                                        <span>Packs</span>
                                        <input
                                            type="number"
                                            onWheel={(e) => e.currentTarget.blur()}
                                            min={1}
                                            max={activeForecast.number_of_packets ?? undefined}
                                            value={orderedPackets}
                                            onChange={(e) => {
                                                const packets = Number(e.target.value);
                                                setOrderedPackets(packets);

                                                // 🔁 auto-calculate unit amount
                                                setOrderedAmount(
                                                    packets * (activeForecast.amount_per_packet ?? 1)
                                                );
                                            }}
                                        />
                                        <small>
                                            {activeForecast.amount_per_packet} units per pack
                                        </small>
                                    </label>
                                ) : (
                                    <label>
                                        <span>Amount</span>
                                        <input
                                            type="number"
                                            onWheel={(e) => e.currentTarget.blur()}
                                            min={1}
                                            max={activeForecast.expectedAmount}
                                            value={orderedAmount}
                                            onChange={(e) => setOrderedAmount(Number(e.target.value))}
                                        />
                                    </label>
                                )}
                            </div>

                            <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                                <button
                                    onClick={async () => {
                                        if (!isOrderAmountValid()) {
                                            toast.warn("Please enter a valid order amount.");
                                            return;
                                        }

                                        // ✅ NEW: enforce expected arrival date
                                        if (!forecastExpectedArrivalDate) {
                                            toast.warn("Expected arrival date is required when placing order.");
                                            return;
                                        }

                                        const success = await handleMarkOrdered();
                                        if (success) {
                                            setShowOrderItemPopup(false);
                                            setForecastExpectedArrivalDate('');
                                        }
                                    }}
                                >
                                    Confirm Order
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* SECOND CONFIRMATION POPUP */}
            {showConfirmProceedPopup && (
                <div className="add-item-popup-overlay">
                    <div className="add-item-popup-content second-confirm-forecast-popup-outer">

                        <div className='add-new-part-form'>


                            <h3>Confirm Arrival</h3>

                            <div className='second-confirm-forecast-popup'>
                                <p>
                                    You are about to mark{" "}
                                    <strong>
                                        {isPacketOrder
                                            ? `${arrivedPackets} pack${arrivedPackets !== 1 ? "s" : ""} (${arrivalAmount} units)`
                                            : `${arrivalAmount} item${arrivalAmount !== 1 ? "s" : ""}`}
                                    </strong>{" "}
                                    as arrived.
                                    This amount will be automatically appended to the inventory.
                                    Make sure the amount is correct.
                                </p>


                                <div style={{ display: "flex", justifyContent: "center", gap: "1rem" }}>
                                    <button
                                        onClick={() => setShowConfirmProceedPopup(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={async () => {
                                            const success = await handleMarkArrived();

                                            if (!success) return; // ⛔ DO NOT PROCEED

                                            setShowConfirmProceedPopup(false);
                                            setShowArrivalItemPopup(false);

                                            const matchingPart = invInfo.find(p => p.partID === activeForecast?.part);
                                            setActiveLocation(matchingPart?.location || "Unknown Location");
                                            setActiveLocationPicture(matchingPart?.location_picture);
                                            setShowLocationPopup(true);
                                        }}
                                    >
                                        Proceed
                                    </button>



                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            )}

            {/* POPUP TO SHOW WHERE TO STORE ITEM */}
            {showLocationPopup && (
                <div className="add-item-popup-overlay">
                    <div className="add-item-popup-content second-confirm-forecast-popup-outer">
                        <div className="add-new-part-form">
                            <h3>Storage Reminder</h3>

                            <div className="second-confirm-forecast-popup">
                                <p>
                                    Location: <strong>{activeLocation}</strong><br />
                                    Please take this time to store it at its proper location.
                                </p>

                                <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
                                    {activeLocationPicture ? (
                                        <img
                                            className="location-picture-in-popup"
                                            src={activeLocationPicture}
                                            alt={activeLocationPicture}
                                        />
                                    ) : (
                                        <div
                                            style={{
                                                textAlign: "center",
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}
                                        >
                                            <AiFillPicture
                                                className="location-picture-in-popup"
                                                style={{ fontSize: "6rem", color: "#555" }}
                                            />
                                            <p
                                                style={{
                                                    marginTop: "0.5rem",
                                                    fontStyle: "italic",
                                                    color: "#777",
                                                    fontSize: "0.9rem",
                                                }}
                                            >
                                                {activeLocationPicture || "No picture of storage location available for this part yet"}
                                            </p>
                                        </div>
                                    )}
                                </div>

                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "center",
                                        gap: "1rem",
                                    }}
                                >
                                    <button
                                        onClick={() => setShowLocationPopup(false)}
                                        style={{ marginBottom: "1rem", marginTop: "1rem" }}
                                    >
                                        Done
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* New popup to manage sub-assembly version */}
            {showAddSubAssemblyPopup && (
                <div className="active-products-popup-overlay">
                    <div className="active-products-popup-content">

                        <h2 className="edit-org-popup-header">
                            {editingSubAssemblyId ? 'Edit Sub-Assembly' : 'Add New Sub-Assembly'}
                        </h2>

                        <p>
                            {editingSubAssemblyId
                                ? "Modify an existing sub-assembly version."
                                : "Create a new sub-assembly version."}
                        </p>

                        <div className="edit-org-form edit-manage edit-batch-request-form">

                            {/* Name & Version */}
                            <div className="full-width-section">
                                <label>
                                    <strong>Name & Version:</strong>
                                    <input
                                        type="text"
                                        value={newSubAssemblyName}
                                        onChange={(e) => setNewSubAssemblyName(e.target.value)}
                                        placeholder='E.g "Dome v1.2"'
                                    />
                                </label>
                            </div>

                            <div className='edit-org-form-inner'>
                                {/* Sub-Assembly Picture Upload */}
                                <label>
                                    <strong>Sub-Assembly Picture (optional):</strong>
                                    <div className='file-upload-button-and-file'>
                                        <label className="custom-file-upload">
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => {
                                                    const file = e.target.files?.[0] ?? null;
                                                    if (file && file.size > 1024 * 1024) {
                                                        toast.warn("Pictures must be at most 1MB", {
                                                            autoClose: 4000,
                                                            hideProgressBar: false,
                                                            closeOnClick: true,
                                                            pauseOnHover: true,
                                                            draggable: true,
                                                            style: { backgroundColor: 'white', color: 'black', fontSize: '16px' },
                                                        });
                                                        return;
                                                    }
                                                    setNewSubAssemblyPic(file);
                                                }}
                                            />
                                            <p>Upload Image</p>
                                        </label>
                                        <span className="selected-file-name">
                                            {newSubAssemblyPic ? newSubAssemblyPic.name : existingPicName || "No image uploaded"}
                                        </span>
                                    </div>
                                </label>


                                {/* Sub-Assembly SOP Upload */}
                                <label>
                                    <strong>Sub-Assembly SOP (optional):</strong>
                                    <div className='file-upload-button-and-file'>
                                        <label className="custom-file-upload">
                                            <input
                                                type="file"
                                                accept=".pdf,.doc,.docx,.txt"
                                                onChange={(e) => {
                                                    const file = e.target.files?.[0] ?? null;
                                                    if (file && file.size > 1024 * 1024) {
                                                        toast.warn("Documents must be at most 1MB", {
                                                            autoClose: 4000,
                                                            hideProgressBar: false,
                                                            closeOnClick: true,
                                                            pauseOnHover: true,
                                                            draggable: true,
                                                            style: { backgroundColor: 'white', color: 'black', fontSize: '16px' },
                                                        });
                                                        return;
                                                    }
                                                    setNewSubAssemblySop(file);
                                                }}
                                            />
                                            <p>Upload Document</p>
                                        </label>
                                        <span className="selected-file-name">
                                            {newSubAssemblySop ? newSubAssemblySop.name : existingSopName || "No document uploaded"}
                                        </span>
                                    </div>
                                </label>
                            </div>


                            {/* Parts Section */}
                            <div className="full-width-section">

                                <div className="sub-part-and-amount-row">
                                    <div className="field-group">
                                        <label style={{ position: 'relative' }}>
                                            <span>Part</span>
                                            <input
                                                type="text"
                                                placeholder="Search name or MPN"
                                                value={partSearchTerm}
                                                onChange={(e) => {
                                                    setPartSearchTerm(e.target.value);
                                                    setShowDropdown(true);
                                                }}
                                                onFocus={() => setShowDropdown(true)}
                                                onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                                            />
                                            {showDropdown && (
                                                <div className="autocomplete-dropdown">
                                                    {filteredPartOptions.length > 0 ? (
                                                        filteredPartOptions.map((item) => (
                                                            <li
                                                                key={item.partID}
                                                                style={{ color: 'black' }}
                                                                onClick={() => {
                                                                    setSelectedPartId(item.partID);
                                                                    setPartSearchTerm(item.partName);
                                                                    setShowDropdown(false);
                                                                }}
                                                            >
                                                                <img className='part-image' src={item.partPicture ? item.partPicture : fnnBlueLogo} alt={item.partName} />
                                                                {truncateText(((item.mpn ? (item.mpn + " - ") : "") + item.partName), 40)}
                                                            </li>
                                                        ))
                                                    ) : (
                                                        <li style={{ color: '#999' }}>No matches found</li>
                                                    )}
                                                    <button
                                                        className='inv-order-btn order-btn2'
                                                        style={{ margin: '1rem 0.5rem' }}
                                                        onClick={() => setShowAddItemPopup(true)}>Don&apos;t see my part
                                                    </button>
                                                </div>
                                            )}
                                        </label>
                                    </div>

                                    <div className='sub-part-amount-wrapper'>
                                        <div className="field-group">
                                            <label>
                                                <span>Amount</span>
                                                <input
                                                    type="number"
                                                    onWheel={(e) => e.currentTarget.blur()}
                                                    value={selectedPartAmount}
                                                    onChange={(e) => setSelectedPartAmount(Number(e.target.value))}
                                                    min={1}
                                                    placeholder="Quantity Required"
                                                />
                                            </label>
                                        </div>

                                        <button
                                            className='add-another-part'
                                            disabled={!selectedPartId || selectedPartAmount <= 0}
                                            onClick={() => {
                                                if (!selectedPartId || selectedPartAmount <= 0) return;
                                                const alreadyExists = newSubAssemblyParts.some((part) => part.part === selectedPartId);
                                                if (alreadyExists) {
                                                    toast.warn("Cannot add the same part twice!", {
                                                        autoClose: 4000, hideProgressBar: false, closeOnClick: true,
                                                        pauseOnHover: true, draggable: true,
                                                        style: { backgroundColor: 'white', color: 'black', fontSize: '16px' },
                                                    });
                                                    setPartSearchTerm('');
                                                    return;
                                                }
                                                setNewSubAssemblyParts((prev) => [...prev, { part: selectedPartId, quantity_required: selectedPartAmount }]);
                                                setSelectedPartId(null);
                                                setSelectedPartAmount(1);
                                                setPartSearchTerm('');
                                            }}
                                        >
                                            + Part
                                        </button>
                                    </div>
                                </div>

                                <ul className='inv-manage-items'>
                                    {newSubAssemblyParts.map((p, idx) => {
                                        const part = invInfo.find(i => i.partID === p.part);
                                        return (
                                            <li key={idx}>
                                                {part?.mpn ? `${part?.mpn} - ` : ""} {part?.partName || 'Unknown'} – Qty: {p.quantity_required}
                                                <button onClick={() => setNewSubAssemblyParts(prev => prev.filter((_, i) => i !== idx))}>
                                                    <FaTrashAlt />
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>

                        </div>

                        <div className="hh-duo hh-actions edit-org-popup-footer">
                            <button
                                className="cancel-btn"
                                onClick={() => { closeAddSubPopup(); setPartSearchTerm(''); }}
                            >
                                Cancel
                            </button>

                            {editingSubAssemblyId && (
                                <button

                                    className='hh-delete-btn'
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setSubAssemblyToDeleteId(editingSubAssemblyId);
                                        setShowDeleteSubAssemblyPopup(true);
                                    }}
                                >
                                    Delete
                                </button>
                            )}

                            <button
                                className="confirm-btn"
                                onClick={editOrAddSubAssembly}
                                disabled={!newSubAssemblyName.trim() || newSubAssemblyParts.length === 0}
                            >
                                {editingSubAssemblyId ? 'Save' : 'Confirm'}
                            </button>
                        </div>

                    </div>
                </div>
            )}

            {/* New popup to manage detector version */}
            {showAddDetectorPopup && (
                <div className="active-products-popup-overlay">
                    <div className="active-products-popup-content">

                        <h2 className="edit-org-popup-header">
                            {editingDetectorId ? 'Edit Detector' : 'Add New Detector'}
                        </h2>

                        <p>
                            {editingDetectorId
                                ? "Modify an existing detector version."
                                : "Create a new detector version."}
                        </p>

                        <div className="edit-org-form edit-manage edit-batch-request-form">

                            <div className='edit-org-form-inner'>
                                {/* Name */}
                                <label>
                                    <strong>Name:</strong>
                                    <input
                                        type="text"
                                        value={newDetectorName}
                                        onChange={(e) => setNewDetectorName(e.target.value)}
                                        placeholder='Detector Name'
                                    />
                                </label>

                                {/* Version */}
                                <label>
                                    <strong>Version:</strong>
                                    <input
                                        type="text"
                                        value={newDetectorVersion}
                                        onChange={(e) => setNewDetectorVersion(e.target.value)}
                                        placeholder='Version'
                                    />
                                </label>
                            </div>

                            {/* Sub-Assemblies Section */}
                            <div className="full-width-section">

                                <div className="sub-part-and-amount-row">
                                    <div className="field-group">
                                        <label style={{ position: 'relative' }}>
                                            <span>Sub-Assembly</span>
                                            <input
                                                type="text"
                                                placeholder="Search sub-assembly"
                                                value={subAssemblySearchTerm}
                                                onChange={(e) => {
                                                    setSubAssemblySearchTerm(e.target.value);
                                                    setShowSubAssemblyDropdown(true);
                                                }}
                                                onFocus={() => setShowSubAssemblyDropdown(true)}
                                                onBlur={() => setTimeout(() => setShowSubAssemblyDropdown(false), 200)}
                                            />
                                            {showSubAssemblyDropdown && (
                                                <ul className="autocomplete-dropdown">
                                                    {filteredSubAssemblies.length > 0 ? (
                                                        filteredSubAssemblies.map((sa) => (
                                                            <li
                                                                key={sa.id}
                                                                style={{ color: 'black' }}
                                                                onClick={() => {
                                                                    setSelectedSubAssemblyId(sa.id);
                                                                    setSubAssemblySearchTerm(sa.name);
                                                                    setShowSubAssemblyDropdown(false);
                                                                }}
                                                            >
                                                                {sa.name}
                                                            </li>
                                                        ))
                                                    ) : (
                                                        <li style={{ color: '#999' }}>No matches found</li>
                                                    )}
                                                </ul>
                                            )}
                                        </label>
                                    </div>

                                    <div className='sub-part-amount-wrapper'>
                                        <div className="field-group">
                                            <label>
                                                <span>Amount</span>
                                                <input
                                                    type="number"
                                                    onWheel={(e) => e.currentTarget.blur()}
                                                    value={selectedSubAssemblyAmount}
                                                    onChange={(e) => setSelectedSubAssemblyAmount(Number(e.target.value))}
                                                    min={1}
                                                    placeholder="Quantity Required"
                                                />
                                            </label>
                                        </div>

                                        <button
                                            className='add-another-part'
                                            disabled={!selectedSubAssemblyId || selectedSubAssemblyAmount <= 0}
                                            onClick={() => {
                                                if (!selectedSubAssemblyId || selectedSubAssemblyAmount <= 0) return;
                                                const alreadyExists = newDetectorSubAssemblies.some((sa) => sa.subassembly === selectedSubAssemblyId);
                                                if (alreadyExists) {
                                                    toast.warn("Cannot add the same sub-assembly twice!", {
                                                        autoClose: 4000, hideProgressBar: false, closeOnClick: true,
                                                        pauseOnHover: true, draggable: true,
                                                        style: { backgroundColor: 'white', color: 'black', fontSize: '16px' },
                                                    });
                                                    setSubAssemblySearchTerm('');
                                                    return;
                                                }
                                                setNewDetectorSubAssemblies((prev) => [...prev, { subassembly: selectedSubAssemblyId, quantity_required: selectedSubAssemblyAmount }]);
                                                setSelectedSubAssemblyId(null);
                                                setSelectedSubAssemblyAmount(1);
                                                setSubAssemblySearchTerm('');
                                            }}
                                        >
                                            + Sub-Assembly
                                        </button>
                                    </div>

                                </div>

                                <ul className='inv-manage-items'>
                                    {newDetectorSubAssemblies.map((s, idx) => {
                                        const sub = subAssemblies.find(sa => sa.id === s.subassembly);
                                        return (
                                            <li key={idx}>
                                                {sub?.name || 'Unknown'} – Qty: {s.quantity_required}
                                                <button onClick={() => setNewDetectorSubAssemblies(prev => prev.filter((_, i) => i !== idx))}>
                                                    <FaTrashAlt />
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>

                        </div>

                        <div className="hh-duo hh-actions edit-org-popup-footer">
                            <button
                                className="cancel-btn"
                                onClick={() => { closeAddDetectorPopup(); setSubAssemblySearchTerm(''); }}
                            >
                                Cancel
                            </button>

                            {editingDetectorId && (
                                <button
                                    className='hh-delete-btn'
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setDetectorToDeleteId(editingDetectorId);
                                        setShowDeleteDetectorPopup(true);
                                    }}
                                >
                                    Delete
                                </button>
                            )}

                            <button
                                className="confirm-btn"
                                onClick={editOrAddDetector}
                                disabled={!newDetectorName.trim() || newDetectorSubAssemblies.length === 0}
                            >
                                {editingDetectorId ? 'Save' : 'Confirm'}
                            </button>
                        </div>

                    </div>
                </div>
            )}

            {/* New popup for creating new item */}
            {showAddItemPopup && (
                <div className="active-products-popup-overlay">
                    <div className="active-products-popup-content">

                        <h2 className="edit-org-popup-header">Add New Item</h2>
                        <p>Fill in the details to add a new inventory part.</p>

                        <div className="edit-org-form edit-manage">

                            {/* Part Name */}
                            <label className="customer-edit-org-name full-width-section">
                                <strong>Part Name:</strong>
                                <input
                                    type="text"
                                    placeholder='E.g "D-Link DWM-311 Router"'
                                    value={newPartData.partName}
                                    onChange={(e) => setNewPartData(prev => ({ ...prev, partName: e.target.value }))}
                                />
                            </label>

                            {/* Amount */}
                            <label>
                                <strong>Amount:</strong>
                                <input
                                    type="number"
                                    onWheel={(e) => e.currentTarget.blur()}
                                    placeholder="Current Amount"
                                    value={newPartData.currentAmount}
                                    onChange={(e) => setNewPartData(prev => ({ ...prev, currentAmount: Number(e.target.value) }))}
                                />
                            </label>

                            {/* Unit Cost */}
                            <label>
                                <strong>Unit Cost:</strong>
                                <input
                                    type="number"
                                    onWheel={(e) => e.currentTarget.blur()}
                                    step="0.01"
                                    placeholder="Unit Cost"
                                    value={newPartData.unitCost}
                                    onChange={(e) => setNewPartData(prev => ({ ...prev, unitCost: Number(e.target.value) }))}
                                />
                            </label>

                            {/* Category */}
                            <label>
                                <strong>Category:</strong>
                                <select
                                    value={newPartData.category}
                                    onChange={(e) => setNewPartData(prev => ({ ...prev, category: e.target.value }))}
                                >
                                    {CATEGORY_OPTIONS.map(option => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </label>

                            {/* Sold in Packs */}
                            <label>
                                <strong>Sold in Packs?</strong>
                                <input
                                    type="checkbox"
                                    checked={!!newPartData.sold_in_packets}
                                    onChange={(e) =>
                                        setNewPartData(prev => ({
                                            ...prev,
                                            sold_in_packets: e.target.checked,
                                            amount_per_packet: e.target.checked ? prev.amount_per_packet : null,
                                        }))
                                    }
                                />
                            </label>

                            {/* Amount per Pack (conditional) */}
                            {newPartData.sold_in_packets && (
                                <label className="full-width-section">
                                    <strong>Amount per Pack:</strong>
                                    <input
                                        type="number"
                                        onWheel={(e) => e.currentTarget.blur()}
                                        min={1}
                                        placeholder="Units per pack"
                                        value={newPartData.amount_per_packet ?? ''}
                                        onChange={(e) =>
                                            setNewPartData(prev => ({
                                                ...prev,
                                                amount_per_packet: e.target.value === '' ? null : Number(e.target.value),
                                            }))
                                        }
                                    />
                                </label>
                            )}

                            {/* Optional Fields Divider */}
                            <div className="full-width-section" style={{ borderTop: '1px solid #ddd', paddingTop: '0.5rem' }}>
                                <strong>Optional Fields</strong>
                            </div>

                            {/* Stock Target */}
                            <label>
                                <strong>Stock Target:</strong>
                                <input
                                    type="number"
                                    onWheel={(e) => e.currentTarget.blur()}
                                    placeholder="Desired stock level"
                                    value={newPartData.stock_target ?? ''}
                                    onChange={(e) =>
                                        setNewPartData(prev => ({
                                            ...prev,
                                            stock_target: e.target.value === '' ? null : Number(e.target.value),
                                        }))
                                    }
                                />
                            </label>

                            {/* Store Link */}
                            <label>
                                <strong>Store Link:</strong>
                                <input
                                    type="url"
                                    placeholder="https://store/item.com"
                                    value={newPartData.partLink ?? ''}
                                    onChange={(e) => setNewPartData(prev => ({ ...prev, partLink: e.target.value }))}
                                />
                            </label>

                            {/* Location */}
                            <label>
                                <strong>Location:</strong>
                                <input
                                    type="text"
                                    placeholder='E.g "Room X, first drawer"'
                                    value={newPartData.location}
                                    onChange={(e) => setNewPartData(prev => ({ ...prev, location: e.target.value }))}
                                />
                            </label>

                            {/* Description */}
                            <label>
                                <strong>Description:</strong>
                                <input
                                    type="text"
                                    placeholder='E.g "Screw used to mount Dome"'
                                    value={newPartData.description}
                                    onChange={(e) => setNewPartData(prev => ({ ...prev, description: e.target.value }))}
                                />
                            </label>

                            {/* MPN */}
                            <label>
                                <strong>MPN:</strong>
                                <input
                                    type="text"
                                    placeholder="Manufacturer Part Number"
                                    value={newPartData.mpn}
                                    onChange={(e) => setNewPartData(prev => ({ ...prev, mpn: e.target.value }))}
                                />
                            </label>

                            {/* Manufacturer */}
                            <label>
                                <strong>Manufacturer:</strong>
                                <input
                                    type="text"
                                    placeholder="Company that manufactures the part"
                                    value={newPartData.manufacturer}
                                    onChange={(e) => setNewPartData(prev => ({ ...prev, manufacturer: e.target.value }))}
                                />
                            </label>

                            {/* Part Picture Upload */}
                            <label className="full-width-section">
                                <strong>Part Picture:</strong>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => {
                                        const file = e.target.files?.[0] ?? null;
                                        if (file && file.size > 1024 * 1024) {
                                            toast.warn("Pictures must be at most 1MB", toastStyle);
                                            return;
                                        }
                                        setNewPartData(prev => ({ ...prev, partPictureFile: file }));
                                    }}
                                />
                                {newPartData.partPictureFile && (
                                    <span className="selected-file-name">{newPartData.partPictureFile.name}</span>
                                )}
                            </label>

                            {/* Part Document Upload */}
                            <label className="full-width-section">
                                <strong>Part Document:</strong>
                                <input
                                    type="file"
                                    accept=".pdf,.doc,.docx,.txt"
                                    onChange={(e) => {
                                        const file = e.target.files?.[0] ?? null;
                                        if (file && file.size > 1024 * 1024) {
                                            toast.warn("Documents must be at most 1MB", toastStyle);
                                            return;
                                        }
                                        setNewPartData(prev => ({ ...prev, partDocumentFile: file }));
                                    }}
                                />
                                {newPartData.partDocumentFile && (
                                    <span className="selected-file-name">{newPartData.partDocumentFile.name}</span>
                                )}
                            </label>

                            {/* Location Picture Upload */}
                            <label className="full-width-section">
                                <strong>Picture of Location:</strong>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => {
                                        const file = e.target.files?.[0] ?? null;
                                        if (file && file.size > 1024 * 1024) {
                                            toast.warn("Pictures must be at most 1MB", toastStyle);
                                            return;
                                        }
                                        setNewPartData(prev => ({ ...prev, partLocationFile: file }));
                                    }}
                                />
                                {newPartData.partLocationFile && (
                                    <span className="selected-file-name">{newPartData.partLocationFile.name}</span>
                                )}
                            </label>

                        </div>

                        {/* Footer */}
                        <div className="hh-duo hh-actions edit-org-popup-footer">
                            <button
                                className="cancel-btn"
                                onClick={() => {
                                    setShowAddItemPopup(false);
                                    resetNewPartData();
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                className="confirm-btn"
                                disabled={!newPartData.partName.trim()}
                                onClick={async () => {
                                    await addNewPart();
                                    setShowAddItemPopup(false);
                                }}
                            >
                                Confirm
                            </button>
                        </div>

                    </div>
                </div>
            )}

            {/* Confirm Delete Item Popup */}
            {showDeleteItemPopup && (
                <div className='add-item-popup-overlay'>
                    <div className='add-item-popup-content del-pop'>

                        <div className='inv-delete-inner-pop'>
                            <h3>Are you sure you would like to delete this item?</h3>

                            <div className='inv-delete-buttons-div'>
                                <button onClick={() => {
                                    setShowDeleteItemPopup(false);
                                    setItemToDeleteId(null);
                                }}>
                                    Cancel
                                </button>
                                <button
                                    onClick={() => {
                                        if (itemToDeleteId !== null) {
                                            deleteInventoryItem(itemToDeleteId);
                                        }
                                    }}
                                >
                                    Delete
                                </button>
                            </div>
                        </div>

                    </div>
                </div>
            )}

            {/* Confirm Count Popup */}
            {showCountedPopup && itemToCount && (() => {
                // Resolve the linked sub-assembly and its parts for the warning
                const linkedSubId =
                    editedValuesMap[itemToCount.partID]?.bundle_subassembly
                    ?? itemToCount.bundle_subassembly;

                const linkedSub = linkedSubId
                    ? subAssemblies.find(sa => sa.id === linkedSubId)
                    : null;

                return (
                    <div className="add-item-popup-overlay">
                        <div className="add-item-popup-content inv-inner-pop-extension">
                            <div className="inv-small-pop">

                                <div className="hh-delete-text-and-icon" style={{ width: '100%' }}>
                                    <div className="marketing-finish-icon-div">
                                        <GoNumber className="inv-count-icon" />
                                    </div>

                                    <div style={{ width: '100%' }}>
                                        <h3 style={{ fontWeight: '400', marginBottom: '1rem' }}>
                                            Confirm count for{itemToCount.mpn ? ` ${itemToCount.mpn} -` : ""} {itemToCount.partName}
                                        </h3>

                                        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                                            <span>Currently Listed Amount</span>
                                            <input
                                                type="number"
                                                onWheel={(e) => e.currentTarget.blur()}
                                                min={0}
                                                defaultValue={
                                                    editedValuesMap[itemToCount.partID]?.currentAmount
                                                    ?? itemToCount.currentAmount
                                                }
                                                onChange={(e) =>
                                                    setEditedValuesMap(prev => ({
                                                        ...prev,
                                                        [itemToCount.partID]: {
                                                            ...prev[itemToCount.partID],
                                                            currentAmount: Number(e.target.value),
                                                        },
                                                    }))
                                                }
                                                style={{ padding: '0.4rem', fontSize: '1rem' }}
                                            />
                                        </label>

                                        {/* Bundle warning */}
                                        {linkedSub && (() => {

                                            const newCount =
                                                editedValuesMap[itemToCount.partID]?.currentAmount
                                                ?? itemToCount.currentAmount;

                                            const oldCount = itemToCount.currentAmount;

                                            const delta = newCount - oldCount;

                                            return (
                                                <div style={{
                                                    marginTop: '1rem',
                                                    padding: '0.75rem',
                                                    borderRadius: '6px',
                                                    backgroundColor: 'var(--warning-bg, #fff8e1)',
                                                    border: '1px solid var(--warning-border, #f0c040)',
                                                    fontSize: '0.875rem',
                                                }}>
                                                    <p style={{ marginBottom: '0.4rem' }}>
                                                        <strong>⚠ Bundle item:</strong>{" "}
                                                        Confirming will adjust inventory quantities for all parts in{" "}
                                                        <strong>{linkedSub.name}</strong>{" "}
                                                        based on the bundle quantity difference.
                                                    </p>

                                                    <p style={{
                                                        marginBottom: '0.6rem',
                                                        fontSize: '0.8rem',
                                                        opacity: 0.85,
                                                    }}>
                                                        Previous bundle count: <strong>{oldCount}</strong>
                                                        {" → "}
                                                        New bundle count: <strong>{newCount}</strong>
                                                        {" "}
                                                        ({delta >= 0 ? "+" : ""}{delta} change)
                                                    </p>

                                                    <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
                                                        {linkedSub.parts.map(p => {

                                                            const part = invInfo.find(i => i.partID === p.part);

                                                            const adjustment =
                                                                p.quantity_required * delta;

                                                            const currentInventory =
                                                                part?.currentAmount ?? 0;

                                                            const newInventory =
                                                                Math.max(0, currentInventory + adjustment);

                                                            return (
                                                                <li key={p.part}>
                                                                    {part?.partName ?? `Part #${p.part}`}

                                                                    {" → "}

                                                                    {adjustment >= 0 ? (
                                                                        <>
                                                                            +<strong>{adjustment}</strong>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <strong>{adjustment}</strong>
                                                                        </>
                                                                    )}

                                                                    {" "}

                                                                    <span style={{ opacity: 0.8 }}>
                                                                        ({currentInventory} → {newInventory})
                                                                    </span>
                                                                </li>
                                                            );
                                                        })}
                                                    </ul>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </div>

                                <div className="hh-duo hh-actions">
                                    <button
                                        className="cancel-btn"
                                        onClick={() => {
                                            setShowCountedPopup(false);
                                            setItemToCount(null);
                                        }}
                                    >
                                        Cancel
                                    </button>

                                    <button
                                        className="confirm-btn"
                                        onClick={async () => {
                                            const updatedAmount =
                                                editedValuesMap[itemToCount.partID]?.currentAmount
                                                ?? itemToCount.currentAmount;

                                            await markItemCounted(itemToCount.partID, updatedAmount);

                                            setEditedValuesMap(prev => ({ ...prev, [itemToCount.partID]: {} }));
                                            setExpandedSlips(prev => ({ ...prev, [itemToCount.partID]: false }));
                                            setShowCountedPopup(false);
                                            setItemToCount(null);
                                        }}
                                    >
                                        Confirm
                                    </button>
                                </div>

                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Set Stock Status Popup */}
            {showSetStockPopup && (
                <div className="add-item-popup-overlay">
                    <div className="add-item-popup-content inv-inner-pop-extension">
                        <div className="inv-small-pop">

                            <div className="hh-delete-text-and-icon" style={{ width: '100%' }}>

                                <div className="marketing-finish-icon-div">
                                    <BsBoxFill className="inv-count-icon" />
                                </div>

                                <div style={{ width: '100%' }}>
                                    <h3 style={{ fontWeight: '400', marginBottom: '0.8rem' }}>
                                        Set Detector Stock Target
                                    </h3>

                                    <p style={{ fontSize: '0.9rem', opacity: 0.8, marginBottom: '1rem' }}>
                                        Set how many detectors you should be able to build at any time based on available parts (bill of materials).
                                        The selected amount determines the stock status of all parts required to build a detector.
                                    </p>

                                    <label style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                                        <input
                                            type="number"
                                            min={0}
                                            value={newStockAmount}
                                            onWheel={(e) => e.currentTarget.blur()}
                                            onChange={(e) => setNewStockAmount(Number(e.target.value))}
                                            style={{ padding: '0.4rem', fontSize: '1rem' }}
                                        />
                                    </label>
                                </div>
                            </div>

                            <div className="hh-duo hh-actions">
                                <button
                                    className="cancel-btn"
                                    onClick={() => setShowSetStockPopup(false)}
                                >
                                    Cancel
                                </button>

                                <button
                                    className="confirm-btn"
                                    onClick={() => {
                                        handleStockNumber(); // reuse your existing logic
                                        setShowSetStockPopup(false);
                                    }}
                                >
                                    Confirm
                                </button>
                            </div>

                        </div>
                    </div>
                </div>
            )}

            {showDeleteSubAssemblyPopup && (
                <div className='add-item-popup-overlay'>
                    <div className='add-item-popup-content del-pop'>
                        <div className='inv-delete-inner-pop'>
                            <h3>Are you sure you want to delete this sub-assembly?</h3>

                            <div className='inv-delete-buttons-div'>
                                <button onClick={() => {
                                    setShowDeleteSubAssemblyPopup(false);
                                    setSubAssemblyToDeleteId(null);
                                }}>
                                    Cancel
                                </button>
                                <button
                                    onClick={() => {
                                        if (subAssemblyToDeleteId !== null) {
                                            handleDeleteSubAssembly(subAssemblyToDeleteId);
                                        }
                                    }}
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showDeleteDetectorPopup && (
                <div className='add-item-popup-overlay'>
                    <div className='add-item-popup-content del-pop'>
                        <div className='inv-delete-inner-pop'>
                            <h3>Are You Sure You Want to Delete This Detector Version?</h3>

                            <div className='inv-delete-buttons-div'>
                                <button onClick={() => {
                                    setShowDeleteDetectorPopup(false);
                                    setDetectorToDeleteId(null);
                                }}>
                                    Cancel
                                </button>
                                <button
                                    onClick={() => {
                                        if (detectorToDeleteId !== null) {
                                            handleDeleteDetector(detectorToDeleteId);
                                        }
                                    }}
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showSelectDetectorPopup && currentSubAssemblyId && (
                <div className="add-item-popup-overlay">
                    <div className="built-det-for-sub-content">
                        <button
                            style={{ color: "black" }}
                            className="close-popup-button"
                            onClick={() => {
                                setShowSelectDetectorPopup(false);
                                setCurrentSubAssemblyId(null);
                            }}
                        >
                            x
                        </button>

                        <div className="subassembly-selector">
                            <strong>Select Detector for Sub-Assembly</strong>

                            <div className="subassembly-options-box">
                                {(() => {
                                    // ✅ 1. Find the built sub-assembly object (e.g. { id: "DOM35-2", subassembly: 35 })
                                    const builtSubAssemblyObj = builtSubAssemblies.find(
                                        bsa => bsa.id === currentSubAssemblyId
                                    );
                                    if (!builtSubAssemblyObj) {
                                        return <p className="no-options">Invalid built sub-assembly.</p>;
                                    }

                                    // ✅ 2. Extract its numeric subassembly ID (e.g. 35)
                                    const subassemblyId = builtSubAssemblyObj.subassembly;

                                    // ✅ 3. Find detector models whose BOM includes that subassembly ID
                                    const validDetectorModels = detectors.filter(det =>
                                        det.subassemblies.some(ds => ds.subassembly === subassemblyId)
                                    );

                                    // ✅ 4. Get their version numbers
                                    const validVersions = validDetectorModels.map(det => det.version);

                                    // ✅ 5. Filter built detectors based on those valid versions
                                    const matchingBuiltDetectors = builtDetectors.filter(det => {
                                        const detModel = detectors.find(d => d.id === det.detector);
                                        return detModel && validVersions.includes(detModel.version);
                                    });

                                    // ✅ 6. Display appropriate message if none found
                                    if (!matchingBuiltDetectors.length) {
                                        return (
                                            <p className="no-options">
                                                No detectors available for version(s):{" "}
                                                {validVersions.length ? validVersions.join(", ") : "None"}
                                            </p>
                                        );
                                    }

                                    // ✅ 7. Render filtered detectors
                                    return matchingBuiltDetectors.map(det => {
                                        const detModel = detectors.find(d => d.id === det.detector);
                                        return (
                                            <div
                                                key={det.mac_address}
                                                style={{ cursor: "pointer" }}
                                                onClick={() => {
                                                    if (!currentSubAssemblyId) return;
                                                    handleUnitStatusChange(currentSubAssemblyId, "installed", det.mac_address);
                                                    setShowSelectDetectorPopup(false);
                                                    setCurrentSubAssemblyId(null);
                                                }}
                                                className="subassembly-option"
                                            >
                                                {det.code ? `${det.code} - ` : ""} {det.mac_address} — v{detModel?.version || "?"}
                                            </div>
                                        );
                                    });
                                })()}
                            </div>

                            <br />
                            <p>
                                <strong>Note 1:</strong> Only detectors whose version includes this sub-assembly
                                type will be shown.
                            </p>
                            <br />
                            <p><strong>Note 2:</strong> If the selected detector already has a sub-assembly of this type
                                (e.g., Dome v1), selecting that detector will remove the current sub-assembly it has and replace it
                                with the newly selected one.
                            </p><br />

                            <p><strong>Example:</strong> A detector currently has a Dome Sub-Assembly DOM1-1. If you assign
                                another Dome Sub-Assembly DOM1-2 to the same detector, DOM1-1 will be removed and marked as
                                available, while DOM1-2 will be installed in the detector.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {showStatusConfirmPopup && subAssemblyToConfirmId && statusToConfirm && (
                <div className="add-item-popup-overlay">
                    <div className="add-item-popup-content second-confirm-forecast-popup-outer">

                        <div className="add-new-part-form">

                            <h3>Confirm Status Change</h3>

                            <div className="second-confirm-forecast-popup">

                                <p>
                                    You are about to mark <strong>{builtSubAssemblies.find(bsa => bsa.id === subAssemblyToConfirmId)?.id}</strong> as <strong>{statusToConfirm}</strong>. Would you like to proceed?
                                </p>

                                <div style={{ display: "flex", justifyContent: "center", gap: "1rem" }}>
                                    <button
                                        onClick={() => {
                                            setShowStatusConfirmPopup(false);
                                            setSubAssemblyToConfirmId(null);
                                            setStatusToConfirm(null);
                                        }}
                                    >
                                        No
                                    </button>
                                    <button
                                        onClick={() => {
                                            if (subAssemblyToConfirmId && statusToConfirm) {
                                                handleUnitStatusChange(subAssemblyToConfirmId, statusToConfirm);
                                            }
                                            setShowStatusConfirmPopup(false);
                                            setSubAssemblyToConfirmId(null);
                                            setStatusToConfirm(null);
                                        }}
                                    >
                                        Yes
                                    </button>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            )}

            {showBuildConfirmPopup && (
                <div className="add-item-popup-overlay">
                    <div className="add-item-popup-content confirm-a-build-wrapper">

                        <div className='add-new-part-form'>

                            <h3>Confirm Sub-Assembly Build</h3>

                            <div className='confirm-a-build-inner'>

                                {buildSummary.buildNew.length > 0 && (
                                    <>
                                        <div className='subassembly-selector'><strong><p>You are about to build NEW:</p></strong></div>
                                        <table className="confirm-table">
                                            <thead><tr><th>Sub</th><th>Quantity</th></tr></thead>
                                            <tbody>
                                                {buildSummary.buildNew.map((c, i) => (
                                                    <tr key={i}><td>{c.sub}</td><td>{c.qty}</td></tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        <p>Which will <strong>consume</strong> the listed sub-assemblies worth of parts from the inventory and create new built sub-assembly units.</p>
                                    </>
                                )}

                                {buildSummary.register.length > 0 && (
                                    <>
                                        <div className='subassembly-selector'><strong><p>You are about to register EXISTING:</p></strong></div>
                                        <table className="confirm-table">
                                            <thead><tr><th>Sub</th><th>Quantity</th></tr></thead>
                                            <tbody>
                                                {buildSummary.register.map((c, i) => (
                                                    <tr key={i}><td>{c.sub}</td><td>{c.qty}</td></tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        <p>Which will create new built sub-assembly units <strong>without consuming</strong> inventory.</p>
                                    </>
                                )}

                                {buildSummary.addComponents.length > 0 && (
                                    <>
                                        <div className='subassembly-selector'><strong><p>You are about to add components:</p></strong></div>
                                        <table className="confirm-table">
                                            <thead><tr><th>Sub</th><th>Quantity</th></tr></thead>
                                            <tbody>
                                                {buildSummary.addComponents.map((a, i) => (
                                                    <tr key={i}><td>{a.sub}</td><td>{a.qty}</td></tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        <p>Which will <strong>add</strong> the listed sub-assemblies worth of parts to the inventory.</p>
                                    </>
                                )}

                                <p style={{ marginTop: '1rem', textAlign: 'center' }}>Would you like to proceed?</p>

                                <div style={{ display: "flex", justifyContent: "center", gap: "1rem" }}>
                                    <button
                                        style={{ marginTop: 'unset' }}
                                        onClick={() => setShowBuildConfirmPopup(false)}
                                    >
                                        No
                                    </button>
                                    <button
                                        style={{ marginTop: 'unset' }}
                                        onClick={async () => {
                                            setShowBuildConfirmPopup(false);
                                            await handleSubAssemblyBuildAction();

                                            // Only show the result popup if something was built
                                            if (buildSummary.buildNew.length > 0 || buildSummary.register.length > 0) {
                                                setShowBuildResultPopup(true);
                                            }
                                            else {
                                                setShowSectionPopup(false)
                                            }
                                        }}
                                    >
                                        Yes
                                    </button>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            )}

            {showBuildResultPopup && (
                <div className="add-item-popup-overlay">
                    <div className="add-item-popup-content confirm-a-build-wrapper">

                        <div className='add-new-part-form'>
                            <h3>Build Complete</h3>

                            <div className='confirm-a-build-inner'>

                                <div className='subassembly-selector'><strong><p>You have just built:</p></strong></div>

                                <table className="confirm-table">
                                    <thead>
                                        <tr><th>Sub</th><th>Quantity</th><th>IDs</th></tr>
                                    </thead>
                                    <tbody>
                                        {newlyBuiltSubs.map((b, i) => (
                                            <tr key={i}>
                                                <td>{b.name}</td>
                                                <td>{b.qty}</td>
                                                <td>{b.ids.join(", ")}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>

                                <p>Please take this moment to add labels to the built sub-assemblies with their respective IDs.</p>

                                <div style={{ display: "flex", justifyContent: "center" }}>
                                    <button onClick={() => (setShowBuildResultPopup(false), setShowSectionPopup(false), closeConsumeAddPopup('sub'))}>OK</button>
                                </div>

                            </div>
                        </div>


                    </div>
                </div>
            )}

            {/* Rendering the build activity Tooltip */}
            {barTooltip && createPortal(
                <div
                    style={{
                        position: 'fixed',
                        left: barTooltip.x,
                        top: barTooltip.y,
                        transform: 'translate(-50%, calc(-100% - 4px))',
                        background: 'rgba(0,0,0,0.7)',
                        color: '#fff',
                        padding: '2px 6px',
                        fontSize: '0.75rem',
                        borderRadius: '3px',
                        whiteSpace: 'nowrap',
                        pointerEvents: 'none',
                        zIndex: 99999,
                    }}
                >
                    {barTooltip.text}
                </div>,
                document.body
            )}

            {/* Check if user when discarting a sub-assembly, if they want to re-add the items back to inventory or not */}
            {showDiscardPopup && (
                <div className="add-item-popup-overlay">
                    <div className="add-item-popup-content inv-inner-pop-extension">
                        <div className="inv-small-pop">

                            <div className="hh-delete-text-and-icon" style={{ width: '100%' }}>
                                <div className="marketing-finish-icon-div">
                                    <LuArchiveRestore className="inv-count-icon" />
                                </div>
                                <div style={{ width: '100%' }}>
                                    <h3 style={{ fontWeight: '400', marginBottom: '0.8rem' }}>
                                        Add items back to inventory?
                                    </h3>
                                    <p style={{ fontSize: '0.9rem', opacity: 0.8, marginBottom: '1rem' }}>
                                        Clicking &quot;Yes&quot; will re-add what was consumed from building
                                        this sub-assembly back to the inventory count. Selecting &quot;No&quot; simply marks this
                                        sub-assembly as discarded and no items are re-added to the inventory count.
                                    </p>
                                </div>
                            </div>

                            <div className="hh-duo hh-actions">
                                <button
                                    className="cancel-btn"
                                    onClick={() => {
                                        setShowDiscardPopup(false);
                                        setDiscardTargetId(null);
                                        setDiscardSubAssemblyTypeId(null);
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="cancel-btn"
                                    onClick={() => handleDiscardConfirm(false)}
                                >
                                    No
                                </button>
                                <button
                                    className="cancel-btn"
                                    onClick={() => handleDiscardConfirm(true)}
                                >
                                    Yes
                                </button>
                            </div>

                        </div>
                    </div>
                </div>
            )}



        </div>
    )
}

export default InventoryManagement